import {
    closestCenter,
    DndContext,
    DragOverlay,
    KeyboardSensor,
    PointerSensor,
    useDroppable,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import plusFill from '@iconify/icons-eva/plus-fill';
import { Icon } from '@iconify/react';
import { Add, Assistant, CheckCircle, Close, DoNotDisturbAltOutlined, DoNotDisturbOn, Download, Error, InfoOutlined, OpenInNewOutlined, Search, Update, Upload, Warning } from '@mui/icons-material';
import { Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, Drawer, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, LinearProgress, Menu, MenuItem, Popover, Radio, RadioGroup, Select, Skeleton, Stack, Switch, TextField, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import CalendarRangePicker from 'src/components/CalendarRangePicker';
import NotificationCenter from 'src/components/NotificationCenter';
import Page from "src/components/Page";
import { AssistantProvider, useAssistant } from 'src/hooks/useAssistant';
import apiInstance from 'src/utils/apiAuth';
import { getOfferingChipStyle, getOfferingAccentColor, getDDMMYYYYFormat } from 'src/utils/commonUtils';
import * as constants from 'src/utils/constants';
import { notificationApi } from 'src/utils/notificationApi';
import pushNotifications from 'src/utils/pushNotifications';
import { tasksApi } from 'src/utils/tasksApi';
import { showErrorToast, showToast } from 'src/utils/Toast/SnackbarProvider';
import sampleExcelFile from '../../../../assets/sample_excel_file.xlsx';
import ColumnVisibilityControl from '../../components/ColumnVisibilityControl';
import { getStageDisplayName, isHiddenStage } from 'src/utils/stageDisplay';
import { buildStagePercentMap, isGatedStage } from 'src/utils/stageGate';
import AssistantDrawer from './AssistantDrawer';
import FinancialCaptureDialog from './FinancialCaptureDialog';
import AddToDndDialog from '../company/AddToDndDialog';
import { getDndDetails, removeDnd } from 'src/utils/dndApi';

import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import UserInfo from "src/utils/Authorization/UserInfo";

const AddCompany = React.lazy(() => import('../company/AddCompany'));
const NotificationOnboarding = React.lazy(() => import('src/components/NotificationCenter/NotificationOnboarding'));

/**
 * Filters user roles based on stage name using centralized mapping
 * @param {string} stageName - The name of the stage
 * @param {Array} allRoles - All available user roles from API
 * @returns {Array} Filtered roles applicable to the stage
 */
const getFilteredRolesByStage = (stageName, allRoles) => {
    // Return empty array if no roles provided
    if (!allRoles || allRoles.length === 0) return [];

    // Return all roles if no stage name
    if (!stageName) return allRoles;

    const stageNameLower = stageName.toLowerCase();

    // Find matching stage key from config
    let suggestedRoles = [];
    for (const [key, config] of Object.entries(STAGE_ROLES_CONFIG)) {
        if (stageNameLower.includes(key)) {
            suggestedRoles = config.suggested;
            break;
        }
    }

    // If no suggested roles found, return all roles
    if (suggestedRoles.length === 0) {
        return allRoles;
    }

    // Filter roles based on suggested list
    return allRoles.filter(role =>
        role && role.label && suggestedRoles.some(suggestedRole =>
            role.label.toLowerCase().includes(suggestedRole.toLowerCase()) ||
            suggestedRole.toLowerCase().includes(role.label.toLowerCase())
        )
    );
};

// Unified configuration for stage roles (using role LABELS from database)
// - suggested: Roles shown in the UI for each stage
// - required: Roles marked as "(must)" that BLOCK stage changes if not assigned
// Team/role assignment on stage change is DEFERRED for the AutoFacets pipeline —
// to be defined per phase later. With every stage's `suggested`/`required` empty,
// the drag-drop team-assignment drawer (PRIORITY 3) never auto-opens and no move
// is blocked. The Intro Call *task* is still assigned when moving into Qualified
// Meetings (PRIORITY 2). Re-populate a stage's `suggested` (and `required` for a
// "(must)") to bring its assignment prompt back.
const STAGE_ROLES_CONFIG = {
    'outreach': { suggested: [], required: [] },
    'future-outreach': { suggested: [], required: [] },
    'qualified meetings': { suggested: [], required: [] },
    'intro call': { suggested: [], required: [] },
    'workshop + dm': { suggested: [], required: [] },
    'nb loi + msa': { suggested: [], required: [] },
    'dd - phase': { suggested: [], required: [] },
    'closing': { suggested: [], required: [] },
    'closed': { suggested: [], required: [] },
    '100': { suggested: [], required: [] },
};

// Draggable Ticket Component
function DraggableTicket({ ticket, onTicketClick, isHighlighted, isDueReminder, reminder, onViewReminder, isZeroPercentStage, isFivePercentStage, isHundredPercentStage, onMarkOutreached, onMarkInvalid, onViewInvalidDetails, onViewNewsletterDetails, onArchiveCompany, onViewArchiveDetails, onAddDnd, onRemoveDnd, isSuperAdmin }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: ticket.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
    // DND (Do-Not-Disturb) popover anchor — local to the card (FR-012 interactive badge).
    const [dndPopoverAnchor, setDndPopoverAnchor] = useState(null);

    // B3: format TCI/TCV like the Deal Details drawer — "<currency.label> <amount>"
    // (currency label absent -> no prefix), with thousands separators for card readability.
    const fmtMoney = (v) => `${ticket.currency_name ? ticket.currency_name + ' ' : ''}${Number(v).toLocaleString('en-US')}`;

    const isGWXAgent = ticket.created_by_name === "GWX Agent";
    const isOutreached = ticket.is_outreached === true;
    const isInvalid = ticket.is_invalid === true;
    const isDnd = ticket.is_dnd === true;
    const isArchived = ticket.is_archived === true;
    const isRedHighlighted = ticket.is_red_color === true;
    const redColorRule = ticket.red_color_rule; // '2_weeks' or '6_weeks' or null
    const teamLeadName = ticket.team_lead_name || null;

    // GWX Agent styling only applies if NOT outreached
    const showGWXAgentStyling = isGWXAgent && !isOutreached;

    // Determine background color based on reminder status
    const getBgColor = () => {
        if (isDueReminder || isRedHighlighted) return '#ffebee'; // Light red for due reminders
        if (reminder) return '#e3f2fd'; // Light blue for active reminders
        if (isHighlighted) return '#fff3e0';
        if (showGWXAgentStyling) return '#fff3e0';
        return '#ffffff';
    };

    const getBorderColor = () => {
        if (isDueReminder || isRedHighlighted) return '#f44336'; // Red for due reminders
        if (reminder) return '#2196f3'; // Blue for active reminders
        if (isHighlighted || showGWXAgentStyling) return '#ff9800';
        return '#e0e0e0';
    };

    return (
        <Card
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={(e) => {
                if (!isDragging) {
                    onTicketClick(ticket.id);
                }
            }}
            sx={{
                mb: 1.5,
                cursor: isDragging ? 'grabbing' : 'pointer',
                '&:hover': {
                    boxShadow: isDueReminder ? '0 4px 16px rgba(244,67,54,0.4)' : (showGWXAgentStyling ? '0 4px 16px rgba(255,152,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)'),
                    transform: 'translateY(-2px)',
                    borderColor: isDueReminder ? '#d32f2f' : (showGWXAgentStyling ? '#ff9800' : (isHighlighted ? '#ff9800' : '#1976d2'))
                },
                border: `${isDueReminder || reminder ? '2px' : '1px'} solid ${getBorderColor()}`,
                borderRadius: 2.5,
                minHeight: 96,
                userSelect: 'none',
                transition: 'all 0.2s ease-in-out',
                bgcolor: getBgColor(),
                boxShadow: isDueReminder ? '0 4px 16px rgba(244,67,54,0.3)' : (isHighlighted ? '0 4px 16px rgba(255,152,0,0.3)' : (showGWXAgentStyling ? '0 2px 8px rgba(255,152,0,0.25)' : 'none')),
                // Offering-colored left accent bar (replaces the old country-colored avatar
                // as the card's color cue). Pseudo-element so it follows the border-radius
                // and is unaffected by the border/hover recolor logic above.
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: '4px',
                    bgcolor: getOfferingAccentColor(ticket.offering_name || ticket.business_type_name)
                }
            }}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 1.5 } }}>
                {/* Deadline Alert Indicator - Shows which rule triggered red highlighting */}
                {isRedHighlighted && redColorRule && (
                    <Tooltip title={redColorRule === '2_weeks' ? 'Company in 25%/50% stage for more than 2 weeks' : 'Company taking more than 6 weeks from 10% stage'}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.5,
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                bgcolor: '#fee2e2',
                                border: '1.5px solid #ef4444',
                            }}
                        >
                            <span style={{ fontSize: '0.8rem' }}>⏰</span>
                            <Typography sx={{
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                color: '#b91c1c',
                            }}>
                                {redColorRule === '2_weeks' ? '⚠️ 2+ Weeks in Stage' : '⚠️ 6+ Weeks Overall'}
                            </Typography>
                        </Box>
                    </Tooltip>
                )}

                {/* Reminder Indicator - Highlighted Compact */}
                {reminder && (
                    <Tooltip title="Click to view/edit reminder">
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewReminder && onViewReminder({ ...reminder, company: ticket.id });
                            }}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.5,
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: isDueReminder ? '#fee2e2' : '#dbeafe',
                                border: isDueReminder ? '1.5px solid #ef4444' : '1.5px solid #3b82f6',
                                '&:hover': {
                                    bgcolor: isDueReminder ? '#fecaca' : '#bfdbfe',
                                }
                            }}
                        >
                            <span style={{ fontSize: '0.8rem' }}>🔔</span>
                            <span style={{ fontSize: '0.8rem' }}>📝</span>
                            <Typography sx={{
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                color: isDueReminder ? '#b91c1c' : '#1d4ed8',
                            }}>
                                {isDueReminder ? (
                                    '⚠️ DUE!'
                                ) : (
                                    `${new Date(reminder.reminder_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} • ${new Date(reminder.reminder_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                )}
                            </Typography>
                        </Box>
                    </Tooltip>
                )}

                {teamLeadName && (
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666', display: 'block', mb: 0.5 }}>
                        👔 Team Lead: <Box component="span" sx={{ fontWeight: 'bold' }}>{teamLeadName}</Box>
                    </Typography>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            color: '#1a1a1a',
                            fontSize: '1.05rem',
                            lineHeight: 1.3,
                            flex: 1,
                            minWidth: 0,
                            overflowWrap: 'anywhere'
                        }}
                    >
                        {ticket.company_name}
                    </Typography>
                    <Tooltip title="Open in new tab">
                        <Box
                            component="a"
                            href={`/companies/${ticket.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                ml: 0.5,
                                mt: 0.25,
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: '#9e9e9e',
                                flexShrink: 0,
                                '&:hover': { color: '#1976d2' }
                            }}
                        >
                            <OpenInNewOutlined sx={{ fontSize: '1rem' }} />
                        </Box>
                    </Tooltip>
                </Box>

                {(ticket.business_type_name || ticket.offering_name) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                        {ticket.business_type_name && (
                            <Chip
                                label={ticket.business_type_name}
                                size="small"
                                sx={{
                                    ...getOfferingChipStyle(ticket.business_type_name),
                                    fontSize: '0.68rem',
                                    height: '20px',
                                    fontWeight: 500
                                }}
                            />
                        )}
                        {ticket.offering_name && (
                            <Chip
                                label={ticket.offering_name}
                                size="small"
                                sx={{
                                    ...getOfferingChipStyle(ticket.offering_name),
                                    fontSize: '0.68rem',
                                    height: '20px',
                                    fontWeight: 500
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* DND (Do-Not-Disturb) indicator / control — available on every card
                    regardless of stage (FR-008/009/012/013). Clicks are isolated from the
                    card so they don't trigger navigation or drag. */}
                <Box sx={{ mb: 0.5 }} onClick={(e) => e.stopPropagation()}>
                    {isDnd ? (
                        <>
                            <Chip
                                icon={<DoNotDisturbOn sx={{ fontSize: '0.9rem !important', color: 'white !important' }} />}
                                label="DND"
                                size="small"
                                onClick={(e) => { e.stopPropagation(); setDndPopoverAnchor(e.currentTarget); }}
                                sx={{
                                    bgcolor: '#c62828',
                                    color: 'white',
                                    fontWeight: 700,
                                    fontSize: '0.68rem',
                                    height: '20px',
                                    cursor: 'pointer',
                                    '& .MuiChip-icon': { ml: '4px' },
                                    '&:hover': { bgcolor: '#b71c1c' }
                                }}
                            />
                            <Popover
                                open={Boolean(dndPopoverAnchor)}
                                anchorEl={dndPopoverAnchor}
                                onClose={(e) => { e?.stopPropagation?.(); setDndPopoverAnchor(null); }}
                                onClick={(e) => e.stopPropagation()}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                PaperProps={{ sx: { p: 1.5, maxWidth: 280 } }}
                            >
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#c62828', mb: 0.5 }}>
                                    🚫 Do-Not-Disturb
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', mb: 0.25 }}>
                                    <b>Reason:</b> {ticket.dnd_details?.reason_display || '—'}
                                </Typography>
                                {ticket.dnd_details?.note && (
                                    <Typography sx={{ fontSize: '0.75rem', mb: 0.25 }}>
                                        <b>Note:</b> {ticket.dnd_details.note}
                                    </Typography>
                                )}
                                {ticket.dnd_details?.marked_by_name && (
                                    <Typography sx={{ fontSize: '0.72rem', color: '#666' }}>
                                        By {ticket.dnd_details.marked_by_name}
                                    </Typography>
                                )}
                                {ticket.dnd_details?.marked_at && (
                                    <Typography sx={{ fontSize: '0.72rem', color: '#666', mb: 0.75 }}>
                                        {new Date(ticket.dnd_details.marked_at).toLocaleString()}
                                    </Typography>
                                )}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    fullWidth
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDndPopoverAnchor(null);
                                        onRemoveDnd && onRemoveDnd(ticket);
                                    }}
                                    sx={{ mt: 0.5, fontSize: '0.72rem', textTransform: 'none' }}
                                >
                                    Remove from DND
                                </Button>
                            </Popover>
                        </>
                    ) : (
                        <Tooltip title="Add to DND">
                            <Chip
                                icon={<DoNotDisturbAltOutlined sx={{ fontSize: '0.85rem !important' }} />}
                                label="Add to DND"
                                size="small"
                                variant="outlined"
                                onClick={(e) => { e.stopPropagation(); onAddDnd && onAddDnd(ticket); }}
                                sx={{
                                    color: '#9e9e9e',
                                    borderColor: '#cfcfcf',
                                    fontSize: '0.65rem',
                                    height: '20px',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: '#c62828', color: '#c62828', bgcolor: 'rgba(198,40,40,0.04)' }
                                }}
                            />
                        </Tooltip>
                    )}
                </Box>

                {ticket.segment_name && (
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666', display: 'block', mb: 0.5 }}>
                        🧩 Segment: <Box component="span" sx={{ fontWeight: 'bold' }}>{ticket.segment_name}</Box>
                    </Typography>
                )}

                {ticket.partner_name && (
                    <Chip
                        size="small"
                        label={`🤝 ${ticket.partner_name}`}
                        sx={{ height: '20px', fontSize: '0.68rem', mb: 0.5 }}
                    />
                )}

                {(ticket.tci || ticket.total_contract_value) && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                        {ticket.tci && (
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666' }}>
                                💰 TCI: <Box component="span" sx={{ fontWeight: 'bold' }}>{fmtMoney(ticket.tci)}</Box>
                            </Typography>
                        )}
                        {ticket.total_contract_value && (
                            <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666' }}>
                                📊 TCV: <Box component="span" sx={{ fontWeight: 'bold' }}>{fmtMoney(ticket.total_contract_value)}</Box>
                            </Typography>
                        )}
                    </Box>
                )}

                {ticket.industry_name && (
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666', display: 'block', mb: 0.5 }}>
                        🏭 Industry: <Box component="span" sx={{ fontWeight: 'bold' }}>{ticket.industry_name}</Box>
                    </Typography>
                )}

                <Box sx={{ display: 'flex', gap: 1, fontSize: '0.8rem', color: '#666' }}>
                    <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                        📅 {ticket.created ? getDDMMYYYYFormat(ticket.created) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                        🔄 {ticket.modified ? getDDMMYYYYFormat(ticket.modified) : 'N/A'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1, mt: 0.75 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666' }}>
                        👤 BA: <Box component="span" sx={{ fontWeight: 'bold' }}>{ticket.deal_lead ? `${ticket.deal_lead.first_name}` : 'N/A'}</Box>
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.8rem', color: '#666' }}>
                        🎯 BM: <Box component="span" sx={{ fontWeight: 'bold' }}>{ticket.sam ? `${ticket.sam.first_name}` : 'N/A'}</Box>
                    </Typography>
                </Box>

                {/* Outreach Status Indicator */}
                {isZeroPercentStage && isOutreached && ticket.outreach_details && (
                    <Tooltip title={`Outreached by ${ticket.outreach_details.marked_by} - Click for details`}>
                        <Box
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5,
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                                bgcolor: '#e8f5e9',
                                border: '1px solid #4caf50',
                            }}
                        >
                            <span style={{ fontSize: '0.7rem' }}>✅</span>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#2e7d32' }}>
                                Outreached
                            </Typography>
                        </Box>
                    </Tooltip>
                )}

                {/* Invalid Company Indicator - Only for 5% stage */}
                {isFivePercentStage && isInvalid && ticket.invalid_details && (
                    <Tooltip title={`Invalid: ${ticket.invalid_details.reason || 'No reason'} - Click for details`}>
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewInvalidDetails && onViewInvalidDetails(ticket);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5,
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: '#fff3e0',
                                border: '1px solid #ff9800',
                                '&:hover': { bgcolor: '#ffe0b2' }
                            }}
                        >
                            <span style={{ fontSize: '0.7rem' }}>⚠️</span>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#e65100' }}>
                                Invalid
                            </Typography>
                            <InfoOutlined sx={{ fontSize: '0.7rem', color: '#e65100' }} />
                        </Box>
                    </Tooltip>
                )}

                {/* Newsletter Only Indicator - Only for 5% stage */}
                {isFivePercentStage && ticket.is_newsletter_only && ticket.newsletter_details && (
                    <Tooltip title={`Newsletter Only: ${ticket.newsletter_details.note || 'No note'} - Click for details`}>
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewNewsletterDetails && onViewNewsletterDetails(ticket);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5,
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: '#e3f2fd',
                                border: '1px solid #2196f3',
                                '&:hover': { bgcolor: '#bbdefb' }
                            }}
                        >
                            <span style={{ fontSize: '0.7rem' }}>📧</span>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1565c0' }}>
                                Newsletter Only
                            </Typography>
                            <InfoOutlined sx={{ fontSize: '0.7rem', color: '#1565c0' }} />
                        </Box>
                    </Tooltip>
                )}

                {/* Archive Indicator - Only for 100% stage */}
                {isHundredPercentStage && isArchived && ticket.archive_details && (
                    <Tooltip title={`Archived: ${ticket.archive_details.note || 'No note'} - Click for details`}>
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                onViewArchiveDetails && onViewArchiveDetails(ticket);
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mt: 0.5,
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: '#f3e5f5',
                                border: '1px solid #9c27b0',
                                '&:hover': { bgcolor: '#e1bee7' }
                            }}
                        >
                            <span style={{ fontSize: '0.7rem' }}>📦</span>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#7b1fa2' }}>
                                Archived
                            </Typography>
                            <InfoOutlined sx={{ fontSize: '0.7rem', color: '#7b1fa2' }} />
                        </Box>
                    </Tooltip>
                )}

                {/* Action Dropdown - Only for 0% stage and not yet outreached */}
                {isZeroPercentStage && !isOutreached && (
                    <>
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                setActionMenuAnchor(actionMenuAnchor ? null : e.currentTarget);
                            }}
                            sx={{
                                mt: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                px: 1,
                                py: 0.4,
                                borderRadius: 1,
                                cursor: 'pointer',
                                bgcolor: '#e3f2fd',
                                border: '1px solid #1976d2',
                                '&:hover': {
                                    bgcolor: '#bbdefb',
                                }
                            }}
                        >
                            <span style={{ fontSize: '0.7rem' }}>⚡</span>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#1565c0' }}>
                                Actions ▾
                            </Typography>
                        </Box>
                        {/* Dropdown Menu using MUI Menu */}
                        <Menu
                            anchorEl={actionMenuAnchor}
                            open={Boolean(actionMenuAnchor)}
                            onClose={(e) => {
                                e?.stopPropagation?.();
                                setActionMenuAnchor(null);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            PaperProps={{
                                sx: { minWidth: 180 }
                            }}
                        >
                            <MenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActionMenuAnchor(null);
                                    onMarkOutreached && onMarkOutreached(ticket);
                                }}
                                sx={{ fontSize: '0.8rem' }}
                            >
                                <span style={{ marginRight: 8 }}>📤</span>
                                Mark as Outreached
                            </MenuItem>
                            <MenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setActionMenuAnchor(null);
                                    onMarkInvalid && onMarkInvalid(ticket);
                                }}
                                sx={{ fontSize: '0.8rem', color: '#e65100' }}
                            >
                                <span style={{ marginRight: 8 }}>⚠️</span>
                                Invalid Company
                            </MenuItem>
                        </Menu>
                    </>
                )}

                {/* Archive Action - Only for 100% stage, super admin, and not yet archived */}
                {isHundredPercentStage && isSuperAdmin && !isArchived && (
                    <Box
                        onClick={(e) => {
                            e.stopPropagation();
                            onArchiveCompany && onArchiveCompany(ticket);
                        }}
                        sx={{
                            mt: 0.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.4,
                            borderRadius: 1,
                            cursor: 'pointer',
                            bgcolor: '#f3e5f5',
                            border: '1px solid #9c27b0',
                            '&:hover': {
                                bgcolor: '#e1bee7',
                            }
                        }}
                    >
                        <span style={{ fontSize: '0.7rem' }}>📦</span>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#7b1fa2' }}>
                            Archive
                        </Typography>
                    </Box>
                )}
            </CardContent>
        </Card>
    );
}

// Droppable Column Component - Jira/HubSpot Style
function DroppableColumn({ stageId, stageName, children, actualCount, isZeroPercentStage, isFivePercentStage, isHundredPercentStage, outreachFilter, onOutreachFilterChange, invalidFilter, onInvalidFilterChange, newsletterFilter, onNewsletterFilterChange, archiveFilter, onArchiveFilterChange, isSuperAdmin }) {
    const { setNodeRef, isOver } = useDroppable({
        id: `column-${stageId}`,
        data: {
            type: 'column',
            stageId,
            stageName
        }
    });

    return (
        <Box
            sx={{
                width: 300,
                minWidth: 300,
                maxWidth: 300,
                mr: 2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#f7f8f9',
                borderRadius: '8px',
                border: '1px solid #dfe1e6',
                overflow: 'hidden',
                transition: 'all 0.15s ease'
            }}
        >
            {/* Professional Header - Jira Style */}
            <Box
                sx={{
                    bgcolor: '#f7f8f9',
                    borderBottom: '1px solid #dfe1e6',
                    p: '12px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    flexShrink: 0,
                    minHeight: (isZeroPercentStage || (isHundredPercentStage && isSuperAdmin)) ? '70px' : '48px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Tooltip title={getStageDisplayName(stageName)} placement="top">
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#172b4d',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {getStageDisplayName(stageName)}
                        </Typography>
                    </Tooltip>

                    {/* Count Badge - HubSpot Style */}
                    <Box sx={{
                        bgcolor: '#deebff',
                        color: '#0052cc',
                        borderRadius: '12px',
                        px: '8px',
                        py: '2px',
                        minWidth: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        ml: 1
                    }}>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 600,
                                fontSize: '12px',
                                lineHeight: 1
                            }}
                        >
                            {actualCount}
                        </Typography>
                    </Box>
                </Box>

                {/* Outreach Filter Switch - Only for 0% stage */}
                {isZeroPercentStage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {outreachFilter === 'outreached' ? '✅ Outreached' : '📤 Not Outreached'}
                        </Typography>
                        <Box
                            onClick={() => onOutreachFilterChange && onOutreachFilterChange(outreachFilter === 'outreached' ? 'not_outreached' : 'outreached')}
                            sx={{
                                width: 36,
                                height: 18,
                                borderRadius: 9,
                                bgcolor: outreachFilter === 'outreached' ? '#4caf50' : '#e0e0e0',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                ml: 'auto'
                            }}
                        >
                            <Box sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: 'white',
                                position: 'absolute',
                                top: 2,
                                left: outreachFilter === 'outreached' ? 20 : 2,
                                transition: 'left 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                        </Box>
                    </Box>
                )}

                {/* Invalid Filter Switch - Only for 5% stage */}
                {isFivePercentStage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {invalidFilter === 'invalid' ? '⚠️ Show Invalid' : '✓ Hide Invalid'}
                        </Typography>
                        <Box
                            onClick={() => onInvalidFilterChange && onInvalidFilterChange(invalidFilter === 'invalid' ? 'not_invalid' : 'invalid')}
                            sx={{
                                width: 36,
                                height: 18,
                                borderRadius: 9,
                                bgcolor: invalidFilter === 'invalid' ? '#ff9800' : '#e0e0e0',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                ml: 'auto'
                            }}
                        >
                            <Box sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: 'white',
                                position: 'absolute',
                                top: 2,
                                left: invalidFilter === 'invalid' ? 20 : 2,
                                transition: 'left 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                        </Box>
                    </Box>
                )}

                {/* Newsletter Filter Switch - Only for 5% stage */}
                {isFivePercentStage && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {newsletterFilter === 'newsletter' ? '📧 Show Newsletter' : '✓ Hide Newsletter'}
                        </Typography>
                        <Box
                            onClick={() => onNewsletterFilterChange && onNewsletterFilterChange(newsletterFilter === 'newsletter' ? 'not_newsletter' : 'newsletter')}
                            sx={{
                                width: 36,
                                height: 18,
                                borderRadius: 9,
                                bgcolor: newsletterFilter === 'newsletter' ? '#2196f3' : '#e0e0e0',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                ml: 'auto'
                            }}
                        >
                            <Box sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: 'white',
                                position: 'absolute',
                                top: 2,
                                left: newsletterFilter === 'newsletter' ? 20 : 2,
                                transition: 'left 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                        </Box>
                    </Box>
                )}

                {/* Archive Filter Switch - Only for 100% stage and super admin */}
                {isHundredPercentStage && isSuperAdmin && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#666' }}>
                            {archiveFilter === 'archived' ? '📦 Show Archived' : '✓ Hide Archived'}
                        </Typography>
                        <Box
                            onClick={() => onArchiveFilterChange && onArchiveFilterChange(archiveFilter === 'archived' ? 'not_archived' : 'archived')}
                            sx={{
                                width: 36,
                                height: 18,
                                borderRadius: 9,
                                bgcolor: archiveFilter === 'archived' ? '#9c27b0' : '#e0e0e0',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                ml: 'auto'
                            }}
                        >
                            <Box sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: 'white',
                                position: 'absolute',
                                top: 2,
                                left: archiveFilter === 'archived' ? 20 : 2,
                                transition: 'left 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                            }} />
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Drop Zone */}
            <Box
                ref={setNodeRef}
                sx={{
                    flex: 1,
                    bgcolor: isOver ? '#e4fcff' : '#ffffff',
                    transition: 'background-color 0.15s ease',
                    minHeight: 100,
                    border: isOver ? '2px dashed #00b8d9' : 'none',
                    borderTop: 'none',
                    position: 'relative'
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

// LocalStorage key for persisting filters
const FILTERS_STORAGE_KEY = 'dashboard_filters';

function DashboardContent() {
    const navigate = useNavigate();
    const userInfo = UserInfo();

    // Helper function to load filters from localStorage
    const loadFiltersFromLocalStorage = useCallback(() => {
        try {
            const savedFilters = localStorage.getItem(FILTERS_STORAGE_KEY);
            if (savedFilters) {
                const parsed = JSON.parse(savedFilters);
                return parsed;
            }
        } catch (error) {
            console.error('Failed to load filters from localStorage:', error);
        }
        return null;
    }, []);

    // Load saved filters once
    const savedFilters = useMemo(() => loadFiltersFromLocalStorage(), [loadFiltersFromLocalStorage]);

    // State management - grouped by functionality
    const [isLoading, setIsLoading] = useState(true);
    const [viewType, setViewType] = useState('stage');
    // Assistant Hook
    const assistant = useAssistant();


    // Drag and drop states
    const [activeId, setActiveId] = useState(null);
    const [draggedTicket, setDraggedTicket] = useState(null);
    const [recentlyUpdated, setRecentlyUpdated] = useState(null);

    // Filter states - Initialize from localStorage
    const [selectedDateRange, setSelectedDateRange] = useState(savedFilters?.selectedDateRange || []);
    const [filterParams, setFilterParams] = useState(savedFilters?.filterParams || null);
    const [selectedCountries, setSelectedCountries] = useState(savedFilters?.selectedCountries || []);
    const [selectedOfferings, setSelectedOfferings] = useState(savedFilters?.selectedOfferings || []);
    const [selectedBusinessTypes, setSelectedBusinessTypes] = useState(savedFilters?.selectedBusinessTypes || []);
    const [selectedPartners, setSelectedPartners] = useState(savedFilters?.selectedPartners || []);
    const [introducedByPartnerOnly, setIntroducedByPartnerOnly] = useState(savedFilters?.introducedByPartnerOnly || false);
    const [searchTerm, setSearchTerm] = useState(savedFilters?.searchTerm || '');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [selectedCreatedBy, setSelectedCreatedBy] = useState('');

    // Data states
    const [userData, setUserData] = useState([]);
    const [industryData, setIndustryData] = useState([]);
    const [stageViewCountries, setStageViewCountries] = useState([]);
    const [stageViewOfferings, setStageViewOfferings] = useState([]);
    const [stageViewBusinessTypes, setStageViewBusinessTypes] = useState([]);
    const [stagePartners, setStagePartners] = useState([]);
    const [globalTotal, setGlobalTotal] = useState([0, 0]);

    // Column visibility state - Initialize from localStorage
    const [visibleColumns, setVisibleColumns] = useState(savedFilters?.visibleColumns || []);

    // Tickets and pagination
    const [tickets, setTickets] = useState({});
    const [loadingMore, setLoadingMore] = useState({});
    const [hasMore, setHasMore] = useState({});
    const [currentPages, setCurrentPages] = useState({});
    const [totalCounts, setTotalCounts] = useState({});
    const [userSpecificCounts, setUserSpecificCounts] = useState({});
    const [countryCounts, setCountryCounts] = useState({});
    const [summaryData, setSummaryData] = useState([]);

    // Add Company and Import states
    const [isAddCompanyDrawerOpen, setIsAddCompanyDrawerOpen] = useState(false);
    const [isImportExcelDialogOpen, setIsImportExcelDialogOpen] = useState(false);
    const [isStageRulesModalOpen, setIsStageRulesModalOpen] = useState(false);

    // Deal Details Dialog states
    const [dealDetailsDialogOpen, setDealDetailsDialogOpen] = useState(false);
    const [pendingDragData, setPendingDragData] = useState(null);

    // QM-exit comment prompt states (US-4)
    const [qmExitCommentDialogOpen, setQmExitCommentDialogOpen] = useState(false);
    const [qmExitComment, setQmExitComment] = useState('');

    // Financial capture gate states (B1 — T9): drag into a >=50% stage must
    // capture TCI/TCV before the move is allowed to proceed.
    const [stagePercentMap, setStagePercentMap] = useState({});
    const [financialCaptureOpen, setFinancialCaptureOpen] = useState(false);
    const [financialCaptureData, setFinancialCaptureData] = useState(null); // { draggedTicket, sourceStage, targetStage }
    const [financialInitialValues, setFinancialInitialValues] = useState(null);
    const [financialCaptureCurrencyLabel, setFinancialCaptureCurrencyLabel] = useState(undefined);
    const financialFetchTicketIdRef = useRef(null); // guards against out-of-order pre-fill fetches (latest drag wins)

    // Task Assignment Drawer states (for 10% stage)
    const [taskAssignmentDrawerOpen, setTaskAssignmentDrawerOpen] = useState(false);

    // Team Assignment Drawer states
    const [teamAssignmentDrawerOpen, setTeamAssignmentDrawerOpen] = useState(false);

    // 5% Stage Reminder Dialog states
    const [fivePercentReminderDialogOpen, setFivePercentReminderDialogOpen] = useState(false);
    const [dueReminderCompanyIds, setDueReminderCompanyIds] = useState([]);
    const [companyReminders, setCompanyReminders] = useState({}); // Map of companyId -> reminder data
    const [reminderNoteDialogOpen, setReminderNoteDialogOpen] = useState(false);
    const [selectedReminderNote, setSelectedReminderNote] = useState(null);
    const [reminderRemovalConfirmOpen, setReminderRemovalConfirmOpen] = useState(false);
    const [pendingReminderRemoval, setPendingReminderRemoval] = useState(null); // Store pending stage change that requires reminder removal

    const [excelImportSummary, setExcelImportSummary] = useState(null);
    const [errorExcelFileLink, setErrorExcelFileLink] = useState(null);
    const [importStatus, setImportStatus] = useState('Pending');
    const [isExcelImportProcessing, setIsExcelImportProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isExcelImportCompleted, setIsExcelImportCompleted] = useState(false);

    // Outreach filter and modal states
    const [outreachFilter, setOutreachFilter] = useState('not_outreached'); // 'outreached' or 'not_outreached'
    const [markOutreachDialogOpen, setMarkOutreachDialogOpen] = useState(false);
    const [selectedOutreachCompany, setSelectedOutreachCompany] = useState(null);
    const [outreachComment, setOutreachComment] = useState('');
    const [outreachMethod, setOutreachMethod] = useState('email');
    const [outreachDate, setOutreachDate] = useState('');
    const [outreachTime, setOutreachTime] = useState('');
    const [isMarkingOutreach, setIsMarkingOutreach] = useState(false);

    // Invalid company filter and modal states
    const [invalidFilter, setInvalidFilter] = useState('not_invalid'); // 'invalid' or 'not_invalid'
    const [markInvalidDialogOpen, setMarkInvalidDialogOpen] = useState(false);
    const [selectedInvalidCompany, setSelectedInvalidCompany] = useState(null);
    const [invalidReasons, setInvalidReasons] = useState([]);
    const [selectedInvalidReason, setSelectedInvalidReason] = useState('');
    const [invalidDescription, setInvalidDescription] = useState('');
    const [isMarkingInvalid, setIsMarkingInvalid] = useState(false);
    const [invalidDetailsDrawerOpen, setInvalidDetailsDrawerOpen] = useState(false);
    const [viewingInvalidCompany, setViewingInvalidCompany] = useState(null);
    const [isRemovingInvalid, setIsRemovingInvalid] = useState(false);

    // DND (Do-Not-Disturb) state — the "Add to DND" dialog is shared/lifted here (mirrors
    // how the invalid modal lives on the parent); the per-card status popover is local.
    const [addDndDialogOpen, setAddDndDialogOpen] = useState(false);
    const [addDndCompany, setAddDndCompany] = useState(null);

    // Newsletter company filter state
    const [newsletterFilter, setNewsletterFilter] = useState('not_newsletter'); // 'newsletter' or 'not_newsletter'
    const [newsletterDetailsDrawerOpen, setNewsletterDetailsDrawerOpen] = useState(false);
    const [viewingNewsletterCompany, setViewingNewsletterCompany] = useState(null);
    const [isRemovingNewsletter, setIsRemovingNewsletter] = useState(false);

    // Archive company filter state (100% stage - super admin only)
    const [archiveFilter, setArchiveFilter] = useState('not_archived'); // 'archived' or 'not_archived'
    const [archiveDetailsDrawerOpen, setArchiveDetailsDrawerOpen] = useState(false);
    const [viewingArchivedCompany, setViewingArchivedCompany] = useState(null);
    const [isArchiving, setIsArchiving] = useState(false);
    const [isUnarchiving, setIsUnarchiving] = useState(false);
    const [archiveNote, setArchiveNote] = useState('');
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [companyToArchive, setCompanyToArchive] = useState(null);

    // Refs
    const scrollRefs = useRef({});
    const horizontalScrollRef = useRef(null);
    const abortControllersRef = useRef({});
    const isInitialMount = useRef(true);
    const lastDragEndTimeRef = useRef(0);
    const loadTicketsRef = useRef(null); // Reference to loadTickets function
    const [highlightedCompanyId, setHighlightedCompanyId] = useState(null);
    const [fetchedDealDetails, setFetchedDealDetails] = useState(null);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Memoized auth headers
    const authHeaders = useMemo(() => ({
        Authorization: `Token ${localStorage.getItem("authToken")}`
    }), []);

    // Memoized base URL
    const baseURL = useMemo(() =>
        `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}/api/v1`,
        []
    );

    // Debounce search term - faster when clearing (100ms) vs typing (800ms)
    useEffect(() => {
        const delay = searchTerm === '' ? 100 : 800;
        const timeoutId = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, delay);
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Abort ongoing requests when filters change
    const abortPendingRequests = useCallback(() => {
        Object.values(abortControllersRef.current).forEach(controller => {
            controller.abort();
        });
        abortControllersRef.current = {};
    }, []);

    // 🔄 Silent API sync - Jira-like background update
    const updateCompanyStage = useCallback(async (companyId, newStageId, comment) => {
        try {
            console.log(`Updating company ${companyId} to stage ${newStageId}`);
            await apiInstance.patch(
                `${baseURL}/company/${companyId}/update-stage/`,
                { stage_id: newStageId, ...(comment ? { comment } : {}) },
                { headers: authHeaders }
            );
            console.log(`✅ Stage update successful for company ${companyId}`);
            // ✅ Silent success - no toast for smooth UX
            return true;
        } catch (error) {
            console.error(`❌ Failed to update stage for company ${companyId}:`, error);
            // ❌ Silent fail - no blocking error messages
            return false;
        }
    }, [baseURL, authHeaders]);

    // Drag and drop handlers
    const handleDragStart = useCallback((event) => {
        const { active } = event;
        setActiveId(active.id);

        // Find the dragged ticket
        let foundTicket = null;
        Object.values(tickets).forEach(stageTickets => {
            const ticket = stageTickets.find(t => t.id === active.id);
            if (ticket) {
                foundTicket = ticket;
            }
        });
        setDraggedTicket(foundTicket);
    }, [tickets]);

    // (isStage100Percent / isStage5Percent removed — the drag-drop handler no longer
    // opens 100% deal-details or 5% reminder dialogs; only the Qualified Meetings
    // task drawer prompts on a stage change.)

    // Helper function to extract percentage from stage name
    const getStagePercentage = (stage) => {
        if (!stage || !stage.name) return null;
        const percentMatch = stage.name.match(/(\d+)%/);
        return percentMatch ? parseInt(percentMatch[1]) : null;
    };

    // Define valid stage progression sequence (excluding 5% - it's a special bypass stage)
    const STAGE_PROGRESSION = [0, 10, 25, 50, 75, 100];  // 90% stage removed from the board

    // Function to validate stage progression
    const validateStageProgression = (currentStage, newStage) => {
        const currentPercent = getStagePercentage(currentStage);
        const newPercent = getStagePercentage(newStage);

        // If we can't determine percentages, allow the change
        if (currentPercent === null || newPercent === null) {
            return { isValid: true, message: '' };
        }

        // Allow moving to 5% from any stage (rejection/on-hold stage)
        if (newPercent === 5) {
            return { isValid: true, message: '' };
        }

        // Allow moving FROM 5% to any other stage (since 5% is a bypass/holding stage)
        if (currentPercent === 5) {
            return { isValid: true, message: '' };
        }

        // Allow moving backward (to a lower percentage)
        if (newPercent < currentPercent) {
            return { isValid: true, message: '' };
        }

        // Find the current and new stage positions in the progression
        const currentIndex = STAGE_PROGRESSION.indexOf(currentPercent);
        const newIndex = STAGE_PROGRESSION.indexOf(newPercent);

        // If stages not in progression array, allow the change
        if (currentIndex === -1 || newIndex === -1) {
            return { isValid: true, message: '' };
        }

        // Check if user is trying to skip stages (moving forward more than 1 step)
        if (newIndex > currentIndex + 1) {
            const nextStage = STAGE_PROGRESSION[currentIndex + 1];
            return {
                isValid: false,
                message: `You cannot skip stages. Please move to ${nextStage}% first before advancing to ${newPercent}%.`
            };
        }

        return { isValid: true, message: '' };
    };

    const handleDragOver = useCallback((event) => {
        const { active, over } = event;

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // If dropping over a column
        if (overId.toString().startsWith('column-')) {
            return;
        }
    }, []);

    // 🚀 Perform the instant (Jira-like) move: optimistic UI + silent background sync.
    // `comment` (optional) is forwarded to the PATCH body for a QM-exit note.
    const performInstantMove = useCallback((draggedTicket, sourceStage, targetStage, comment) => {
        // 🚀 INSTANT UI UPDATE - Move ticket immediately (Jira-like!)
        setTickets(prev => {
            const newTickets = { ...prev };

            // Remove from source
            newTickets[sourceStage.name] = newTickets[sourceStage.name].filter(
                t => t.id !== draggedTicket.id
            );

            // Add to target (at top)
            if (!newTickets[targetStage.name]) {
                newTickets[targetStage.name] = [];
            }
            newTickets[targetStage.name] = [draggedTicket, ...newTickets[targetStage.name]];

            return newTickets;
        });

        // 📊 SILENT COUNT SYNC - Update counts instantly
        setSummaryData(prev => prev.map(stage => {
            if (stage.id === sourceStage.id) {
                return { ...stage, count: Math.max(0, stage.count - 1) };
            }
            if (stage.id === targetStage.id) {
                return { ...stage, count: stage.count + 1 };
            }
            return stage;
        }));

        // 🔄 BACKGROUND API SYNC - Fire and forget
        updateCompanyStage(draggedTicket.id, targetStage.id, comment).catch(() => {
            // Silent fail - could add subtle notification if needed
        });

        // Set cooldown to prevent API-triggered reloads
        lastDragEndTimeRef.current = Date.now();

        // ✅ DONE! User sees instant feedback, API syncs silently
    }, [updateCompanyStage]);

    // Financial capture gate (B1 fix): the board ticket carries no financial
    // fields, so opening the dialog straight from `draggedTicket` produces an
    // EMPTY form on a re-drag of an already-captured company — re-typing there
    // can overwrite the stored financials with wrong values. Fetch the
    // company's CURRENT stored financials first and pre-fill from those.
    // Never blocks the dialog: any fetch error falls back to empty values.
    const openFinancialCaptureWithFetch = useCallback(async (ticket) => {
        // Mark this the latest pending pre-fill fetch; a later drag supersedes it so an
        // out-of-order response can't seed one company's financials for another (data-integrity).
        const requestId = ticket.id;
        financialFetchTicketIdRef.current = requestId;
        try {
            const res = await apiInstance.get(
                `${baseURL}/company/${ticket.id}/`,
                { headers: authHeaders }
            );
            if (financialFetchTicketIdRef.current !== requestId) return; // superseded by a newer drag
            const c = res?.data?.data ?? res?.data ?? {};
            setFinancialInitialValues({
                tci: c.tci ?? '',
                total_contract_value: c.total_contract_value ?? '',
                one_time_saas: c.one_time_saas ?? '',
                mrr: c.mrr ?? '',
            });
            setFinancialCaptureCurrencyLabel(c?.currency?.label);
        } catch (error) {
            if (financialFetchTicketIdRef.current !== requestId) return; // superseded by a newer drag
            setFinancialInitialValues({
                tci: '',
                total_contract_value: '',
                one_time_saas: '',
                mrr: '',
            });
            setFinancialCaptureCurrencyLabel(undefined);
        }
        if (financialFetchTicketIdRef.current !== requestId) return; // don't open for a superseded request
        setFinancialCaptureOpen(true);
    }, [baseURL, authHeaders]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        // 🎯 INSTANT RESET - Clear drag overlay immediately
        setActiveId(null);
        setDraggedTicket(null);

        if (!over) return;

        const activeId = active.id;
        const overId = over.id;

        // Find dragged ticket and stages
        let draggedTicket = null;
        let sourceStage = null;
        let targetStage = null;

        // Find source
        for (const [stageName, stageTickets] of Object.entries(tickets)) {
            const ticket = stageTickets.find(t => t.id === activeId);
            if (ticket) {
                draggedTicket = ticket;
                sourceStage = summaryData.find(s => s.name === stageName);
                break;
            }
        }

        if (!draggedTicket || !sourceStage) return;

        // Find target
        if (overId.toString().startsWith('column-')) {
            const targetStageId = overId.toString().replace('column-', '');
            targetStage = summaryData.find(s => s.id.toString() === targetStageId);
        } else {
            for (const [stageName, stageTickets] of Object.entries(tickets)) {
                if (stageTickets.find(t => t.id === overId)) {
                    targetStage = summaryData.find(s => s.name === stageName);
                    break;
                }
            }
        }

        if (!targetStage || sourceStage.id === targetStage.id) return;

        // 🔥 PRIORITY 0: Validate stage progression sequence
        const progressionValidation = validateStageProgression(sourceStage, targetStage);

        if (!progressionValidation.isValid) {
            // Show error message
            showToast(progressionValidation.message, 'error');
            return;
        }

        // The ONLY auto-prompt on a stage change: open the Intro Call task-assignment
        // drawer when a company ENTERS Qualified Meetings (meetings are scheduled/
        // taken there). No team-assignment, no 100% deal-details, no 5% reminder —
        // every other move is instant (team/deal assignment is handled separately).
        const stageNameLower = targetStage?.name?.toLowerCase() || '';

        if (stageNameLower.includes('qualified meetings')) {
            setPendingDragData({ draggedTicket, sourceStage, targetStage });
            setTaskAssignmentDrawerOpen(true);
            return;
        }

        // QM-EXIT NOTE: prompt for a note the first time a company moves FORWARD out of
        // Qualified Meetings (FR-012). Skip when a note was already captured (FR-013).
        const srcLower = sourceStage?.name?.toLowerCase() || '';
        const srcIdx = summaryData.findIndex(s => s.id === sourceStage.id);
        const tgtIdx = summaryData.findIndex(s => s.id === targetStage.id);
        const isForwardOutOfQM = srcLower === 'qualified meetings' && srcIdx !== -1 && tgtIdx !== -1 && tgtIdx > srcIdx;
        if (isForwardOutOfQM && !draggedTicket.has_qm_exit_comment) {   // FR-013: skip if already captured
            setPendingDragData({ draggedTicket, sourceStage, targetStage });
            setQmExitComment('');
            setQmExitCommentDialogOpen(true);
            return;                                                     // FR-012: do not move yet
        }

        // FINANCIAL CAPTURE GATE (B1 — FR-007/FR-010/FR-011/FR-012): entering a
        // >=50% stage requires TCI/TCV before the move is allowed. Gate-first —
        // no optimistic move here, so a Cancel needs no rollback. Re-fires on
        // every gated move (FR-012), regardless of prior capture.
        if (isGatedStage(targetStage, stagePercentMap)) {
            setFinancialCaptureData({ draggedTicket, sourceStage, targetStage });
            openFinancialCaptureWithFetch(draggedTicket);              // pre-fill from stored financials (fire-and-forget)
            return;                                                     // FR-011: no move until confirmed
        }

        // 🚀 INSTANT UI UPDATE + silent background sync
        performInstantMove(draggedTicket, sourceStage, targetStage);
    }, [tickets, summaryData, performInstantMove, stagePercentMap, openFinancialCaptureWithFetch]);

    // QM-exit note: confirm → move with comment attached
    const handleQmExitCommentConfirm = useCallback(() => {
        if (!qmExitComment.trim()) return;
        if (!pendingDragData) return;
        const { draggedTicket, sourceStage, targetStage } = pendingDragData;
        performInstantMove(draggedTicket, sourceStage, targetStage, qmExitComment.trim());
        setQmExitCommentDialogOpen(false);
        setPendingDragData(null);
        setQmExitComment('');
    }, [qmExitComment, pendingDragData, performInstantMove]);

    // QM-exit note: cancel → abort the move entirely (FR-017)
    const handleQmExitCommentCancel = useCallback(() => {
        setQmExitCommentDialogOpen(false);
        setPendingDragData(null);
        setQmExitComment('');
    }, []);

    // Financial capture gate: confirm → persist financials FIRST (FR-010), and
    // only on success move the card. `is_stage_capture: true` routes the PATCH
    // through the shared write path so it always records financials history
    // (FR-012), and `stage_id` stamps the target stage server-side.
    const handleFinancialCaptureConfirm = useCallback(async (values) => {
        if (!financialCaptureData) return;
        const { draggedTicket, sourceStage, targetStage } = financialCaptureData;

        try {
            await apiInstance.patch(
                `${baseURL}/company/${draggedTicket.id}/update-deal-fields/`,
                {
                    tci: values.tci,
                    total_contract_value: values.total_contract_value,
                    one_time_saas: values.one_time_saas,
                    mrr: values.mrr,
                    stage_id: targetStage.id,
                    is_stage_capture: true,
                },
                { headers: authHeaders }
            );
        } catch (error) {
            // ❌ Save failed — keep the dialog open with the user's entered values, no move (FR-010)
            showToast(error.response?.data?.message || 'Failed to save deal financials', 'error');
            return;
        }

        // ✅ Financials saved — close/clear the dialog, then move the card (server gate now passes)
        setFinancialCaptureOpen(false);
        setFinancialCaptureData(null);
        setFinancialInitialValues(null);
        performInstantMove(draggedTicket, sourceStage, targetStage);
    }, [financialCaptureData, baseURL, authHeaders, performInstantMove]);

    // Financial capture gate: cancel → abort the move entirely, no rollback needed (FR-011)
    const handleFinancialCaptureCancel = useCallback(() => {
        setFinancialCaptureOpen(false);
        setFinancialCaptureData(null);
        setFinancialInitialValues(null);
        setFinancialCaptureCurrencyLabel(undefined);
    }, []);

    // Handle deal details save
    const handleDealDetailsSave = useCallback(async (dealData) => {
        if (!pendingDragData) return;

        const { draggedTicket, sourceStage, targetStage } = pendingDragData;

        try {
            // Save deal details first
            await tasksApi.saveClosedDealDetails(draggedTicket.id, dealData);

            // Check if target stage is 100% (Closed)
            const stageNameLower = targetStage?.name?.toLowerCase() || '';
            const is100PercentStage = stageNameLower.includes('100') || stageNameLower.includes('closed');

            if (is100PercentStage) {
                // Close deal details drawer
                setDealDetailsDialogOpen(false);

                // Open team assignment drawer after a small delay for smooth transition
                setTimeout(() => {
                    setTeamAssignmentDrawerOpen(true);
                }, 300);

                // Don't clear pendingDragData here - it's needed for team assignment
                // Don't move company yet - it will be moved after team assignment
                showToast('Deal details saved. Please assign team members.', 'success');
                return;
            }

            // Then move the company (for non-100% stages, though currently this dialog only opens for 100%)
            setTickets(prev => {
                const newTickets = { ...prev };
                newTickets[sourceStage.name] = newTickets[sourceStage.name].filter(
                    t => t.id !== draggedTicket.id
                );
                if (!newTickets[targetStage.name]) {
                    newTickets[targetStage.name] = [];
                }
                newTickets[targetStage.name] = [draggedTicket, ...newTickets[targetStage.name]];
                return newTickets;
            });

            setSummaryData(prev => prev.map(stage => {
                if (stage.id === sourceStage.id) {
                    return { ...stage, count: Math.max(0, stage.count - 1) };
                }
                if (stage.id === targetStage.id) {
                    return { ...stage, count: stage.count + 1 };
                }
                return stage;
            }));

            await updateCompanyStage(draggedTicket.id, targetStage.id);

            showToast('Company moved to Closed - 100% successfully', 'success');
            setDealDetailsDialogOpen(false);
            setPendingDragData(null);
            lastDragEndTimeRef.current = Date.now();
        } catch (error) {
            showErrorToast('Failed to save deal details');
        }
    }, [pendingDragData, updateCompanyStage]);

    // Handle deal details cancel
    const handleDealDetailsCancel = useCallback(() => {
        setDealDetailsDialogOpen(false);
        setPendingDragData(null);
        setFetchedDealDetails(null);
    }, []);

    // Handle 5% reminder save
    const handleFivePercentReminderSave = useCallback(async (reminderData) => {
        if (!pendingDragData) return;

        const { draggedTicket, sourceStage, targetStage } = pendingDragData;

        try {
            // Create the reminder via API
            const createResponse = await apiInstance.post(
                `${baseURL}/stage-reminders/`,
                {
                    company_id: draggedTicket.id,
                    previous_stage_id: sourceStage?.id || null,
                    target_stage_id: targetStage?.id || null,
                    note: reminderData.note,
                    reminder_date: reminderData.reminderDate,
                },
                { headers: authHeaders }
            );

            // Get the created reminder data with ID
            const createdReminder = createResponse?.data?.data || createResponse?.data;

            console.log('📦 Moving ticket:', {
                ticketId: draggedTicket.id,
                sourceStage: sourceStage.name,
                targetStage: targetStage.name
            });

            // Move the ticket to 5% stage
            setTickets(prev => {
                const newTickets = { ...prev };
                console.log('📦 Available stage keys:', Object.keys(newTickets));
                console.log('📦 Source stage tickets before:', newTickets[sourceStage.name]?.length);

                // Remove from source
                if (newTickets[sourceStage.name]) {
                    newTickets[sourceStage.name] = newTickets[sourceStage.name].filter(
                        t => t.id !== draggedTicket.id
                    );
                }

                // Ensure target stage array exists
                if (!newTickets[targetStage.name]) {
                    newTickets[targetStage.name] = [];
                }

                // Add reminder info to the ticket
                const ticketWithReminder = {
                    ...draggedTicket,
                    has_reminder: true,
                    reminder_date: reminderData.reminderDate,
                    reminder_note: reminderData.note
                };
                newTickets[targetStage.name] = [ticketWithReminder, ...newTickets[targetStage.name]];

                console.log('📦 Source stage tickets after:', newTickets[sourceStage.name]?.length);
                console.log('📦 Target stage tickets after:', newTickets[targetStage.name]?.length);

                return newTickets;
            });

            // Update counts
            setSummaryData(prev => prev.map(stage => {
                if (stage.id === sourceStage.id) {
                    return { ...stage, count: Math.max(0, stage.count - 1) };
                }
                if (stage.id === targetStage.id) {
                    return { ...stage, count: stage.count + 1 };
                }
                return stage;
            }));

            // Update company stage via API
            const stageUpdateSuccess = await updateCompanyStage(draggedTicket.id, targetStage.id);
            if (!stageUpdateSuccess) {
                console.error('Failed to update company stage to 5%');
                showErrorToast('Reminder saved but failed to update stage. Please refresh.');
            }

            // Store reminder in local state for icons (with ID from API)
            setCompanyReminders(prev => ({
                ...prev,
                [draggedTicket.id]: {
                    id: createdReminder?.id,
                    company: draggedTicket.id,
                    note: reminderData.note,
                    reminder_date: reminderData.reminderDate,
                    is_active: true
                }
            }));

            showToast('Company moved to 5% with reminder set', 'success');
            setFivePercentReminderDialogOpen(false);
            setPendingDragData(null);
            lastDragEndTimeRef.current = Date.now();

        } catch (error) {
            console.error('Error saving 5% reminder:', error);
            showErrorToast('Failed to save reminder. Please try again.');
        }
    }, [pendingDragData, updateCompanyStage, baseURL, authHeaders]);

    // Handle 5% reminder cancel
    const handleFivePercentReminderCancel = useCallback(() => {
        setFivePercentReminderDialogOpen(false);
        setPendingDragData(null);
    }, []);

    // Handle 5% newsletter only save
    const handleFivePercentNewsletterSave = useCallback(async ({ note }) => {
        if (!pendingDragData) return;

        const { draggedTicket, sourceStage, targetStage } = pendingDragData;

        try {
            // First, mark the company for newsletter only
            const newsletterResponse = await apiInstance.post(
                `${baseURL}/company/${draggedTicket.id}/mark-newsletter/`,
                { note },
                { headers: authHeaders }
            );

            if (!newsletterResponse?.data?.success) {
                throw new Error(newsletterResponse?.data?.message || 'Failed to mark for newsletter');
            }

            // Then move the company to 5% stage
            await updateCompanyStage(draggedTicket.id, targetStage.id);

            // Update local tickets state - add is_newsletter_only flag
            setTickets(prev => {
                const newTickets = { ...prev };
                // Remove from source
                newTickets[sourceStage.name] = (prev[sourceStage.name] || []).filter(t => t.id !== draggedTicket.id);
                // Add to target with newsletter flag
                const ticketWithNewsletter = {
                    ...draggedTicket,
                    stage_name: targetStage.name,
                    is_newsletter_only: true,
                    newsletter_details: newsletterResponse.data.data
                };
                newTickets[targetStage.name] = [ticketWithNewsletter, ...(prev[targetStage.name] || [])];
                return newTickets;
            });

            // Update counts
            setSummaryData(prev => prev.map(stage => {
                if (stage.id === sourceStage.id) {
                    return { ...stage, count: Math.max(0, stage.count - 1) };
                }
                if (stage.id === targetStage.id) {
                    return { ...stage, count: stage.count + 1 };
                }
                return stage;
            }));

            showToast('Company moved to 5% and marked for newsletter only', 'success');
            setFivePercentReminderDialogOpen(false);
            setPendingDragData(null);
            lastDragEndTimeRef.current = Date.now();

        } catch (error) {
            console.error('Error marking newsletter only:', error);
            showErrorToast(error?.response?.data?.message || 'Failed to mark for newsletter. Please try again.');
        }
    }, [pendingDragData, updateCompanyStage, baseURL, authHeaders]);

    // Fetch due reminders (companies that need attention)
    const fetchDueReminders = useCallback(async () => {
        try {
            const response = await apiInstance.get(
                `${baseURL}/stage-reminders/due-reminders/`,
                { headers: authHeaders }
            );
            if (response?.data?.success) {
                setDueReminderCompanyIds(response.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching due reminders:', error);
        }
    }, [baseURL, authHeaders]);

    // Fetch all active reminders (for companies in 5% stage)
    const fetchAllActiveReminders = useCallback(async () => {
        try {
            const response = await apiInstance.get(
                `${baseURL}/stage-reminders/?is_active=true&page_size=500`,
                { headers: authHeaders }
            );
            if (response?.data?.results?.data || response?.data?.data) {
                const reminders = response?.data?.results?.data || response?.data?.data || [];
                const remindersMap = {};
                reminders.forEach(reminder => {
                    if (reminder.company) {
                        remindersMap[reminder.company] = reminder;
                    }
                });
                setCompanyReminders(remindersMap);
            }
        } catch (error) {
            console.error('Error fetching active reminders:', error);
        }
    }, [baseURL, authHeaders]);

    // Fetch reminder for a specific company
    const fetchCompanyReminder = useCallback(async (companyId) => {
        try {
            const response = await apiInstance.get(
                `${baseURL}/stage-reminders/by-company/${companyId}/`,
                { headers: authHeaders }
            );
            if (response?.data?.success && response?.data?.data) {
                setCompanyReminders(prev => ({
                    ...prev,
                    [companyId]: response.data.data
                }));
                return response.data.data;
            }
        } catch (error) {
            console.error('Error fetching company reminder:', error);
        }
        return null;
    }, [baseURL, authHeaders]);

    // Handle viewing reminder note
    const handleViewReminderNote = useCallback(async (reminder) => {
        // If reminder doesn't have an ID, fetch it from the API
        if (!reminder?.id && reminder?.company) {
            try {
                const response = await apiInstance.get(
                    `${baseURL}/stage-reminders/by-company/${reminder.company}/`,
                    { headers: authHeaders }
                );
                if (response?.data?.success && response?.data?.data) {
                    const fullReminder = response.data.data;
                    setCompanyReminders(prev => ({
                        ...prev,
                        [reminder.company]: fullReminder
                    }));
                    setSelectedReminderNote(fullReminder);
                    setReminderNoteDialogOpen(true);
                    return;
                }
            } catch (error) {
                console.error('Error fetching reminder details:', error);
            }
        }
        setSelectedReminderNote(reminder);
        setReminderNoteDialogOpen(true);
    }, [baseURL, authHeaders]);

    // Handle editing reminder
    const handleEditReminder = useCallback(async (reminderId, updateData) => {
        try {
            const response = await apiInstance.patch(
                `${baseURL}/stage-reminders/${reminderId}/`,
                updateData,
                { headers: authHeaders }
            );

            // Update local state with new data
            const updatedReminder = response?.data?.data || response?.data;
            const companyId = selectedReminderNote?.company;

            console.log('Edit response:', updatedReminder);
            console.log('Company ID:', companyId);

            if (companyId) {
                // Merge updated data with existing reminder data, preserving important fields
                setCompanyReminders(prev => {
                    const existingReminder = prev[companyId] || {};
                    const mergedReminder = {
                        ...existingReminder,
                        ...updatedReminder,
                        // Ensure these fields are always present
                        id: updatedReminder?.id || existingReminder.id || reminderId,
                        company: companyId,
                        is_active: updatedReminder?.is_active !== undefined ? updatedReminder.is_active : true,
                        reminder_date: updatedReminder?.reminder_date || updateData.reminder_date,
                        note: updatedReminder?.note || updateData.note
                    };
                    console.log('Merged reminder:', mergedReminder);
                    return {
                        ...prev,
                        [companyId]: mergedReminder
                    };
                });
                setSelectedReminderNote(prev => ({
                    ...prev,
                    ...updatedReminder,
                    company: companyId,
                    id: updatedReminder?.id || prev?.id || reminderId
                }));
            }

            showToast('Reminder updated successfully', 'success');
            return true;
        } catch (error) {
            console.error('Error updating reminder:', error);
            showErrorToast('Failed to update reminder');
            return false;
        }
    }, [baseURL, authHeaders, selectedReminderNote]);

    // Handle deleting/deactivating reminder
    const handleDeactivateReminder = useCallback(async (reminderId, companyId) => {
        try {
            await apiInstance.post(
                `${baseURL}/stage-reminders/${reminderId}/deactivate/`,
                {},
                { headers: authHeaders }
            );

            // Close the dialog first
            setReminderNoteDialogOpen(false);
            setSelectedReminderNote(null);

            // Update companyReminders state immediately
            setCompanyReminders(prev => {
                const updated = { ...prev };
                delete updated[companyId];
                return updated;
            });
            setDueReminderCompanyIds(prev => prev.filter(id => id !== companyId));

            // Force tickets re-render by creating new object reference
            setTickets(prev => ({ ...prev }));

            showToast('Reminder deactivated', 'success');
        } catch (error) {
            console.error('Error deactivating reminder:', error);
            showErrorToast('Failed to deactivate reminder');
        }
    }, [baseURL, authHeaders]);

    // Handle reminder removal confirmation (when moving from 5% to another stage)
    const handleReminderRemovalConfirm = useCallback(async () => {
        if (!pendingReminderRemoval) return;

        const { draggedTicket, sourceStage, targetStage } = pendingReminderRemoval;
        const reminder = companyReminders[draggedTicket.id];

        try {
            // Deactivate the reminder first
            if (reminder?.id) {
                await apiInstance.post(
                    `${baseURL}/stage-reminders/${reminder.id}/deactivate/`,
                    {},
                    { headers: authHeaders }
                );
            }

            // Remove from local state
            setCompanyReminders(prev => {
                const updated = { ...prev };
                delete updated[draggedTicket.id];
                return updated;
            });
            setDueReminderCompanyIds(prev => prev.filter(id => id !== draggedTicket.id));

            // Now proceed with the stage change (same logic as normal drag)
            setTickets(prev => {
                const newTickets = { ...prev };
                newTickets[sourceStage.name] = newTickets[sourceStage.name].filter(
                    t => t.id !== draggedTicket.id
                );
                if (!newTickets[targetStage.name]) {
                    newTickets[targetStage.name] = [];
                }
                newTickets[targetStage.name] = [draggedTicket, ...newTickets[targetStage.name]];
                return newTickets;
            });

            // Update counts
            setSummaryData(prev => prev.map(stage => {
                if (stage.id === sourceStage.id) {
                    return { ...stage, count: Math.max(0, stage.count - 1) };
                }
                if (stage.id === targetStage.id) {
                    return { ...stage, count: stage.count + 1 };
                }
                return stage;
            }));

            // Update stage via API
            await updateCompanyStage(draggedTicket.id, targetStage.id);

            showToast(`Reminder removed. Company moved to ${targetStage.name}`, 'success');
        } catch (error) {
            console.error('Error removing reminder:', error);
            showErrorToast('Failed to remove reminder');
        } finally {
            setReminderRemovalConfirmOpen(false);
            setPendingReminderRemoval(null);
        }
    }, [pendingReminderRemoval, companyReminders, baseURL, authHeaders, updateCompanyStage]);

    // Handle reminder removal cancel
    const handleReminderRemovalCancel = useCallback(() => {
        setReminderRemovalConfirmOpen(false);
        setPendingReminderRemoval(null);
    }, []);

    // Fetch due reminders and all active reminders on mount
    useEffect(() => {
        fetchDueReminders();
        fetchAllActiveReminders();
    }, [fetchDueReminders, fetchAllActiveReminders]);

    // Fetch deal details when dialog opens
    useEffect(() => {
        const fetchDealDetails = async () => {
            if (dealDetailsDialogOpen && pendingDragData?.draggedTicket?.id) {
                try {
                    const response = await tasksApi.getClosedDealDetails(pendingDragData.draggedTicket.id);
                    if (response?.data?.success && response?.data?.data) {
                        setFetchedDealDetails(response.data.data);
                    } else if (response?.data) {
                        setFetchedDealDetails(response.data);
                    }
                } catch (error) {
                    console.error('Error fetching deal details:', error);
                    // If error (e.g. 404), we just start with empty form, which is fine
                }
            }
        };

        fetchDealDetails();
    }, [dealDetailsDialogOpen, pendingDragData]);

    // Filter team members based on stage and role - for 50% and 75% stages, filter by specific teams per role
    const getFilteredTeamMembersForRole = useCallback(async (stage, roleLabel, allTeamMembers) => {
        if (!stage || !roleLabel) return allTeamMembers || [];

        const stageNameLower = stage?.name?.toLowerCase() || stage?.label?.toLowerCase() || '';
        const is50PercentStage = stageNameLower.includes('nb loi + msa') || stageNameLower.includes('50%');
        const is75PercentStage = stageNameLower.includes('dd - phase') || stageNameLower.includes('75%');

        // Determine which team to filter by based on stage and role
        let targetTeamName = null;

        if (is50PercentStage && roleLabel === 'Legal Strategist') {
            targetTeamName = 'Team Comm/Legal DD';
        } else if (is75PercentStage) {
            if (roleLabel === 'Technical Leader') {
                targetTeamName = 'Team Tech DD';
            } else if (roleLabel === 'Commercial Leader') {
                targetTeamName = 'Team Comm/Legal DD';
            }
        }

        // If no filtering needed for this role/stage combination, return all team members
        if (!targetTeamName) {
            return allTeamMembers || [];
        }

        try {
            // Fetch teams to get team IDs for filtering
            const teamsResponse = await tasksApi.getTeams();
            const allTeams = teamsResponse?.data?.data || teamsResponse?.data || [];

            // Find the specific team we need
            const targetTeam = allTeams.find(team => team.name === targetTeamName);


            // Fetch team members
            const teamMembersResponse = await tasksApi.getTeamMembers();
            const allTeamMembers = teamMembersResponse?.data || [];

            if (!targetTeam) {
                return allTeamMembers || [];
            }
            // Get members from the target team
            const membersFromTeam = [];
            const memberIds = new Set();

            // Include team lead
            if (targetTeam.lead) {
                const leadId = targetTeam.lead.toString();
                if (!memberIds.has(leadId)) {
                    const leadMember = allTeamMembers.find(m =>
                        (m.pk || m.id)?.toString() === leadId
                    );
                    if (leadMember) {
                        membersFromTeam.push(leadMember);
                        memberIds.add(leadId);
                    }
                }
            }

            // Include team members
            if (targetTeam.team_members && Array.isArray(targetTeam.team_members)) {
                targetTeam.team_members.forEach(member => {
                    const userId = (member.user_id || member.user || member.pk)?.toString();
                    if (userId && !memberIds.has(userId)) {
                        const fullMember = allTeamMembers.find(m =>
                            (m.pk || m.id)?.toString() === userId ||
                            (m.user_id || m.user)?.toString() === userId
                        );
                        if (fullMember) {
                            membersFromTeam.push(fullMember);
                            memberIds.add(userId);
                        }
                    }
                });
            }

            return membersFromTeam.length > 0 ? membersFromTeam : allTeamMembers;
        } catch (error) {
            console.error('Error filtering team members:', error);
            return allTeamMembers || [];
        }
    }, []);

    // Handle task assignment complete (for 10% stage)
    const handleTaskAssignmentComplete = useCallback(async () => {
        if (!pendingDragData) return;

        const { draggedTicket, sourceStage, targetStage } = pendingDragData;

        try {
            // Move the company
            setTickets(prev => {
                const newTickets = { ...prev };

                // Remove from source stage
                if (newTickets[sourceStage.name]) {
                    newTickets[sourceStage.name] = newTickets[sourceStage.name].filter(
                        t => t.id !== draggedTicket.id
                    );
                }

                // Initialize target stage if doesn't exist
                if (!newTickets[targetStage.name]) {
                    newTickets[targetStage.name] = [];
                }

                // Remove from target stage if already exists (prevent duplicates)
                newTickets[targetStage.name] = newTickets[targetStage.name].filter(
                    t => t.id !== draggedTicket.id
                );

                // Add to target stage at the beginning
                newTickets[targetStage.name] = [draggedTicket, ...newTickets[targetStage.name]];

                return newTickets;
            });

            setSummaryData(prev => prev.map(stage => {
                if (stage.id === sourceStage.id) {
                    return { ...stage, count: Math.max(0, stage.count - 1) };
                }
                if (stage.id === targetStage.id) {
                    return { ...stage, count: stage.count + 1 };
                }
                return stage;
            }));

            await updateCompanyStage(draggedTicket.id, targetStage.id);

            showToast('Task assigned successfully! Company moved to ' + targetStage.name, 'success');
            setTaskAssignmentDrawerOpen(false);
            setPendingDragData(null);
            lastDragEndTimeRef.current = Date.now();

            // Show loading state for both stages during reload
            setLoadingMore(prev => ({
                ...prev,
                [sourceStage.name]: true,
                [targetStage.name]: true
            }));

            // Force reload both stages after cooldown to ensure backend sync
            setTimeout(() => {
                if (loadTicketsRef.current) {
                    loadTicketsRef.current(sourceStage.name, sourceStage.id, 1, false);
                    loadTicketsRef.current(targetStage.name, targetStage.id, 1, false);
                }
            }, 3100);
        } catch (error) {
            showErrorToast('Failed to assign task');
        }
    }, [pendingDragData, updateCompanyStage]);

    // Handle task assignment cancel
    const handleTaskAssignmentCancel = useCallback(() => {
        setTaskAssignmentDrawerOpen(false);
        setPendingDragData(null);
    }, []);

    // Handle team assignment complete
    const handleTeamAssignmentComplete = useCallback(async () => {
        if (!pendingDragData) return;

        const { draggedTicket, sourceStage, targetStage } = pendingDragData;

        try {
            console.log(`[Team Assignment] Moving company ${draggedTicket.company_name} from "${sourceStage.name}" to "${targetStage.name}"`);

            // Move the company
            setTickets(prev => {
                const newTickets = { ...prev };

                // Remove from source stage
                if (newTickets[sourceStage.name]) {
                    const beforeCount = newTickets[sourceStage.name].length;
                    newTickets[sourceStage.name] = newTickets[sourceStage.name].filter(
                        t => t.id !== draggedTicket.id
                    );
                    console.log(`[Team Assignment] Removed from ${sourceStage.name}: ${beforeCount} -> ${newTickets[sourceStage.name].length}`);
                }

                // Initialize target stage if doesn't exist
                if (!newTickets[targetStage.name]) {
                    newTickets[targetStage.name] = [];
                    console.log(`[Team Assignment] Initialized ${targetStage.name} stage`);
                }

                // Remove from target stage if already exists (prevent duplicates)
                newTickets[targetStage.name] = newTickets[targetStage.name].filter(
                    t => t.id !== draggedTicket.id
                );

                // Add to target stage at the beginning
                newTickets[targetStage.name] = [draggedTicket, ...newTickets[targetStage.name]];
                console.log(`[Team Assignment] Added to ${targetStage.name}: now ${newTickets[targetStage.name].length} companies`);

                return newTickets;
            });

            setSummaryData(prev => prev.map(stage => {
                if (stage.id === sourceStage.id) {
                    return { ...stage, count: Math.max(0, stage.count - 1) };
                }
                if (stage.id === targetStage.id) {
                    return { ...stage, count: stage.count + 1 };
                }
                return stage;
            }));

            await updateCompanyStage(draggedTicket.id, targetStage.id);

            showToast('Team assigned successfully! Company moved to ' + targetStage.name, 'success');
            setTeamAssignmentDrawerOpen(false);
            setPendingDragData(null);

            // Set timestamp BEFORE reload to avoid cooldown conflict
            lastDragEndTimeRef.current = Date.now() - 3500;

            // Reload both stages immediately to verify backend sync
            if (loadTicketsRef.current) {
                loadTicketsRef.current(sourceStage.name, sourceStage.id, 1, false);
                loadTicketsRef.current(targetStage.name, targetStage.id, 1, false);
            }
        } catch (error) {
            console.error('[Team Assignment] Error:', error);
            showErrorToast('Failed to assign team');
        }
    }, [pendingDragData, updateCompanyStage]);

    // Handle team assignment cancel
    const handleTeamAssignmentCancel = useCallback(() => {
        setTeamAssignmentDrawerOpen(false);
        setPendingDragData(null);
    }, []);

    // Optimized API call with abort controller
    const makeRequest = useCallback(async (url, key) => {
        if (abortControllersRef.current[key]) {
            abortControllersRef.current[key].abort();
        }

        const controller = new AbortController();
        abortControllersRef.current[key] = controller;

        try {
            const response = await apiInstance.get(url, {
                headers: authHeaders,
                signal: controller.signal
            });
            delete abortControllersRef.current[key];
            return response;
        } catch (error) {
            if (!error || error?.name === 'AbortError' || error?.name === 'CanceledError' || error?.__CANCEL__) {
                return null;
            }
            throw error;
        }
    }, [authHeaders]);

    const [teamData, setTeamData] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]); // Full user data for team assignment drawer
    const [selectedTeams, setSelectedTeams] = useState(savedFilters?.selectedTeams || []);
    const [selectedUser, setSelectedUser] = useState(savedFilters?.selectedUser || []);
    const [userRoles, setUserRoles] = useState([]); // For team assignment drawer

    // Fetch user roles for team assignment
    useEffect(() => {
        const fetchUserRoles = async () => {
            if (teamAssignmentDrawerOpen) {
                try {
                    const response = await apiInstance.get('user-role-type/', {
                        headers: {
                            "Authorization": `Token ${localStorage.getItem('authToken')}`,
                            'Content-Type': 'application/json'
                        }
                    });
                    setUserRoles(response.data?.data || []);
                } catch (error) {
                    console.error('Error fetching user roles:', error);
                    setUserRoles([]);
                }
            }
        };
        fetchUserRoles();
    }, [teamAssignmentDrawerOpen]);

    // Helper function to save filters to localStorage
    const saveFiltersToLocalStorage = useCallback((filters) => {
        try {
            localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
        } catch (error) {
            console.error('Failed to save filters to localStorage:', error);
            // Handle quota exceeded or other errors silently
        }
    }, []);

    // Save filters to localStorage whenever they change
    useEffect(() => {
        const filtersToSave = {
            selectedDateRange,
            filterParams,
            selectedCountries,
            selectedOfferings,
            selectedBusinessTypes,
            selectedPartners,
            introducedByPartnerOnly,
            searchTerm,
            selectedTeams,
            selectedUser,
            visibleColumns
        };
        saveFiltersToLocalStorage(filtersToSave);
    }, [selectedDateRange, filterParams, selectedCountries, selectedOfferings, selectedBusinessTypes, selectedPartners, introducedByPartnerOnly, searchTerm, selectedTeams, selectedUser, visibleColumns, saveFiltersToLocalStorage]);

    // Get filter data with Promise.all for parallel loading
    const getFilterData = useCallback(async () => {
        try {
            const [countriesRes, usersRes, teamsRes, offeringsRes, businessTypesRes, partnersRes] = await Promise.all([
                makeRequest(`${baseURL}/location?company_added=true`, 'countries'),
                makeRequest(`${baseURL}/users-viewset?short_details=true&dashboard_view=true`, 'users'),
                // makeRequest(`${baseURL}/industry?company_added=true`, 'industries'),
                makeRequest(`${baseURL}/teams/`, 'teams'),
                makeRequest(`${baseURL}/offering`, 'offerings'),
                makeRequest(`${baseURL}/business-type`, 'business_types'),
                makeRequest(`${baseURL}/partner`, 'partners')
            ]);

            if (countriesRes) {
                const countries = countriesRes.data?.data || [];
                const countryList = [
                    { country: 'All', total: 0, id: 'all' },
                    ...countries.map(c => ({ country: c.label, total: 0, id: c.id }))
                ];
                setStageViewCountries(countryList);

                // Auto-select country if only 1 country available (for team leads)
                const actualCountries = countries.filter(c => c.id !== 'all');
                if (actualCountries.length === 1 && selectedCountries.length === 0) {
                    setSelectedCountries([actualCountries[0].id]);
                }
            }

            if (offeringsRes) {
                const offerings = offeringsRes.data?.data || [];
                setStageViewOfferings(
                    offerings.map(o => ({ offering: o.label, id: o.id }))
                );
            }

            if (businessTypesRes) {
                const businessTypes = businessTypesRes.data?.data || [];
                setStageViewBusinessTypes(businessTypes.map(b => ({ business_type: b.label, id: b.id })));
            }

            if (partnersRes) {
                const partners = partnersRes.data?.data || [];
                setStagePartners(partners.map(p => ({ partner: `${p.first_name || ''} ${p.last_name || ''}`.trim(), id: p.id })));
            }

            if (usersRes) {
                const users = usersRes.data?.data || usersRes.data || [];
                // Set full user data for team assignment drawer
                setTeamMembers(users);
                // Set simplified user data for filters
                const userList = users.map(u => ({
                    id: u.pk,
                    name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
                    user: `${u.first_name || ''} ${u.last_name || ''}`.trim()
                }));

                // Check if GWX Agent exists in the list, if not fetch and add it
                const hasGWXAgent = userList.some(u => u.name === 'GWX Agent');
                if (!hasGWXAgent) {
                    try {
                        // Fetch GWX Agent user separately
                        const gwxRes = await makeRequest(`${baseURL}/users-viewset?search=GWX Agent&short_details=true`, 'gwx_agent');
                        const gwxUsers = gwxRes?.data?.data || gwxRes?.data || [];
                        const gwxAgentUser = gwxUsers.find(u => u.first_name === 'GWX' && u.last_name === 'Agent');
                        if (gwxAgentUser) {
                            userList.unshift({
                                id: gwxAgentUser.pk,
                                name: 'GWX Agent',
                                user: 'GWX Agent'
                            });
                        }
                    } catch (err) {
                        console.error('Failed to fetch GWX Agent:', err);
                    }
                }

                setUserData(userList);
            }

            // Commented as this data is not used anywhere to avoid extra api call
            // if (industriesRes) {
            //     const industries = industriesRes.data?.data || [];
            //     setIndustryData(
            //         industries
            //             .filter(i => i.is_active)
            //             .map(i => ({ id: i.id, industry: i.label }))
            //     );
            // }

            if (teamsRes) {
                const teams = teamsRes.data?.data || teamsRes.data || [];
                const teamList = teams.map(t => ({
                    id: t.id,
                    name: t.name
                }));
                setTeamData(teamList);

                // Auto-select team if only 1 team available (for team leads)
                if (teamList.length === 1 && selectedTeams.length === 0) {
                    setSelectedTeams([teamList[0].id]);
                }
            }
        } catch (error) {
            console.error('Failed to load filter data:', error);
        }
    }, [baseURL, makeRequest]);

    // Build query params helper
    const buildQueryParams = useCallback((additionalParams = {}) => {
        const params = { ...additionalParams };

        if (selectedCountries?.length > 0) {
            params.country_id = selectedCountries.join(',');
        }

        if (selectedOfferings?.length > 0) {
            params.offering_id = selectedOfferings.join(',');
        }

        if (selectedBusinessTypes?.length > 0) {
            params.business_type_id = selectedBusinessTypes.join(',');
        }

        if (selectedPartners?.length > 0) {
            params.partner_id = selectedPartners.join(',');
        }

        if (introducedByPartnerOnly) {
            params.introduced_by_partner = 'true';
        }

        if (filterParams?.created_by?.length) {
            params.created_by = filterParams.created_by.join(',');
        }

        if (filterParams?.industry?.length) {
            params.industry = filterParams.industry.join(',');
        }

        if (selectedTeams?.length) {
            params.team_id = selectedTeams.join(',');
        }

        if (selectedUser?.length) {
            params.user_id = selectedUser.join(',');
        }

        if (selectedDateRange?.length) {
            const [start, end, type] = selectedDateRange;
            if (start) params.start_date = start;
            if (end) params.end_date = end;
            if (type) params.date_filter_type = type;
        }

        if (debouncedSearchTerm) {
            params.search = debouncedSearchTerm;
        }

        return new URLSearchParams(params).toString();
    }, [selectedCountries, selectedOfferings, selectedBusinessTypes, selectedPartners, introducedByPartnerOnly, filterParams, selectedDateRange, debouncedSearchTerm, stageViewCountries, selectedTeams, selectedUser]);

    // Get country counts - single batch API call instead of per-country
    const getCountryCounts = useCallback(async () => {
        // STOP this api call as the data is not used anymore
        // To prevent unnecessary call and optimize dashboard loading experience
        return;
    }, [stageViewCountries, viewType, baseURL, makeRequest, buildQueryParams]);

    // Get dashboard summary
    const getDashboardSummary = useCallback(async () => {
        try {
            const params = buildQueryParams({ view_type: viewType });
            const url = `${baseURL}/company/dashboard-totals/?${params}`;

            const response = await makeRequest(url, 'dashboard-summary');
            if (!response) return;

            const columns = response.data?.data?.columns || [];
            // Cheap gate: drop the 90% "Closing" stage so it never becomes a column
            // (no DB change — the stage still exists server-side).
            const validColumns = columns.filter(col => col.name && col.id && !isHiddenStage(col.name));

            const summary = validColumns.map(col => ({
                id: col.id,
                name: col.name,
                count: col.total_count || 0
            }));

            setSummaryData(summary);
            setGlobalTotal([
                response.data?.filtered_counts || 0,
                response.data?.total_counts || 0
            ]);

            // Initialize visible columns if not set
            if (visibleColumns.length === 0) {
                setVisibleColumns(summary.map(item => item.name));
            }

            // Initialize ticket states
            const initialState = {};
            summary.forEach(item => {
                initialState[item.name] = {
                    tickets: [],
                    page: 1,
                    hasMore: true,
                    loading: false
                };
            });

            setTickets(prev => {
                const updated = {};
                summary.forEach(item => updated[item.name] = []);
                return updated;
            });
            setCurrentPages(prev => {
                const updated = {};
                summary.forEach(item => updated[item.name] = 1);
                return updated;
            });
            setHasMore(prev => {
                const updated = {};
                summary.forEach(item => updated[item.name] = true);
                return updated;
            });
            setLoadingMore(prev => {
                const updated = {};
                summary.forEach(item => updated[item.name] = false);
                return updated;
            });

        } catch (error) {
            console.error('Dashboard summary error:', error);
        }
    }, [viewType, baseURL, buildQueryParams, makeRequest]);

    // Get user-specific counts
    const getUserSpecificCounts = useCallback(async () => {
        if (!selectedCreatedBy) {
            setUserSpecificCounts({});
            return;
        }

        try {
            const params = buildQueryParams({
                user_id: selectedCreatedBy,
                view_type: viewType
            });
            const url = `${baseURL}/company/user-stage-counts/?${params}`;

            const response = await makeRequest(url, 'user-counts');
            if (!response) return;

            const countsData = response.data?.data?.counts || {};
            const counts = {};
            Object.values(countsData).forEach(item => {
                counts[item.name] = item.count;
            });

            setUserSpecificCounts(counts);
        } catch (error) {
            setUserSpecificCounts({});
        }
    }, [selectedCreatedBy, viewType, baseURL, buildQueryParams, makeRequest]);

    // Load tickets with optimization
    const loadTickets = useCallback(async (stageName, stageId, page = 1, append = false) => {
        // Check if we're in drag cooldown (3 seconds after drag)
        const dragCooldown = (Date.now() - lastDragEndTimeRef.current) < 3000;

        if (!stageName || stageName === 'Unknown' || !stageId || loadingMore[stageName] || dragCooldown) {
            return;
        }

        setLoadingMore(prev => ({ ...prev, [stageName]: true }));

        try {
            const additionalParams = {
                page,
                page_size: 10,
                view_type: viewType
            };

            // Add stage filter
            additionalParams.stage_id = stageId;

            // Add outreach filter for 0% stage (exact match, not 10%, 50%, etc.)
            // Skip outreach filter when searching - show all companies
            const isZeroPercentStage = stageName && (
                stageName.toLowerCase().includes('- 0%') ||
                stageName.toLowerCase().includes('outreach - 0') ||
                stageName.toLowerCase() === 'outreach - 0%'
            );
            if (isZeroPercentStage && outreachFilter && !debouncedSearchTerm) {
                additionalParams.outreach_status = outreachFilter;
            }

            // Add invalid filter for 5% stage
            const isFivePercentStage = stageName && (
                stageName.toLowerCase().includes('- 5%') ||
                stageName.toLowerCase().includes('future-outreach - 5')
            );
            if (isFivePercentStage && invalidFilter && !debouncedSearchTerm) {
                additionalParams.invalid_status = invalidFilter;
            }

            // Add newsletter filter for 5% stage
            if (isFivePercentStage && newsletterFilter && !debouncedSearchTerm) {
                additionalParams.newsletter_status = newsletterFilter;
            }

            // Add archive filter for 100% stage
            const isHundredPercentStage = stageName && (
                stageName.toLowerCase().includes('- 100%') ||
                stageName.toLowerCase().includes('closed')
            );
            if (isHundredPercentStage && archiveFilter && !debouncedSearchTerm) {
                additionalParams.archive_status = archiveFilter;
            }

            const params = buildQueryParams(additionalParams);
            const url = `${baseURL}/company/dashboard-tickets/?${params}`;

            const response = await makeRequest(url, `tickets-${stageName}-${page}`);
            if (!response) return;

            const allTickets = response.data?.data?.tickets || [];
            const activeTickets = allTickets.filter(ticket => ticket.is_active !== false);
            const seenIds = new Set();
            const newTickets = activeTickets.filter(ticket => {
                if (seenIds.has(ticket.id)) return false;
                seenIds.add(ticket.id);
                return true;
            });

            const pagination = response.data?.data?.pagination || {};

            setTickets(prev => ({
                ...prev,
                [stageName]: append ? [...(prev[stageName] || []), ...newTickets] : newTickets
            }));

            if (pagination.total_count !== undefined) {
                setTotalCounts(prev => ({
                    ...prev,
                    [stageName]: pagination.total_count
                }));
            }

            setHasMore(prev => ({
                ...prev,
                [stageName]: pagination.has_next || false
            }));

            setCurrentPages(prev => ({
                ...prev,
                [stageName]: pagination.current_page || page
            }));

        } catch (error) {
            if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
                console.error('Load tickets error:', error);
            }
        } finally {
            setLoadingMore(prev => ({ ...prev, [stageName]: false }));
        }
    }, [viewType, baseURL, buildQueryParams, makeRequest, loadingMore, outreachFilter, invalidFilter, newsletterFilter, archiveFilter]);

    // Store loadTickets in ref so it can be called from handlers without dependency issues
    useEffect(() => {
        loadTicketsRef.current = loadTickets;
    }, [loadTickets]);

    // Optimized scroll handler with throttling
    const handleScroll = useCallback((stageName, stageId) => {
        const scrollContainer = scrollRefs.current[stageName];
        if (!scrollContainer || loadingMore[stageName] || !hasMore[stageName]) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
        const threshold = 100;

        if (scrollHeight - scrollTop - clientHeight < threshold) {
            const nextPage = (currentPages[stageName] || 1) + 1;
            loadTickets(stageName, stageId, nextPage, true);
        }
    }, [loadingMore, hasMore, currentPages, loadTickets]);

    // Handle outreach filter change - reload 0% stage tickets
    const handleOutreachFilterChange = useCallback(async (newFilter) => {
        setOutreachFilter(newFilter);
        // Find the 0% stage and reload its tickets (exact match, not 10%, 50%, etc.)
        const zeroPercentStage = summaryData.find(item => item.name && (
            item.name.toLowerCase().includes('- 0%') ||
            item.name.toLowerCase().includes('outreach - 0') ||
            item.name.toLowerCase() === 'outreach - 0%'
        ));
        if (zeroPercentStage) {
            const stageName = zeroPercentStage.name;

            // Clear existing tickets for this stage
            setTickets(prev => ({ ...prev, [stageName]: [] }));
            setCurrentPages(prev => ({ ...prev, [stageName]: 1 }));
            setHasMore(prev => ({ ...prev, [stageName]: true }));

            // Directly fetch with new filter (don't rely on state)
            try {
                const additionalParams = {
                    page: 1,
                    page_size: 10,
                    view_type: viewType,
                    stage_id: zeroPercentStage.id,
                    outreach_status: newFilter // Use the new filter directly
                };
                const params = buildQueryParams(additionalParams);
                const url = `${baseURL}/company/dashboard-tickets/?${params}`;

                const response = await apiInstance.get(url, { headers: authHeaders });
                if (response?.data?.data?.tickets) {
                    const allTickets = response.data.data.tickets.filter(t => t.is_active !== false);
                    setTickets(prev => ({ ...prev, [stageName]: allTickets }));

                    // Update pagination state
                    if (response.data?.data?.pagination?.total_count !== undefined) {
                        setTotalCounts(prev => ({
                            ...prev,
                            [stageName]: response.data.data.pagination.total_count
                        }));
                    }
                    setHasMore(prev => ({
                        ...prev,
                        [stageName]: response.data.data.pagination?.has_next || false
                    }));
                    setCurrentPages(prev => ({ ...prev, [stageName]: 1 }));
                }
            } catch (error) {
                console.error('Error reloading tickets:', error);
            }
        }
    }, [summaryData, viewType, baseURL, authHeaders, buildQueryParams]);

    // Handle marking a company as outreached
    const handleMarkOutreached = useCallback((ticket) => {
        setSelectedOutreachCompany(ticket);
        setOutreachComment('');
        setOutreachMethod('email');
        // Set default date/time to current
        const now = new Date();
        setOutreachDate(now.toISOString().split('T')[0]); // YYYY-MM-DD
        setOutreachTime(now.toTimeString().slice(0, 5)); // HH:MM
        setMarkOutreachDialogOpen(true);
    }, []);

    // Submit mark as outreached
    const submitMarkOutreached = useCallback(async () => {
        if (!selectedOutreachCompany || !outreachComment.trim()) {
            showErrorToast('Comment is required');
            return;
        }

        if (!outreachDate || !outreachTime) {
            showErrorToast('Date and time are required');
            return;
        }

        setIsMarkingOutreach(true);
        try {
            // Combine date and time into ISO format
            const outreachedAt = new Date(`${outreachDate}T${outreachTime}:00`).toISOString();

            const response = await apiInstance.post(
                `${baseURL}/company/${selectedOutreachCompany.id}/mark-outreached/`,
                {
                    comment: outreachComment.trim(),
                    outreach_method: outreachMethod,
                    outreached_at: outreachedAt
                },
                { headers: authHeaders }
            );

            if (response.data.success) {
                showToast('Company marked as outreached successfully');
                setMarkOutreachDialogOpen(false);

                // Update the ticket in state to reflect the change (exact 0% match)
                const stageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 0%') ||
                    item.name.toLowerCase().includes('outreach - 0') ||
                    item.name.toLowerCase() === 'outreach - 0%'
                ))?.name;
                if (stageName) {
                    setTickets(prev => ({
                        ...prev,
                        [stageName]: (prev[stageName] || []).map(t =>
                            t.id === selectedOutreachCompany.id
                                ? { ...t, is_outreached: true, outreach_details: response.data.data }
                                : t
                        )
                    }));
                }
            }
        } catch (error) {
            console.error('Mark outreach error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to mark as outreached');
        } finally {
            setIsMarkingOutreach(false);
        }
    }, [selectedOutreachCompany, outreachComment, outreachMethod, outreachDate, outreachTime, baseURL, authHeaders, summaryData]);

    // Fetch invalid reasons from API
    const fetchInvalidReasons = useCallback(async () => {
        try {
            const response = await apiInstance.get(
                `${baseURL}/company/invalid-reasons/`,
                { headers: authHeaders }
            );
            console.log('Invalid reasons response:', response.data);
            if (response.data.success) {
                setInvalidReasons(response.data.data || []);
            } else if (response.data && Array.isArray(response.data)) {
                // Handle case where API returns array directly
                setInvalidReasons(response.data);
            }
        } catch (error) {
            console.error('Failed to fetch invalid reasons:', error);
            showErrorToast('Failed to load invalid reasons');
        }
    }, [baseURL, authHeaders]);

    // Handle marking a company as invalid
    const handleMarkInvalid = useCallback(async (ticket) => {
        setSelectedInvalidCompany(ticket);
        setSelectedInvalidReason('');
        setInvalidDescription('');
        await fetchInvalidReasons();
        setMarkInvalidDialogOpen(true);
    }, [fetchInvalidReasons]);

    // Submit mark as invalid
    const submitMarkInvalid = useCallback(async () => {
        if (!selectedInvalidCompany || !selectedInvalidReason) {
            showErrorToast('Please select a reason');
            return;
        }

        setIsMarkingInvalid(true);
        try {
            const response = await apiInstance.post(
                `${baseURL}/company/${selectedInvalidCompany.id}/mark-invalid/`,
                {
                    reason_id: selectedInvalidReason,
                    description: invalidDescription.trim() || null
                },
                { headers: authHeaders }
            );

            if (response.data.success) {
                showToast('Company marked as invalid and moved to 5% stage');
                setMarkInvalidDialogOpen(false);

                // Remove the ticket from 0% stage (it moves to 5%)
                const zeroStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 0%') ||
                    item.name.toLowerCase().includes('outreach - 0') ||
                    item.name.toLowerCase() === 'outreach - 0%'
                ))?.name;
                if (zeroStageName) {
                    setTickets(prev => ({
                        ...prev,
                        [zeroStageName]: (prev[zeroStageName] || []).filter(t => t.id !== selectedInvalidCompany.id)
                    }));
                }

                // Reload 5% stage tickets
                const fiveStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 5%') ||
                    item.name.toLowerCase().includes('future-outreach - 5')
                ))?.name;
                const fiveStageId = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 5%') ||
                    item.name.toLowerCase().includes('future-outreach - 5')
                ))?.id;
                if (fiveStageName && fiveStageId) {
                    loadTickets(fiveStageName, fiveStageId, 1, false);
                }
            }
        } catch (error) {
            console.error('Mark invalid error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to mark as invalid');
        } finally {
            setIsMarkingInvalid(false);
        }
    }, [selectedInvalidCompany, selectedInvalidReason, invalidDescription, baseURL, authHeaders, summaryData, loadTickets]);

    // Handle viewing invalid details
    const handleViewInvalidDetails = useCallback((ticket) => {
        setViewingInvalidCompany(ticket);
        setInvalidDetailsDrawerOpen(true);
    }, []);

    // Handle removing invalid mark
    const handleRemoveInvalid = useCallback(async () => {
        if (!viewingInvalidCompany) return;

        setIsRemovingInvalid(true);
        try {
            const response = await apiInstance.delete(
                `${baseURL}/company/${viewingInvalidCompany.id}/remove-invalid/`,
                { headers: authHeaders }
            );

            if (response.data.success) {
                showToast('Invalid mark removed, company moved back to 0% stage');
                setInvalidDetailsDrawerOpen(false);

                // Remove ticket from 5% stage
                const fiveStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 5%') ||
                    item.name.toLowerCase().includes('future-outreach - 5')
                ))?.name;
                if (fiveStageName) {
                    setTickets(prev => ({
                        ...prev,
                        [fiveStageName]: (prev[fiveStageName] || []).filter(t => t.id !== viewingInvalidCompany.id)
                    }));
                }

                // Reload 0% stage tickets
                const zeroStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 0%') ||
                    item.name.toLowerCase().includes('outreach - 0') ||
                    item.name.toLowerCase() === 'outreach - 0%'
                ))?.name;
                const zeroStageId = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 0%') ||
                    item.name.toLowerCase().includes('outreach - 0') ||
                    item.name.toLowerCase() === 'outreach - 0%'
                ))?.id;
                if (zeroStageName && zeroStageId) {
                    loadTickets(zeroStageName, zeroStageId, 1, false);
                }
            }
        } catch (error) {
            console.error('Remove invalid error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to remove invalid mark');
        } finally {
            setIsRemovingInvalid(false);
        }
    }, [viewingInvalidCompany, baseURL, authHeaders, summaryData, loadTickets]);

    // ---- DND (Do-Not-Disturb) ------------------------------------------------
    // DND never moves a company between stages (spec FR-010), so instead of a full
    // stage reload we patch the affected ticket in place across every stage column.
    const applyDndToTicket = useCallback((ticketId, dndDetails) => {
        setTickets(prev => {
            const next = {};
            Object.keys(prev).forEach(stage => {
                next[stage] = (prev[stage] || []).map(t =>
                    t.id === ticketId
                        ? { ...t, is_dnd: !!dndDetails, dnd_details: dndDetails || null }
                        : t
                );
            });
            return next;
        });
    }, []);

    // Open the shared "Add to DND" dialog for a card.
    const handleAddDnd = useCallback((ticket) => {
        setAddDndCompany({ id: ticket.id, company_name: ticket.company_name });
        setAddDndDialogOpen(true);
    }, []);

    // After a successful mark, fetch the fresh record so the badge/popover has full detail.
    const handleDndAdded = useCallback(async () => {
        const companyId = addDndCompany?.id;
        if (!companyId) return;
        try {
            const res = await getDndDetails(companyId);
            applyDndToTicket(companyId, res?.data?.data || null);
        } catch (error) {
            // Non-fatal: at minimum reflect the flag so the badge appears.
            applyDndToTicket(companyId, { reason_display: '—' });
        }
    }, [addDndCompany, applyDndToTicket]);

    // Remove a company from DND directly from the card popover.
    const handleRemoveDnd = useCallback(async (ticket) => {
        if (!ticket?.id) return;
        try {
            const response = await removeDnd(ticket.id);
            showToast(response?.data?.message || 'Company removed from DND list');
            applyDndToTicket(ticket.id, null);
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Failed to remove company from DND list');
        }
    }, [applyDndToTicket]);

    // Handle viewing newsletter details
    const handleViewNewsletterDetails = useCallback((ticket) => {
        setViewingNewsletterCompany(ticket);
        setNewsletterDetailsDrawerOpen(true);
    }, []);

    // Handle removing newsletter mark
    const handleRemoveNewsletter = useCallback(async () => {
        if (!viewingNewsletterCompany) return;

        setIsRemovingNewsletter(true);
        try {
            const response = await apiInstance.delete(
                `${baseURL}/company/${viewingNewsletterCompany.id}/remove-newsletter/`,
                { headers: authHeaders }
            );

            if (response.data.success) {
                showToast('Newsletter mark removed, company moved back to previous stage');
                setNewsletterDetailsDrawerOpen(false);

                // Remove ticket from 5% stage
                const fiveStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 5%') ||
                    item.name.toLowerCase().includes('future-outreach - 5')
                ))?.name;
                if (fiveStageName) {
                    setTickets(prev => ({
                        ...prev,
                        [fiveStageName]: (prev[fiveStageName] || []).filter(t => t.id !== viewingNewsletterCompany.id)
                    }));
                }

                // Reload 0% stage tickets (or previous stage if available)
                const zeroStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 0%') ||
                    item.name.toLowerCase().includes('outreach - 0') ||
                    item.name.toLowerCase() === 'outreach - 0%'
                ))?.name;
                const zeroStageId = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 0%') ||
                    item.name.toLowerCase().includes('outreach - 0') ||
                    item.name.toLowerCase() === 'outreach - 0%'
                ))?.id;
                if (zeroStageName && zeroStageId) {
                    loadTickets(zeroStageName, zeroStageId, 1, false);
                }
            }
        } catch (error) {
            console.error('Remove newsletter error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to remove newsletter mark');
        } finally {
            setIsRemovingNewsletter(false);
        }
    }, [viewingNewsletterCompany, baseURL, authHeaders, summaryData, loadTickets]);

    // Handle invalid filter change - reload 5% stage tickets
    const handleInvalidFilterChange = useCallback(async (newFilter) => {
        setInvalidFilter(newFilter);
        // Find the 5% stage and reload its tickets
        const fivePercentStage = summaryData.find(item => item.name && (
            item.name.toLowerCase().includes('- 5%') ||
            item.name.toLowerCase().includes('future-outreach - 5')
        ));

        if (fivePercentStage) {
            setTickets(prev => ({ ...prev, [fivePercentStage.name]: [] }));
            setCurrentPages(prev => ({ ...prev, [fivePercentStage.name]: 1 }));
            setHasMore(prev => ({ ...prev, [fivePercentStage.name]: true }));

            try {
                const additionalParams = {
                    page: 1,
                    page_size: 10,
                    view_type: viewType,
                    stage_id: fivePercentStage.id,
                    invalid_status: newFilter
                };

                const params = buildQueryParams(additionalParams);
                const url = `${baseURL}/company/dashboard-tickets/?${params}`;

                const response = await apiInstance.get(url, { headers: authHeaders });
                if (response.data?.data?.tickets) {
                    setTickets(prev => ({
                        ...prev,
                        [fivePercentStage.name]: response.data.data.tickets
                    }));
                    // Update the count to reflect filtered results
                    if (response.data?.data?.pagination?.total_count !== undefined) {
                        setTotalCounts(prev => ({
                            ...prev,
                            [fivePercentStage.name]: response.data.data.pagination.total_count
                        }));
                    }
                    setHasMore(prev => ({
                        ...prev,
                        [fivePercentStage.name]: response.data.data.pagination?.has_next || false
                    }));
                }
            } catch (error) {
                console.error('Error reloading tickets:', error);
            }
        }
    }, [summaryData, viewType, baseURL, authHeaders, buildQueryParams]);

    // Handle newsletter filter change - reload 5% stage tickets
    const handleNewsletterFilterChange = useCallback(async (newFilter) => {
        console.log('📧 Newsletter filter changed to:', newFilter);
        setNewsletterFilter(newFilter);
        // Find the 5% stage and reload its tickets
        const fivePercentStage = summaryData.find(item => item.name && (
            item.name.toLowerCase().includes('- 5%') ||
            item.name.toLowerCase().includes('future-outreach - 5')
        ));

        if (fivePercentStage) {
            console.log('📧 Reloading 5% stage with newsletter filter:', newFilter);
            setTickets(prev => ({ ...prev, [fivePercentStage.name]: [] }));
            setCurrentPages(prev => ({ ...prev, [fivePercentStage.name]: 1 }));
            setHasMore(prev => ({ ...prev, [fivePercentStage.name]: true }));

            try {
                const additionalParams = {
                    page: 1,
                    page_size: 10,
                    view_type: viewType,
                    stage_id: fivePercentStage.id,
                    newsletter_status: newFilter
                };

                const params = buildQueryParams(additionalParams);
                const url = `${baseURL}/company/dashboard-tickets/?${params}`;
                console.log('📧 API URL:', url);

                const response = await apiInstance.get(url, { headers: authHeaders });
                console.log('📧 API Response:', response.data);
                if (response.data?.data?.tickets) {
                    setTickets(prev => ({
                        ...prev,
                        [fivePercentStage.name]: response.data.data.tickets
                    }));
                    if (response.data?.data?.pagination?.total_count !== undefined) {
                        console.log('📧 Total count:', response.data.data.pagination.total_count);
                        setTotalCounts(prev => ({
                            ...prev,
                            [fivePercentStage.name]: response.data.data.pagination.total_count
                        }));
                    }
                    setHasMore(prev => ({
                        ...prev,
                        [fivePercentStage.name]: response.data.data.pagination?.has_next || false
                    }));
                }
            } catch (error) {
                console.error('Error reloading tickets:', error);
            }
        }
    }, [summaryData, viewType, baseURL, authHeaders, buildQueryParams]);

    // Handle archive filter change - reload 100% stage tickets (super admin only)
    const handleArchiveFilterChange = useCallback(async (newFilter) => {
        setArchiveFilter(newFilter);
        // Find the 100% stage and reload its tickets
        const hundredPercentStage = summaryData.find(item => item.name && (
            item.name.toLowerCase().includes('- 100%') ||
            item.name.toLowerCase().includes('closed')
        ));

        if (hundredPercentStage) {
            setTickets(prev => ({ ...prev, [hundredPercentStage.name]: [] }));
            setCurrentPages(prev => ({ ...prev, [hundredPercentStage.name]: 1 }));
            setHasMore(prev => ({ ...prev, [hundredPercentStage.name]: true }));

            try {
                const additionalParams = {
                    page: 1,
                    page_size: 10,
                    view_type: viewType,
                    stage_id: hundredPercentStage.id,
                    archive_status: newFilter
                };

                const params = buildQueryParams(additionalParams);
                const url = `${baseURL}/company/dashboard-tickets/?${params}`;

                const response = await apiInstance.get(url, { headers: authHeaders });
                if (response.data?.data?.tickets) {
                    setTickets(prev => ({
                        ...prev,
                        [hundredPercentStage.name]: response.data.data.tickets
                    }));
                    if (response.data?.data?.pagination?.total_count !== undefined) {
                        setTotalCounts(prev => ({
                            ...prev,
                            [hundredPercentStage.name]: response.data.data.pagination.total_count
                        }));
                    }
                    setHasMore(prev => ({
                        ...prev,
                        [hundredPercentStage.name]: response.data.data.pagination?.has_next || false
                    }));
                }
            } catch (error) {
                console.error('Error reloading tickets:', error);
            }
        }
    }, [summaryData, viewType, baseURL, authHeaders, buildQueryParams]);

    // Handle archive company
    const handleArchiveCompany = useCallback(async () => {
        if (!companyToArchive) return;

        setIsArchiving(true);
        try {
            const response = await apiInstance.post(
                `${baseURL}/company/${companyToArchive.id}/archive/`,
                { note: archiveNote },
                { headers: authHeaders }
            );

            if (response.data.success) {
                showToast('Company archived successfully');
                setArchiveDialogOpen(false);
                setArchiveNote('');
                setCompanyToArchive(null);

                // Update ticket in local state to show archived
                const hundredStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 100%') ||
                    item.name.toLowerCase().includes('closed')
                ))?.name;

                if (hundredStageName) {
                    // If showing non-archived (default), remove the ticket from list (since it's now archived)
                    // If showing archived, update the ticket to show as archived
                    if (archiveFilter !== 'archived') {
                        setTickets(prev => ({
                            ...prev,
                            [hundredStageName]: (prev[hundredStageName] || []).filter(t =>
                                t.id !== companyToArchive.id
                            )
                        }));
                        // Update count
                        setTotalCounts(prev => ({
                            ...prev,
                            [hundredStageName]: Math.max((prev[hundredStageName] || 1) - 1, 0)
                        }));
                    } else {
                        setTickets(prev => ({
                            ...prev,
                            [hundredStageName]: (prev[hundredStageName] || []).map(t =>
                                t.id === companyToArchive.id
                                    ? { ...t, is_archived: true, archive_details: response.data.data }
                                    : t
                            )
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('Archive company error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to archive company');
        } finally {
            setIsArchiving(false);
        }
    }, [companyToArchive, archiveNote, baseURL, authHeaders, summaryData, archiveFilter]);

    // Handle view archive details
    const handleViewArchiveDetails = useCallback((ticket) => {
        setViewingArchivedCompany(ticket);
        setArchiveDetailsDrawerOpen(true);
    }, []);

    // Handle unarchive company
    const handleUnarchiveCompany = useCallback(async () => {
        if (!viewingArchivedCompany) return;

        setIsUnarchiving(true);
        try {
            const response = await apiInstance.delete(
                `${baseURL}/company/${viewingArchivedCompany.id}/unarchive/`,
                { headers: authHeaders }
            );

            if (response.data.success) {
                showToast('Company unarchived successfully');
                setArchiveDetailsDrawerOpen(false);

                // Update ticket in local state to remove archived status
                const hundredStageName = summaryData.find(item => item.name && (
                    item.name.toLowerCase().includes('- 100%') ||
                    item.name.toLowerCase().includes('closed')
                ))?.name;

                if (hundredStageName) {
                    // If showing archived, remove the ticket from list (since it's no longer archived)
                    // If showing non-archived, update the ticket to show as non-archived
                    if (archiveFilter === 'archived') {
                        setTickets(prev => ({
                            ...prev,
                            [hundredStageName]: (prev[hundredStageName] || []).filter(t =>
                                t.id !== viewingArchivedCompany.id
                            )
                        }));
                        // Update count
                        setTotalCounts(prev => ({
                            ...prev,
                            [hundredStageName]: Math.max((prev[hundredStageName] || 1) - 1, 0)
                        }));
                    } else {
                        setTickets(prev => ({
                            ...prev,
                            [hundredStageName]: (prev[hundredStageName] || []).map(t =>
                                t.id === viewingArchivedCompany.id
                                    ? { ...t, is_archived: false, archive_details: null }
                                    : t
                            )
                        }));
                    }
                }
            }
        } catch (error) {
            console.error('Unarchive company error:', error);
            showErrorToast(error.response?.data?.message || 'Failed to unarchive company');
        } finally {
            setIsUnarchiving(false);
        }
    }, [viewingArchivedCompany, baseURL, authHeaders, summaryData, archiveFilter]);

    // Batch load all tickets after summary data is ready
    const loadAllTickets = useCallback(async () => {
        // Check if we're in drag cooldown
        const dragCooldown = (Date.now() - lastDragEndTimeRef.current) < 3000;

        if (summaryData.length === 0 || dragCooldown) {
            return;
        }

        setIsLoading(true);

        try {
            // Only load tickets for visible columns to reduce API calls
            const columnsToLoad = visibleColumns.length > 0
                ? summaryData.filter(item => visibleColumns.includes(item.name))
                : summaryData;

            const loadPromises = columnsToLoad.map(item =>
                loadTickets(item.name, item.id, 1, false).catch(err => {
                    console.error(`Failed to load tickets for ${item.name}:`, err);
                })
            );

            await Promise.all(loadPromises);
        } catch (error) {
            console.error('Load all tickets error:', error);
        } finally {
            setIsLoading(false);
        }
    }, [summaryData, loadTickets, visibleColumns]);

    // Initialize
    useEffect(() => {
        getFilterData();
    }, [getFilterData]);

    // Load stage percentage map (B1 — T9): used by isGatedStage to decide whether
    // a drag into a stage must capture financials first. Fails quietly — an empty
    // map just falls back to the stage-name/label regex in isGatedStage.
    useEffect(() => {
        tasksApi.getStages()
            .then((response) => {
                setStagePercentMap(buildStagePercentMap(response.data));
            })
            .catch(() => {
                // Silent fail - isGatedStage falls back to label regex when the map is empty
            });
    }, []);

    // Reset and reload when major filters change
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        abortPendingRequests();
        setTickets({});
        setCurrentPages({});
        setHasMore({});
        setLoadingMore({});
        setTotalCounts({});
        setUserSpecificCounts({});
        setIsLoading(true);
    }, [viewType, selectedCountries, selectedTeams, selectedUser, selectedBusinessTypes, selectedPartners, introducedByPartnerOnly, filterParams, selectedDateRange, abortPendingRequests]);

    // Load summary data
    useEffect(() => {
        // Check if we're in drag cooldown
        const dragCooldown = (Date.now() - lastDragEndTimeRef.current) < 3000;

        if (stageViewCountries.length > 0 && !dragCooldown) {
            getDashboardSummary();
        }
    }, [getDashboardSummary, stageViewCountries]);

    // Load tickets after summary is ready
    useEffect(() => {
        loadAllTickets();
    }, [summaryData]);

    // Reload tickets when search term changes (debounced)
    // Using a ref to track if this is a search-triggered reload to avoid double-loading
    const prevDebouncedSearchTermRef = useRef(debouncedSearchTerm);

    useEffect(() => {
        // Skip if this is not a search term change (e.g., if loadAllTickets changed for other reasons)
        const searchTermChanged = prevDebouncedSearchTermRef.current !== debouncedSearchTerm;
        prevDebouncedSearchTermRef.current = debouncedSearchTerm;

        if (!searchTermChanged) return;
        if (summaryData.length === 0 || isInitialMount.current) return;

        abortPendingRequests();
        setTickets({});
        setCurrentPages({});
        setHasMore({});
        setLoadingMore({});

        loadAllTickets();
    }, [debouncedSearchTerm, loadAllTickets, abortPendingRequests, summaryData.length]);

    // Auto-load more data if columns don't fill the scrollable area (for large screens)
    // Only runs once after initial loading completes, not on every ticket change
    const autoLoadCheckedRef = useRef(false);
    useEffect(() => {
        if (isLoading || summaryData.length === 0) {
            autoLoadCheckedRef.current = false;
            return;
        }
        if (autoLoadCheckedRef.current) return;

        // Single delayed check after initial load completes
        const timer = setTimeout(() => {
            autoLoadCheckedRef.current = true;
            summaryData.forEach(item => {
                const stageName = item.name;
                const stageId = item.id;
                const scrollContainer = scrollRefs.current[stageName];

                if (scrollContainer && hasMore[stageName] && !loadingMore[stageName]) {
                    const { scrollHeight, clientHeight } = scrollContainer;

                    if (scrollHeight <= clientHeight) {
                        const nextPage = (currentPages[stageName] || 1) + 1;
                        loadTickets(stageName, stageId, nextPage, true);
                    }
                }
            });
        }, 500);

        return () => clearTimeout(timer);
    }, [summaryData, isLoading]);

    // Auto-scroll to searched company
    useEffect(() => {
        if (!searchTerm || isLoading) return;

        // Find the first stage that contains a matching company
        let foundStage = null;
        let foundCompanyId = null;

        for (const [stageName, stageTickets] of Object.entries(tickets)) {
            const matchingTicket = stageTickets.find(ticket =>
                ticket.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (matchingTicket) {
                foundStage = stageName;
                foundCompanyId = matchingTicket.id;
                break;
            }
        }

        if (foundStage && horizontalScrollRef.current) {
            // Find the stage column element
            const stageIndex = summaryData
                .filter(item => visibleColumns.includes(item.name))
                .findIndex(item => item.name === foundStage);

            if (stageIndex !== -1) {
                // Calculate scroll position (each column is 260px + 16px margin)
                const scrollPosition = stageIndex * (260 + 16);

                // Smooth scroll to the stage
                horizontalScrollRef.current.scrollTo({
                    left: scrollPosition,
                    behavior: 'smooth'
                });

                // Highlight the company card
                setHighlightedCompanyId(foundCompanyId);

                // Remove highlight after 3 seconds
                setTimeout(() => {
                    setHighlightedCompanyId(null);
                }, 3000);
            }
        }
    }, [tickets, searchTerm, summaryData, visibleColumns, isLoading]);

    // Get user-specific counts
    useEffect(() => {
        if (summaryData.length > 0) {
            getUserSpecificCounts();
        }
    }, [getUserSpecificCounts, summaryData]);

    // Get country counts
    useEffect(() => {
        if (stageViewCountries.length > 0) {
            getCountryCounts();
        }
    }, [getCountryCounts, stageViewCountries]);

    // Memoized filtered countries
    const filteredCountries = useMemo(() => {
        if (!selectedCreatedBy) return stageViewCountries;
        return stageViewCountries.filter(country =>
            country.id === 'all' || (countryCounts[country.id] && countryCounts[country.id] > 0)
        );
    }, [selectedCreatedBy, stageViewCountries, countryCounts]);

    // Handle ticket click
    const handleTicketClick = useCallback((ticketId) => {
        navigate(`/companies/${ticketId}`);
    }, [navigate]);

    // Import Excel handlers
    const handleDownloadSampleExcel = () => {
        const link = document.createElement('a');
        link.href = sampleExcelFile;
        link.download = 'Sample_Excel_File.xlsx';
        link.click();
    };

    const handleImportExcelClick = () => {
        setIsImportExcelDialogOpen(true);
    };

    const handleExcelFileUpload = () => {
        setExcelImportSummary(null);
        setErrorExcelFileLink(null);
        document.getElementById('excelFileInput').value = '';
        document.getElementById('excelFileInput').click();
    };

    const handleUploadExcel = (event) => {
        const selectedFile = event.target.files[0];
        const MAX_SIZE_MB = 50;
        const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
        if (!selectedFile) return;

        setImportStatus('Uploading');

        if (selectedFile.size > MAX_SIZE_BYTES) {
            showErrorToast(null, "Please select a file smaller than 50 MB.");
            return;
        }

        const acceptedTypes = ['.xls', '.xlsx'];
        const isValidType = acceptedTypes.some(type => selectedFile.name.endsWith(type));

        if (!isValidType) {
            showErrorToast(null, "Only Excel file are allowed.");
            return;
        }

        callImportCompanyDataFromExcel(selectedFile);
    };

    const callImportCompanyDataFromExcel = (selectedFile) => {
        setIsExcelImportProcessing(true);
        setImportStatus('Processing');

        const formData = new FormData();
        formData.append('file', selectedFile);

        apiInstance({
            method: "post",
            url: 'company/import_company_data_from_excel/',
            data: formData,
            headers: {
                Authorization: `Token ${localStorage.getItem('authToken')}`,
            },
            onUploadProgress: (progressEvent) => {
                const { loaded, total } = progressEvent;
                const percent = Math.floor((loaded * 100) / total);
                setUploadProgress(percent);
            },
        })
            .then(function (response) {
                setImportStatus('Validating');
                if (response) {
                    if (response?.data?.errors?.length > 0) {
                        setImportStatus('Completed With Errors');
                    } else {
                        setImportStatus('Completed');
                    }
                    setExcelImportSummary(response?.data?.summary);
                    if (response?.data?.error_log_file) setErrorExcelFileLink(response?.data?.error_log_file);
                }
            })
            .catch(function (error) {
                showErrorToast(error);
                setImportStatus('Failed');
            })
            .finally(() => {
                setIsExcelImportProcessing(false);
                setIsExcelImportCompleted(true);
                setImportStatus(null);
            });
    };

    const handleDownloadErrorLog = () => {
        if (errorExcelFileLink) {
            apiInstance({
                url: `${constants.HTTP_METHOD}${constants.HTTP_URL}${constants.HTTP_PORT}${errorExcelFileLink}`,
                method: 'GET',
                responseType: 'blob',
            }).then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const date = new Date().toLocaleDateString().split('/').join('_');
                link.download = `Failed_Companies_Log_${date}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            });
        }
    };

    const [isTeamLead, setIsTeamLead] = useState(false);
    const checkTeamLeadStatus = async () => {
        try {
            const response = await tasksApi.checkTeamLeadStatus();
            const isLead = !!response?.data?.is_team_lead;
            setIsTeamLead(isLead);
        } catch (error) {
            setIsTeamLead(false);
        }
    };

    useEffect(() => {
        checkTeamLeadStatus();
    }, []);

    // Memoized column renderer
    const renderColumn = useCallback((item) => {
        const stageName = item.name;
        const stageId = item.id;
        const stageTickets = tickets[stageName] || [];
        const isLoadingMore = loadingMore[stageName];
        const hasMoreTickets = hasMore[stageName];

        const selectedUserName = userData.find(user => user.id === selectedCreatedBy)?.user;
        let filteredTickets = selectedCreatedBy ?
            stageTickets.filter(ticket => ticket.created_by_name === selectedUserName) :
            stageTickets;

        // Additional client-side filtering for search - use searchTerm (not debounced) for immediate UI response
        if (searchTerm) {
            filteredTickets = filteredTickets.filter(ticket =>
                ticket.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Check if this is the 0% stage (exact match, not 10%, 50%, etc.)
        const isZeroPercentStage = stageName && (
            stageName.toLowerCase().includes('- 0%') ||
            stageName.toLowerCase().includes('outreach - 0') ||
            stageName.toLowerCase() === 'outreach - 0%'
        );

        // Check if this is the 5% stage
        const isFivePercentStage = stageName && (
            stageName.toLowerCase().includes('- 5%') ||
            stageName.toLowerCase().includes('future-outreach - 5')
        );

        // Check if this is the 100% stage (Closed)
        const isHundredPercentStage = stageName && (
            stageName.toLowerCase().includes('- 100%') ||
            stageName.toLowerCase().includes('closed')
        );

        // Check if user is super admin
        const isSuperAdmin = userInfo?.is_superuser === true;

        // For outreach stage, use totalCounts if available (updated by filter), otherwise use item.count
        let actualCount;
        if (selectedCreatedBy && userSpecificCounts[stageName] !== undefined) {
            actualCount = userSpecificCounts[stageName];
        } else if (isZeroPercentStage && totalCounts[stageName] !== undefined) {
            actualCount = totalCounts[stageName];
        } else if (isFivePercentStage && totalCounts[stageName] !== undefined) {
            actualCount = totalCounts[stageName];
        } else if (isHundredPercentStage && archiveFilter === 'archived') {
            // When showing archived, use tickets array length for accurate count
            actualCount = totalCounts[stageName] !== undefined ? totalCounts[stageName] : stageTickets.length;
        } else if (isHundredPercentStage && totalCounts[stageName] !== undefined) {
            actualCount = totalCounts[stageName];
        } else {
            actualCount = selectedCreatedBy ? 0 : item.count;
        }

        return (
            <DroppableColumn
                key={stageName}
                stageId={stageId}
                stageName={stageName}
                actualCount={actualCount}
                isZeroPercentStage={isZeroPercentStage}
                isFivePercentStage={isFivePercentStage}
                isHundredPercentStage={isHundredPercentStage}
                outreachFilter={outreachFilter}
                onOutreachFilterChange={handleOutreachFilterChange}
                invalidFilter={invalidFilter}
                onInvalidFilterChange={handleInvalidFilterChange}
                newsletterFilter={newsletterFilter}
                onNewsletterFilterChange={handleNewsletterFilterChange}
                archiveFilter={archiveFilter}
                onArchiveFilterChange={handleArchiveFilterChange}
                isSuperAdmin={isSuperAdmin}
            >
                <Box
                    ref={el => scrollRefs.current[stageName] = el}
                    onScroll={() => handleScroll(stageName, stageId)}
                    sx={{
                        height: '100%',
                        overflowY: 'auto',
                        p: 2,
                        pb: 6,
                        '&::-webkit-scrollbar': {
                            width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#c1c1c1',
                            borderRadius: '10px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#a8a8a8',
                        }
                    }}
                >
                    <SortableContext items={filteredTickets.map(t => t.id)} strategy={verticalListSortingStrategy}>
                        {filteredTickets.length > 0 ? (
                            <>
                                {filteredTickets.map(ticket => (
                                    <DraggableTicket
                                        key={ticket.id}
                                        ticket={ticket}
                                        onTicketClick={handleTicketClick}
                                        isHighlighted={ticket.id === highlightedCompanyId}
                                        isDueReminder={dueReminderCompanyIds.includes(ticket.id)}
                                        reminder={companyReminders[ticket.id]}
                                        onViewReminder={handleViewReminderNote}
                                        isZeroPercentStage={isZeroPercentStage}
                                        isFivePercentStage={isFivePercentStage}
                                        isHundredPercentStage={isHundredPercentStage}
                                        onMarkOutreached={handleMarkOutreached}
                                        onMarkInvalid={handleMarkInvalid}
                                        onViewInvalidDetails={handleViewInvalidDetails}
                                        onViewNewsletterDetails={handleViewNewsletterDetails}
                                        onArchiveCompany={(ticket) => {
                                            setCompanyToArchive(ticket);
                                            setArchiveDialogOpen(true);
                                        }}
                                        onViewArchiveDetails={handleViewArchiveDetails}
                                        onAddDnd={handleAddDnd}
                                        onRemoveDnd={handleRemoveDnd}
                                        isSuperAdmin={isSuperAdmin}
                                    />
                                ))}
                                {isLoadingMore && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={20} />
                                    </Box>
                                )}
                                {!hasMoreTickets && filteredTickets.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', p: 1 }}>
                                        No more tickets
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 4,
                                textAlign: 'center',
                                minHeight: 200
                            }}>
                                <Typography variant="h6" sx={{ fontSize: '2rem', mb: 1 }}>📋</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                    {isLoadingMore ? 'Loading companies...' : 'No companies in this stage'}
                                </Typography>
                                {!isLoadingMore && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                        Drag companies here to move them to this stage
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </SortableContext>
                </Box>
            </DroppableColumn>
        );
    }, [tickets, loadingMore, hasMore, userData, selectedCreatedBy, userSpecificCounts, handleScroll, handleTicketClick, recentlyUpdated, highlightedCompanyId, searchTerm, outreachFilter, handleOutreachFilterChange, handleMarkOutreached, invalidFilter, handleInvalidFilterChange, handleMarkInvalid, handleViewInvalidDetails, handleAddDnd, handleRemoveDnd]);

    // Render skeleton columns to match draggable columns design
    const renderSkeletonColumns = () => {
        return (
            Array.from({ length: 6 }).map((_, index) => (
                <Box
                    key={index}
                    sx={{
                        width: 300,
                        minWidth: 300,
                        maxWidth: 300,
                        mr: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        bgcolor: '#f7f8f9',
                        borderRadius: '8px',
                        border: '1px solid #dfe1e6',
                        overflow: 'hidden',
                        transition: 'all 0.15s ease'
                    }}
                >
                    {/* Professional Header - Jira Style */}
                    <Box
                        sx={{
                            bgcolor: '#f7f8f9',
                            borderBottom: '1px solid #dfe1e6',
                            p: '12px 16px',
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexShrink: 0,
                            minHeight: '48px'
                        }}
                    >
                        <Skeleton sx={{ color: '#172b4d', borderRadius: 1 }} variant="text" width={100} height={30} />
                        <Skeleton sx={{ color: '#172b4d' }} variant="circular" width={30} height={30} />
                    </Box>
                    {Array.from({ length: 5 }).map((__, idx) => (
                        <Skeleton key={idx} variant="rectangular" height={80} sx={{ mb: 2, borderRadius: 2, mx: 2 }} />
                    ))}
                </Box>
            ))
        );
    };

    return (
        <Page title="Dashboard | CRM">
            <Box sx={{ bgcolor: '#f5f7fa', height: '100vh', width: '100%', pt: 3 }}>
                <Container sx={{ minWidth: "100%", height: '100%', display: 'flex', flexDirection: 'column' }}>

                    {/* Active Date Filter Indicator - Above Search Bar */}
                    {selectedDateRange && selectedDateRange.length >= 2 && selectedDateRange[0] && selectedDateRange[1] && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Chip
                                size="small"
                                icon={<Icon icon="mdi:calendar" style={{ fontSize: '12px' }} />}
                                label={`${selectedDateRange[2] === 'created' ? 'Created' : 'Modified'}: ${new Date(selectedDateRange[0]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - ${new Date(selectedDateRange[1]).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`}
                                onDelete={() => setSelectedDateRange([])}
                                deleteIcon={<Close sx={{ fontSize: '12px !important' }} />}
                                sx={{
                                    bgcolor: '#e3f2fd',
                                    color: '#1976d2',
                                    fontWeight: 600,
                                    fontSize: '0.65rem',
                                    height: '22px',
                                    '& .MuiChip-label': {
                                        px: 0.75,
                                    },
                                    '& .MuiChip-icon': {
                                        color: '#1976d2',
                                        ml: 0.5,
                                    },
                                    '& .MuiChip-deleteIcon': {
                                        color: '#1976d2',
                                        mr: 0.25,
                                        '&:hover': {
                                            color: '#d32f2f',
                                        },
                                    },
                                }}
                            />
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        {/* Search + Filters */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                flexWrap: 'nowrap',
                                gap: 1.5,
                            }}
                        >
                            {/* 🔍 Left: Search bar + Inline Filters (wrap onto multiple
                                lines instead of scrolling horizontally — 2026-07-21) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, rowGap: 1.5, flex: 1, minWidth: 0 }}>
                                {/* Assistant button hidden as of 2026-07-17 (not needed for now).
                                    Restore by changing `false &&` back to render it. */}
                                {false && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 0.25,
                                    }}
                                >
                                    <Box
                                        onClick={assistant.toggleAssistant}
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: '#ede7f6',
                                            color: '#673ab7',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                                            '&:hover': {
                                                bgcolor: '#d1c4e9',
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
                                            },
                                        }}
                                    >
                                        <Assistant />
                                    </Box>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: '#673ab7',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1,
                                        }}
                                    >
                                        Assistant
                                    </Typography>
                                </Box>
                                )}
                                <TextField
                                    size="small"
                                    placeholder="Search companies..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    sx={{
                                        width: 240,
                                        flexShrink: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            '&:hover fieldset': { borderColor: '#1976d2' },
                                        },
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ color: '#1976d2' }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSearchTerm('')}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <Close sx={{ fontSize: 18, color: '#666' }} />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Country Filter - Show dropdown if more than 1, else show chip */}
                                {stageViewCountries.filter((item) => item.id !== 'all').length > 1 ? (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={stageViewCountries.filter((item) => item.id !== 'all')}
                                        getOptionLabel={(option) => option.country || ''}
                                        value={stageViewCountries.filter((c) => selectedCountries.includes(c.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedCountries(newValue.map((c) => c.id));
                                            setIsLoading(true);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Countries" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                ) : (
                                    selectedCountries.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>Country:</Typography>
                                            <Chip
                                                label={stageViewCountries.find(c => c.id === selectedCountries[0])?.country || ''}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Box>
                                    )
                                )}

                                {/* Offering (Category) Filter - Super Admin only */}
                                {userInfo?.role?.includes("SUPER_ADMIN") && stageViewOfferings.length > 0 && (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={stageViewOfferings}
                                        getOptionLabel={(option) => option.offering || ''}
                                        value={stageViewOfferings.filter((o) => selectedOfferings.includes(o.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedOfferings(newValue.map((o) => o.id));
                                            setIsLoading(true);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Offerings" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                )}

                                {/* Business Type Filter - Ungated (all roles) */}
                                {stageViewBusinessTypes.length > 0 && (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={stageViewBusinessTypes}
                                        getOptionLabel={(option) => option.business_type || ''}
                                        value={stageViewBusinessTypes.filter((b) => selectedBusinessTypes.includes(b.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedBusinessTypes(newValue.map((b) => b.id));
                                            setIsLoading(true);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Business Type" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                )}

                                {/* Partner Filter - Ungated (all roles) */}
                                {stagePartners.length > 0 && (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={stagePartners}
                                        getOptionLabel={(option) => option.partner || ''}
                                        value={stagePartners.filter((p) => selectedPartners.includes(p.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedPartners(newValue.map((p) => p.id));
                                            setIsLoading(true);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Partner" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                )}

                                {/* Partner-introduced only toggle - Ungated (all roles) */}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={introducedByPartnerOnly}
                                            onChange={(e) => {
                                                setIntroducedByPartnerOnly(e.target.checked);
                                                setIsLoading(true);
                                            }}
                                        />
                                    }
                                    label="Partner-introduced only"
                                />

                                {/* Team Filter - Show dropdown if more than 1, else show chip */}
                                {teamData.length > 1 ? (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={teamData}
                                        getOptionLabel={(option) => option.name}
                                        value={teamData.filter((t) => selectedTeams.includes(t.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedTeams(newValue.map((t) => t.id));
                                            setIsLoading(true);
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Teams" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                ) : (
                                    selectedTeams.length > 0 && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>Team:</Typography>
                                            <Chip
                                                label={teamData.find(t => t.id === selectedTeams[0])?.name || ''}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        </Box>
                                    )
                                )}

                                {/* User Filter - Show only for SUPER_ADMIN (not for team leads or team members) */}
                                {userInfo?.role?.includes("SUPER_ADMIN") && (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={userData}
                                        getOptionLabel={(option) => option.name}
                                        value={userData.filter((u) => selectedUser.includes(u.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedUser(newValue.map((u) => u.id));
                                            setIsLoading(true);
                                        }}
                                        renderOption={(props, option) => {
                                            const isGWXAgent = option.name === "GWX Agent";
                                            return (
                                                <Box
                                                    component="li"
                                                    {...props}
                                                    sx={{
                                                        ...(props.sx || {}),
                                                        ...(isGWXAgent && {
                                                            bgcolor: '#fff3e0',
                                                            borderLeft: '3px solid #ff9800',
                                                            fontWeight: 600,
                                                            '&:hover': {
                                                                bgcolor: '#ffe0b2',
                                                            },
                                                            '&.Mui-focused': {
                                                                bgcolor: '#ffe0b2',
                                                            }
                                                        })
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                        <Typography component="span">{option.name}</Typography>
                                                        {isGWXAgent && (
                                                            <Chip
                                                                label="GWX"
                                                                size="small"
                                                                sx={{
                                                                    ml: 1,
                                                                    bgcolor: '#ff9800',
                                                                    color: 'white',
                                                                    fontSize: '0.65rem',
                                                                    height: '18px',
                                                                    fontWeight: 700
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Users" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                )}

                                {/* User Filter - Show only for TEAM LEAD (not for SUPER_ADMIN or team members) */}
                                {isTeamLead && !userInfo?.role?.includes("SUPER_ADMIN") && (
                                    <Autocomplete
                                        multiple
                                        size="small"
                                        options={userData}
                                        getOptionLabel={(option) => option.name}
                                        value={userData.filter((u) => selectedUser.includes(u.id))}
                                        onChange={(e, newValue) => {
                                            setSelectedUser(newValue.map((u) => u.id));
                                            setIsLoading(true);
                                        }}
                                        renderOption={(props, option) => {
                                            const isGWXAgent = option.name === "GWX Agent";
                                            return (
                                                <Box
                                                    component="li"
                                                    {...props}
                                                    sx={{
                                                        ...(props.sx || {}),
                                                        ...(isGWXAgent && {
                                                            bgcolor: '#fff3e0',
                                                            borderLeft: '3px solid #ff9800',
                                                            fontWeight: 600,
                                                            '&:hover': {
                                                                bgcolor: '#ffe0b2',
                                                            },
                                                            '&.Mui-focused': {
                                                                bgcolor: '#ffe0b2',
                                                            }
                                                        })
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                        <Typography component="span">{option.name}</Typography>
                                                        {isGWXAgent && (
                                                            <Chip
                                                                label="GWX"
                                                                size="small"
                                                                sx={{
                                                                    ml: 1,
                                                                    bgcolor: '#ff9800',
                                                                    color: 'white',
                                                                    fontSize: '0.65rem',
                                                                    height: '18px',
                                                                    fontWeight: 700
                                                                }}
                                                            />
                                                        )}
                                                    </Box>
                                                </Box>
                                            );
                                        }}
                                        renderInput={(params) => <TextField {...params} label="Users" variant="outlined" />}
                                        sx={{ minWidth: 200 }}
                                    />
                                )}

                                {/* Clear Filters Button */}
                                {(selectedCountries.length > 0 || selectedTeams.length > 0 || selectedUser.length > 0 || selectedBusinessTypes.length > 0 || selectedPartners.length > 0 || introducedByPartnerOnly || searchTerm || selectedDateRange.length > 0) && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            // Clear filters but maintain auto-selected single team/country for team leads
                                            const actualCountries = stageViewCountries.filter((item) => item.id !== 'all');
                                            const autoSelectedCountry = actualCountries.length === 1 ? [actualCountries[0].id] : [];
                                            const autoSelectedTeam = teamData.length === 1 ? [teamData[0].id] : [];

                                            setSelectedCountries(autoSelectedCountry);
                                            setSelectedTeams(autoSelectedTeam);
                                            setSelectedUser([]);
                                            setSelectedBusinessTypes([]);
                                            setSelectedPartners([]);
                                            setIntroducedByPartnerOnly(false);
                                            setSearchTerm('');
                                            setSelectedDateRange([]);
                                            setFilterParams(null);
                                            setIsLoading(true);
                                            // Clear localStorage
                                            localStorage.removeItem(FILTERS_STORAGE_KEY);
                                        }}
                                        sx={{ height: 40 }}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Box>

                            {/* 🎛️ Right: Other buttons (calendar, column visibility, etc.) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                <CalendarRangePicker
                                    value={selectedDateRange}
                                    onChange={(newValue) => {
                                        const safeValue = newValue || [];
                                        if (JSON.stringify(safeValue) !== JSON.stringify(selectedDateRange)) {
                                            setSelectedDateRange(safeValue);
                                        }
                                    }}
                                    filterType="created"
                                    labels={{
                                        start: { label: 'Created', value: 'created' },
                                        end: { label: 'Modified', value: 'modified' },
                                    }}
                                />

                                <ColumnVisibilityControl
                                    columns={summaryData}
                                    visibleColumns={visibleColumns}
                                    onVisibilityChange={setVisibleColumns}
                                    viewType={viewType}
                                />

                                {/* Add Company Button */}
                                <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<Icon icon={plusFill} />}
                                    sx={{
                                        backgroundColor: '#00AB55',
                                        fontWeight: 700,
                                        borderRadius: 2,
                                        boxShadow: '0 8px 16px rgba(0,171,85,0.1)',
                                        '&:hover': { backgroundColor: '#007B55' },
                                    }}
                                    onClick={() => setIsAddCompanyDrawerOpen(true)}
                                >
                                    Add Company
                                </Button>

                                {/* Total Summary */}
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontWeight: 700,
                                        color: '#1976d2',
                                        fontSize: '1rem',
                                        whiteSpace: 'nowrap',
                                        px: 2,
                                        py: 0.5,
                                        bgcolor: '#e3f2fd',
                                        borderRadius: 2,
                                    }}
                                >
                                    📊 Total: {summaryData
                                        .filter((item) => visibleColumns.includes(item.name))
                                        .reduce((sum, item) => sum + (item.count || 0), 0)}
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
                                    <Tooltip title="View Stage Rules">
                                        <IconButton
                                            onClick={() => setIsStageRulesModalOpen(true)}
                                            sx={{
                                                bgcolor: '#e3f2fd',
                                                color: '#1976d2',
                                                '&:hover': {
                                                    bgcolor: '#bbdefb',
                                                },
                                                width: 40,
                                                height: 40
                                            }}
                                        >
                                            <InfoOutlined />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            color: '#1976d2',
                                            whiteSpace: 'nowrap',
                                            lineHeight: 1
                                        }}
                                    >
                                        Stage Rules
                                    </Typography>
                                </Box>

                                {/* Notification Center */}
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <NotificationCenter />
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Pipeline Board */}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', height: '100%' }}>
                            {/* {Array.from({ length: 6 }, (_, i) => (
                                <Box key={i} sx={{ width: 320, minWidth: 320, maxWidth: 320 }}>
                                    <Skeleton variant="rectangular" height={60} sx={{ mb: 1, borderRadius: 3 }} />
                                    <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 3 }} />
                                </Box>
                            ))} */}
                            {renderSkeletonColumns()}
                        </Box>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragOver={handleDragOver}
                            onDragEnd={handleDragEnd}
                        >
                            <Box
                                ref={horizontalScrollRef}
                                sx={{
                                    display: 'flex',
                                    overflowX: 'auto',
                                    pb: 3,
                                    height: '100%',
                                    '&::-webkit-scrollbar': {
                                        height: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        background: '#f1f1f1',
                                        borderRadius: '10px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        background: '#c1c1c1',
                                        borderRadius: '10px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                        background: '#a8a8a8',
                                    }
                                }}>
                                {summaryData
                                    .filter(item => {
                                        if (!item.name || item.name === 'Unknown') return false;
                                        return visibleColumns.includes(item.name);
                                    })
                                    .map(item => renderColumn(item))}
                            </Box>
                            <DragOverlay>
                                {activeId && draggedTicket ? (
                                    <DraggableTicket
                                        ticket={draggedTicket}
                                        onTicketClick={handleTicketClick}
                                    />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    )}
                </Container>
            </Box >

            {/* Add Company Drawer */}
            < Drawer
                anchor="right"
                open={isAddCompanyDrawerOpen}
                onClose={() => setIsAddCompanyDrawerOpen(false)
                }
                PaperProps={{
                    sx: { width: '40%', minWidth: 400 }
                }}
            >
                <React.Suspense fallback={<CircularProgress />}>
                    <AddCompany setOpenDrawer={setIsAddCompanyDrawerOpen} />
                </React.Suspense>
            </Drawer>

            {/* Mark as Outreached Drawer */}
            < Drawer
                anchor="right"
                open={markOutreachDialogOpen}
                onClose={() => setMarkOutreachDialogOpen(false)}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>📤</span>
                        <Typography variant="h6">Mark as Outreached</Typography>
                    </Box>
                    <IconButton onClick={() => setMarkOutreachDialogOpen(false)} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {selectedOutreachCompany && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                            <Typography variant="h6">{selectedOutreachCompany.company_name}</Typography>
                        </Box>
                    )}

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Outreach Method *</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={outreachMethod}
                                onChange={(e) => setOutreachMethod(e.target.value)}
                            >
                                <MenuItem value="email">📧 Email</MenuItem>
                                <MenuItem value="call">📞 Call</MenuItem>
                                <MenuItem value="linkedin">💼 LinkedIn</MenuItem>
                                <MenuItem value="other">📝 Other</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Date *</Typography>
                            <TextField
                                type="date"
                                fullWidth
                                size="small"
                                value={outreachDate}
                                onChange={(e) => setOutreachDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Time *</Typography>
                            <TextField
                                type="time"
                                fullWidth
                                size="small"
                                value={outreachTime}
                                onChange={(e) => setOutreachTime(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Comment *</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            placeholder="Enter details about the outreach (required)..."
                            value={outreachComment}
                            onChange={(e) => setOutreachComment(e.target.value)}
                            error={!outreachComment.trim()}
                            helperText={!outreachComment.trim() ? 'Comment is required' : ''}
                        />
                    </Box>
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setMarkOutreachDialogOpen(false)} disabled={isMarkingOutreach}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitMarkOutreached}
                        disabled={isMarkingOutreach || !outreachComment.trim()}
                        startIcon={isMarkingOutreach ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isMarkingOutreach ? 'Saving...' : 'Mark as Outreached'}
                    </Button>
                </Box >
            </Drawer >

            {/* QM-exit Note Drawer (US-4) */}
            < Drawer
                anchor="right"
                open={qmExitCommentDialogOpen}
                onClose={handleQmExitCommentCancel}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#1976d2',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>📝</span>
                        <Typography variant="h6">Note on leaving Qualified Meetings</Typography>
                    </Box>
                    <IconButton onClick={handleQmExitCommentCancel} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {pendingDragData?.draggedTicket && (
                        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                            <Typography variant="h6">{pendingDragData.draggedTicket.company_name}</Typography>
                        </Box>
                    )}

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Exit Note *</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={6}
                            placeholder="Why is this company leaving Qualified Meetings? (required)..."
                            value={qmExitComment}
                            onChange={(e) => setQmExitComment(e.target.value)}
                            error={!qmExitComment.trim()}
                            helperText={!qmExitComment.trim() ? 'A note is required' : ''}
                        />
                    </Box>
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={handleQmExitCommentCancel}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleQmExitCommentConfirm}
                        disabled={!qmExitComment.trim()}
                    >
                        Save & Continue
                    </Button>
                </Box >
            </Drawer >

            {/* Financial Capture Dialog (B1 — T9): gates drag entry into a >=50% stage */}
            <FinancialCaptureDialog
                open={financialCaptureOpen}
                targetStageName={financialCaptureData?.targetStage?.name}
                initialValues={financialInitialValues}
                currencyLabel={financialCaptureCurrencyLabel}
                onConfirm={handleFinancialCaptureConfirm}
                onCancel={handleFinancialCaptureCancel}
            />

            {/* Add to DND dialog (shared reusable dialog) */}
            <AddToDndDialog
                open={addDndDialogOpen}
                company={addDndCompany}
                onClose={() => setAddDndDialogOpen(false)}
                onDone={handleDndAdded}
            />

            {/* Mark as Invalid Drawer */}
            < Drawer
                anchor="right"
                open={markInvalidDialogOpen}
                onClose={() => setMarkInvalidDialogOpen(false)}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#ff9800',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>⚠️</span>
                        <Typography variant="h6">Mark as Invalid</Typography>
                    </Box>
                    <IconButton onClick={() => setMarkInvalidDialogOpen(false)} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                        <Typography variant="h6">{selectedInvalidCompany?.company_name}</Typography>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Reason *</Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={selectedInvalidReason}
                                onChange={(e) => setSelectedInvalidReason(e.target.value)}
                                displayEmpty
                            >
                                <MenuItem value="" disabled>Select a reason</MenuItem>
                                {invalidReasons.map((reason) => (
                                    <MenuItem key={reason.id} value={reason.id}>
                                        {reason.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Additional Details (Optional)</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            placeholder="Enter additional details about why this company is invalid..."
                            value={invalidDescription}
                            onChange={(e) => setInvalidDescription(e.target.value)}
                        />
                    </Box>

                    <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                        <Typography variant="caption" color="warning.dark">
                            ⚠️ This company will be moved to the 5% stage and marked as invalid.
                        </Typography>
                    </Box>
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setMarkInvalidDialogOpen(false)} disabled={isMarkingInvalid}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={submitMarkInvalid}
                        disabled={isMarkingInvalid || !selectedInvalidReason}
                        startIcon={isMarkingInvalid ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isMarkingInvalid ? 'Saving...' : 'Mark as Invalid'}
                    </Button>
                </Box >
            </Drawer >

            {/* Invalid Details Drawer */}
            < Drawer
                anchor="right"
                open={invalidDetailsDrawerOpen}
                onClose={() => setInvalidDetailsDrawerOpen(false)}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#ff9800',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>⚠️</span>
                        <Typography variant="h6">Invalid Company Details</Typography>
                    </Box>
                    <IconButton onClick={() => setInvalidDetailsDrawerOpen(false)} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                        <Typography variant="h6">{viewingInvalidCompany?.company_name}</Typography>
                    </Box>

                    {
                        viewingInvalidCompany?.invalid_details && (
                            <>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Reason</Typography>
                                    <Chip
                                        label={viewingInvalidCompany.invalid_details.reason || 'No reason'}
                                        color="warning"
                                        size="small"
                                    />
                                </Box>

                                {viewingInvalidCompany.invalid_details.description && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Description</Typography>
                                        <Typography variant="body2">
                                            {viewingInvalidCompany.invalid_details.description}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Marked By</Typography>
                                    <Typography variant="body2">
                                        {viewingInvalidCompany.invalid_details.marked_by || 'Unknown'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Marked At</Typography>
                                    <Typography variant="body2">
                                        {viewingInvalidCompany.invalid_details.marked_at
                                            ? new Date(viewingInvalidCompany.invalid_details.marked_at).toLocaleString()
                                            : 'Unknown'}
                                    </Typography>
                                </Box>
                            </>
                        )
                    }
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setInvalidDetailsDrawerOpen(false)} disabled={isRemovingInvalid}>
                        Close
                    </Button>
                    <Button
                        variant="outlined"
                        color="success"
                        onClick={handleRemoveInvalid}
                        disabled={isRemovingInvalid}
                        startIcon={isRemovingInvalid ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isRemovingInvalid ? 'Removing...' : 'Remove Invalid & Move to 0%'}
                    </Button>
                </Box >
            </Drawer >

            {/* Newsletter Details Drawer */}
            < Drawer
                anchor="right"
                open={newsletterDetailsDrawerOpen}
                onClose={() => setNewsletterDetailsDrawerOpen(false)}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#2196f3',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>📧</span>
                        <Typography variant="h6">Newsletter Only Details</Typography>
                    </Box>
                    <IconButton onClick={() => setNewsletterDetailsDrawerOpen(false)} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                        <Typography variant="h6">{viewingNewsletterCompany?.company_name}</Typography>
                    </Box>

                    {
                        viewingNewsletterCompany?.newsletter_details && (
                            <>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Note</Typography>
                                    <Typography variant="body2">
                                        {viewingNewsletterCompany.newsletter_details.note || 'No note provided'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Marked By</Typography>
                                    <Typography variant="body2">
                                        {viewingNewsletterCompany.newsletter_details.marked_by || 'Unknown'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Marked At</Typography>
                                    <Typography variant="body2">
                                        {viewingNewsletterCompany.newsletter_details.marked_at
                                            ? new Date(viewingNewsletterCompany.newsletter_details.marked_at).toLocaleString()
                                            : 'Unknown'}
                                    </Typography>
                                </Box>

                                {viewingNewsletterCompany.newsletter_details.previous_stage_id && (
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Previous Stage ID</Typography>
                                        <Typography variant="body2">
                                            {viewingNewsletterCompany.newsletter_details.previous_stage_id}
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )
                    }

                    <Box sx={{ mt: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #2196f3' }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Note:</strong> This company has been marked for newsletter only.
                            They will receive newsletters but won't be contacted directly for business.
                            Removing this mark will move the company back to its previous stage.
                        </Typography>
                    </Box>
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setNewsletterDetailsDrawerOpen(false)} disabled={isRemovingNewsletter}>
                        Close
                    </Button>
                    <Button
                        variant="outlined"
                        color="success"
                        onClick={handleRemoveNewsletter}
                        disabled={isRemovingNewsletter}
                        startIcon={isRemovingNewsletter ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isRemovingNewsletter ? 'Removing...' : 'Remove Newsletter Mark'}
                    </Button>
                </Box >
            </Drawer >

            {/* Archive Drawer */}
            < Drawer
                anchor="right"
                open={archiveDialogOpen}
                onClose={() => {
                    setArchiveDialogOpen(false);
                    setArchiveNote('');
                    setCompanyToArchive(null);
                }}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#9c27b0',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>📦</span>
                        <Typography variant="h6">Archive Company</Typography>
                    </Box>
                    <IconButton
                        onClick={() => {
                            setArchiveDialogOpen(false);
                            setArchiveNote('');
                            setCompanyToArchive(null);
                        }}
                        sx={{ color: 'white' }}
                        disabled={isArchiving}
                    >
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                        <Typography variant="h6">{companyToArchive?.company_name}</Typography>
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Archive Note (optional)"
                        placeholder="Enter a reason or note for archiving this company..."
                        value={archiveNote}
                        onChange={(e) => setArchiveNote(e.target.value)}
                        disabled={isArchiving}
                    />

                    <Box sx={{ mt: 3, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Note:</strong> Archiving a company will hide it from the default view.
                            You can view archived companies by toggling the archive filter in the 100% column.
                        </Typography>
                    </Box>
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                        onClick={() => {
                            setArchiveDialogOpen(false);
                            setArchiveNote('');
                            setCompanyToArchive(null);
                        }}
                        disabled={isArchiving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#9c27b0', '&:hover': { bgcolor: '#7b1fa2' } }}
                        onClick={handleArchiveCompany}
                        disabled={isArchiving}
                        startIcon={isArchiving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isArchiving ? 'Archiving...' : 'Archive Company'}
                    </Button>
                </Box >
            </Drawer >

            {/* Archive Details Drawer */}
            < Drawer
                anchor="right"
                open={archiveDetailsDrawerOpen}
                onClose={() => setArchiveDetailsDrawerOpen(false)}
                PaperProps={{
                    sx: { width: '400px', maxWidth: '90vw' }
                }}
            >
                {/* Header */}
                < Box sx={{
                    bgcolor: '#9c27b0',
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>📦</span>
                        <Typography variant="h6">Archive Details</Typography>
                    </Box>
                    <IconButton onClick={() => setArchiveDetailsDrawerOpen(false)} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box >

                {/* Content */}
                < Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">Company</Typography>
                        <Typography variant="h6">{viewingArchivedCompany?.company_name}</Typography>
                    </Box>

                    {
                        viewingArchivedCompany?.archive_details && (
                            <>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Note</Typography>
                                    <Typography variant="body2">
                                        {viewingArchivedCompany.archive_details.note || 'No note provided'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Archived By</Typography>
                                    <Typography variant="body2">
                                        {viewingArchivedCompany.archive_details.archived_by || 'Unknown'}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>Archived At</Typography>
                                    <Typography variant="body2">
                                        {viewingArchivedCompany.archive_details.archived_at
                                            ? new Date(viewingArchivedCompany.archive_details.archived_at).toLocaleString()
                                            : 'Unknown'}
                                    </Typography>
                                </Box>
                            </>
                        )
                    }

                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f3e5f5', borderRadius: 1, border: '1px solid #9c27b0' }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Note:</strong> This company has been archived.
                            Unarchiving will make it visible in the default view again.
                        </Typography>
                    </Box>
                </Box >

                {/* Footer */}
                < Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setArchiveDetailsDrawerOpen(false)} disabled={isUnarchiving}>
                        Close
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ color: '#9c27b0', borderColor: '#9c27b0' }}
                        onClick={handleUnarchiveCompany}
                        disabled={isUnarchiving}
                        startIcon={isUnarchiving ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {isUnarchiving ? 'Unarchiving...' : 'Unarchive Company'}
                    </Button>
                </Box >
            </Drawer >

            {/* Import Excel Dialog */}
            {
                isImportExcelDialogOpen && (
                    <Dialog maxWidth='sm' fullWidth open={isImportExcelDialogOpen} onClose={() => { setIsImportExcelDialogOpen(false); setExcelImportSummary(null); setErrorExcelFileLink(null); window.location.reload(); }}>
                        <DialogContent sx={{ px: 3 }}>
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography align='center' variant="h5">Import Excel</Typography>
                                <IconButton onClick={() => { setIsImportExcelDialogOpen(false); setExcelImportSummary(null); setErrorExcelFileLink(null); window.location.reload(); }}>
                                    <Close fontSize="medium" />
                                </IconButton>
                            </Stack>
                            <DialogContent>
                                <Stack direction='column' spacing={2}>
                                    <Stack spacing={1} border='1px solid lightgrey' borderRadius='8px' padding={2}>
                                        <Stack direction='row' spacing={1} alignItems='center'>
                                            <Typography><Error fontSize='medium' color='error' /></Typography>
                                            <Typography variant='subtitle1'>Important!</Typography>
                                        </Stack>
                                        <Stack ml='30px'>
                                            <Typography>Please ensure you upload the file in the exact format as the sample file. Otherwise, company data will not be added.</Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack>
                                        {isExcelImportProcessing && (
                                            <Stack spacing={1} alignItems='center'>
                                                {importStatus}
                                                <LinearProgress variant="determinate" value={uploadProgress} sx={{ width: '100%', mt: 2 }} />
                                            </Stack>
                                        )}
                                        {isExcelImportCompleted && excelImportSummary ? (
                                            <Stack spacing={2}>
                                                <Stack spacing={1} border='1px solid lightgrey' borderRadius='8px' padding={2}>
                                                    <Typography variant="subtitle1">Icon Legend</Typography>
                                                    <Stack direction="column" spacing={2}>
                                                        <Stack direction='row' alignItems='center' spacing={1}>
                                                            <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                            <Typography variant="caption">Created: Successfully added.</Typography>
                                                        </Stack>
                                                        <Stack direction='row' alignItems='center' spacing={1}>
                                                            <Update fontSize="medium" style={{ color: 'orange', marginLeft: -3 }} />
                                                            <Typography variant="caption">Updated: Information has been updated successfully.</Typography>
                                                        </Stack>
                                                        <Stack direction='row' alignItems='center' spacing={1}>
                                                            <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                            <Typography variant="caption">Failed: An error occurred during processing.</Typography>
                                                        </Stack>
                                                    </Stack>
                                                </Stack>
                                                <Typography variant="h6">Import Summary</Typography>
                                                <Stack spacing={2} width='max-content'>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent='space-between'>
                                                        <Typography>Company</Typography>
                                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Created">
                                                                    <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.companies_created}</Typography>
                                                            </Stack>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Updated">
                                                                    <Update fontSize="medium" style={{ color: 'orange', background: '' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.companies_updated}</Typography>
                                                            </Stack>
                                                            <Stack alignItems='center' direction='row' spacing={1}>
                                                                <Tooltip title="Failed">
                                                                    <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                                </Tooltip>
                                                                <Typography variant="h6">{excelImportSummary?.companies_failed}</Typography>
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent='space-between'>
                                                        <Typography>Members</Typography>
                                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                                                                <Stack alignItems='center' direction='row' spacing={1}>
                                                                    <Tooltip title="Created">
                                                                        <CheckCircle fontSize="small" style={{ color: 'white', background: 'green', borderRadius: '50%' }} />
                                                                    </Tooltip>
                                                                    <Typography variant="h6">{excelImportSummary?.members_created}</Typography>
                                                                </Stack>
                                                                <Stack alignItems='center' direction='row' spacing={1}>
                                                                    <Tooltip title="Updated">
                                                                        <Update fontSize="medium" style={{ color: 'orange', background: '' }} />
                                                                    </Tooltip>
                                                                    <Typography variant="h6">{excelImportSummary?.members_updated}</Typography>
                                                                </Stack>
                                                                <Stack alignItems='center' direction='row' spacing={1}>
                                                                    <Tooltip title="Failed">
                                                                        <Error fontSize="small" style={{ color: 'white', background: 'red', borderRadius: '50%' }} />
                                                                    </Tooltip>
                                                                    <Typography variant="h6">{excelImportSummary?.members_failed}</Typography>
                                                                </Stack>
                                                            </Stack>
                                                        </Stack>
                                                    </Stack>
                                                    <Typography variant="body1">Total Companies: <b>{excelImportSummary?.total_companies}</b></Typography>
                                                </Stack>
                                                {excelImportSummary?.failed_companies?.length > 0 && (
                                                    <Stack spacing={1}>
                                                        <Stack direction='row' spacing={1} alignItems='center'>
                                                            <Typography><Warning fontSize='medium' color='warning' /></Typography>
                                                            <Typography variant='subtitle1'>Failed Companies</Typography>
                                                        </Stack>
                                                        <Box>
                                                            {excelImportSummary.failed_companies.map((company) => (
                                                                <Chip size='small' label={company} style={{ margin: 2 }} key={company} />
                                                            ))}
                                                        </Box>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        ) : null}
                                    </Stack>
                                    {errorExcelFileLink && (
                                        excelImportSummary?.errors?.length > 0 && (
                                            <Stack spacing={1} width='max-content'>
                                                <Button
                                                    variant='text'
                                                    color='error'
                                                    startIcon={<Download />}
                                                    onClick={() => handleDownloadErrorLog()}
                                                    style={{ background: 'rgba(255, 0, 0, 0.1)' }}
                                                >
                                                    Download Error Log
                                                </Button>
                                            </Stack>
                                        )
                                    )}
                                    <Stack spacing={2} direction={{ xs: "column", sm: "row" }} justifyContent='center'>
                                        <Button
                                            variant="contained"
                                            startIcon={<Upload />}
                                            sx={{
                                                backgroundColor: "#00AB55",
                                                fontWeight: "700",
                                                borderRadius: "8px",
                                                boxShadow: "0 8px 16px 0 rgb(0 171 85 / 10%)"
                                            }}
                                            onClick={() => handleExcelFileUpload()}
                                        >Choose Excel File</Button>
                                        <input
                                            id="excelFileInput"
                                            type="file"
                                            accept=".xls, .xlsx"
                                            style={{ display: 'none' }}
                                            onChange={(event) => handleUploadExcel(event)}
                                        />
                                        <Button
                                            startIcon={<Download />}
                                            style={{ color: 'green', border: '1px solid green', borderRadius: '8px' }}
                                            onClick={() => handleDownloadSampleExcel()}
                                        >
                                            Download Sample Excel File
                                        </Button>
                                    </Stack>
                                </Stack>
                            </DialogContent>
                        </DialogContent>
                    </Dialog>
                )
            }

            {/* Deal Details Drawer */}
            <Drawer
                anchor="right"
                open={dealDetailsDialogOpen}
                onClose={handleDealDetailsCancel}
            >
                <Drawer
                    anchor="right"
                    open={dealDetailsDialogOpen}
                    onClose={handleDealDetailsCancel}
                >
                    <ClosedDealDetailsDrawer
                        closedDealDetails={fetchedDealDetails}
                        onClose={handleDealDetailsCancel}
                        onSave={handleDealDetailsSave}
                    />
                </Drawer>
            </Drawer>

            {/* Task Assignment Drawer for 10% Intro Call Stage */}
            <Drawer
                anchor="right"
                open={taskAssignmentDrawerOpen}
                onClose={(event, reason) => {
                    // Prevent closing when clicking outside or pressing Escape
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    handleTaskAssignmentCancel();
                }}
            >
                <TaskAssignmentDrawerContent
                    stage={pendingDragData?.targetStage}
                    companyName={pendingDragData?.draggedTicket?.company_name}
                    companyId={pendingDragData?.draggedTicket?.id}
                    companyData={pendingDragData?.draggedTicket}
                    teamMembers={teamMembers}
                    onClose={handleTaskAssignmentCancel}
                    onComplete={handleTaskAssignmentComplete}
                />
            </Drawer>

            {/* Team Assignment Drawer */}
            <Drawer
                anchor="right"
                open={teamAssignmentDrawerOpen}
                onClose={(event, reason) => {
                    // Prevent closing when clicking outside or pressing Escape
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                        return;
                    }
                    handleTeamAssignmentCancel();
                }}
            >
                <TeamAssignmentDrawerContent
                    stage={pendingDragData?.targetStage}
                    companyName={pendingDragData?.draggedTicket?.company_name}
                    companyId={pendingDragData?.draggedTicket?.id}
                    companyData={pendingDragData?.draggedTicket}
                    onClose={handleTeamAssignmentCancel}
                    onComplete={handleTeamAssignmentComplete}
                    teamMembers={teamMembers}
                    userRoles={userRoles}
                    getFilteredTeamMembersForRole={getFilteredTeamMembersForRole}
                />
            </Drawer>

            {/* Stage Rules Modal */}
            <StageRulesModal
                open={isStageRulesModalOpen}
                onClose={() => setIsStageRulesModalOpen(false)}
            />

            {/* 5% Stage Reminder Dialog */}
            <FivePercentReminderDialog
                open={fivePercentReminderDialogOpen}
                onClose={handleFivePercentReminderCancel}
                onSave={handleFivePercentReminderSave}
                onNewsletterSave={handleFivePercentNewsletterSave}
                companyName={pendingDragData?.draggedTicket?.company_name}
                sourceStage={pendingDragData?.sourceStage?.name}
            />

            {/* Reminder Note View/Edit Dialog */}
            <ReminderNoteDialog
                open={reminderNoteDialogOpen}
                onClose={() => {
                    setReminderNoteDialogOpen(false);
                    setSelectedReminderNote(null);
                }}
                reminder={selectedReminderNote}
                onEdit={handleEditReminder}
                onDeactivate={handleDeactivateReminder}
            />

            {/* Reminder Removal Confirmation Drawer */}
            <Drawer
                anchor="right"
                open={reminderRemovalConfirmOpen}
                onClose={handleReminderRemovalCancel}
                PaperProps={{
                    sx: { width: { xs: '100%', sm: 450 } }
                }}
            >
                {/* Header */}
                <Box sx={{ bgcolor: '#ff9800', color: 'white', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Warning sx={{ fontSize: '1.5rem' }} />
                        <Typography variant="h6">Remove Reminder?</Typography>
                    </Box>
                    <IconButton onClick={handleReminderRemovalCancel} sx={{ color: 'white' }}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        You are moving <strong>{pendingReminderRemoval?.draggedTicket?.company_name}</strong> from
                        <strong> 5% (On Hold)</strong> to <strong>{pendingReminderRemoval?.targetStage?.name}</strong>.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This company has an active reminder that will be <strong>removed</strong> when you proceed.
                    </Typography>
                    {companyReminders[pendingReminderRemoval?.draggedTicket?.id] && (
                        <Box sx={{ bgcolor: '#fff3e0', p: 2, borderRadius: 1, border: '1px solid #ffb74d' }}>
                            <Typography variant="caption" color="text.secondary">Current Reminder:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                📅 {new Date(companyReminders[pendingReminderRemoval?.draggedTicket?.id]?.reminder_date).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                                📝 {companyReminders[pendingReminderRemoval?.draggedTicket?.id]?.note?.substring(0, 200)}
                                {companyReminders[pendingReminderRemoval?.draggedTicket?.id]?.note?.length > 200 ? '...' : ''}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Footer */}
                <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleReminderRemovalCancel}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleReminderRemovalConfirm}
                    >
                        Remove Reminder & Move
                    </Button>
                </Box>
            </Drawer>

            {/* Notification Onboarding Tutorial */}
            <React.Suspense fallback={null}>
                <NotificationOnboarding
                    userId={userInfo?.pk}
                    onComplete={async () => {
                        try {
                            // Check current permission first
                            let currentPermission = pushNotifications.getNotificationPermission();

                            // If permission is default, request it
                            if (currentPermission === 'default') {
                                currentPermission = await pushNotifications.requestNotificationPermission();
                            }

                            if (currentPermission === 'denied') {
                                showErrorToast('Notification permission denied. Please enable in browser settings.');
                                return;
                            }

                            if (currentPermission === 'granted') {
                                // Register service worker
                                await pushNotifications.registerServiceWorker();

                                // Subscribe to push
                                const subscription = await pushNotifications.subscribeToPushNotifications();

                                // Send to backend
                                const subscriptionData = pushNotifications.getSubscriptionJSON(subscription);
                                const deviceType = pushNotifications.getBrowserType();

                                await notificationApi.registerDevice({
                                    ...subscriptionData,
                                    device_type: deviceType
                                });

                                // Store user-specific permission state
                                if (userInfo?.pk) {
                                    localStorage.setItem(`notification_permission_granted_${userInfo.pk}`, 'true');
                                }

                                showToast('Push notifications enabled!', 'success');
                            }
                        } catch (error) {
                            console.error('Error enabling push notifications:', error);
                            showErrorToast('Failed to enable push notifications');
                        }
                    }}
                    onSkip={() => { }}
                />
                {/* Assistant Drawer */}
                {/* Assistant Drawer */}
                <AssistantDrawer
                // open={assistant.isOpen}
                // onClose={assistant.toggleAssistant}
                // isLoading={assistant.isLoading}
                // isFetchingMore={assistant.isFetchingMore}
                // recentChats={assistant.recentChats}
                // olderChats={assistant.olderChats}
                // selectedChat={assistant.selectedChat}
                // setSelectedChat={assistant.setSelectedChat}
                // onSend={assistant.sendMessage}
                // onRefresh={assistant.refreshChats}
                // loadMoreChats={assistant.loadMoreChats}
                // hasMore={assistant.hasMore}
                />
            </React.Suspense>
        </Page>
    );
}

// Closed Deal Details Drawer Component (from EditJourney.js)
function ClosedDealDetailsDrawer({ closedDealDetails, onClose, onSave }) {
    const [formData, setFormData] = useState({
        tech_team_count: '',
        engagement_period: '',
        total_cash_investment: '',
        equity_conversion_investment: '',
        valuation_at_investing: '',
        equity_percentage: '',
        risk_level: 'B',
        deal_date: '',
        exit_strategy: '',
        lead_gen_eng: '',
        marketing_eng: '',
        governanace_eng: ''
    });
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (closedDealDetails) {
            setFormData({
                tech_team_count: closedDealDetails.tech_team_count || '',
                engagement_period: closedDealDetails.engagement_period || '',
                total_cash_investment: closedDealDetails.total_cash_investment || '',
                equity_conversion_investment: closedDealDetails.equity_conversion_investment || '',
                valuation_at_investing: closedDealDetails.valuation_at_investing || '',
                equity_percentage: closedDealDetails.equity_percentage || '',
                risk_level: closedDealDetails.risk_level || 'B',
                deal_date: closedDealDetails.deal_date || '',
                exit_strategy: closedDealDetails.exit_strategy || '',
                lead_gen_eng: closedDealDetails.lead_gen_eng || '',
                marketing_eng: closedDealDetails.marketing_eng || '',
                governanace_eng: closedDealDetails.governanace_eng || ''
            });
        }
    }, [closedDealDetails]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.total_cash_investment?.trim()) {
            newErrors.total_cash_investment = 'Cash Investment is required';
        }
        if (!formData.equity_percentage?.trim()) {
            newErrors.equity_percentage = 'Equity Percentage is required';
        }
        if (!formData.valuation_at_investing?.trim()) {
            newErrors.valuation_at_investing = 'Valuation is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            showErrorToast('Please fill in all required fields (Cash Investment, Equity %, Valuation)');
            return;
        }

        setIsSaving(true);
        try {
            // Prepare payload - convert empty strings to null for date fields
            const payload = {
                tech_team_count: formData.tech_team_count || null,
                engagement_period: formData.engagement_period || null,
                total_cash_investment: formData.total_cash_investment || null,
                equity_conversion_investment: formData.equity_conversion_investment || null,
                valuation_at_investing: formData.valuation_at_investing || null,
                equity_percentage: formData.equity_percentage || null,
                risk_level: formData.risk_level,
                deal_date: formData.deal_date || null, // Convert empty string to null
                exit_strategy: formData.exit_strategy || null,
                lead_gen_eng: formData.lead_gen_eng || '',
                marketing_eng: formData.marketing_eng || '',
                governanace_eng: formData.governanace_eng || ''
            };


            await onSave(payload);
            // Don't close here - let the parent component handle closing
        } catch (error) {
            console.error('Error in handleSave:', error);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{ width: 380, p: 2.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6">Complete Deal Details</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Step 1 of 2 • Next: Team Assignment
                    </Typography>
                </Box>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </Box>

            <Box sx={{ mb: 2.5, p: 2, backgroundColor: '#fff3e0', borderRadius: 1, borderLeft: '4px solid #ff9800' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100', mb: 0.5 }}>
                    ⚠️ Mandatory to Move to 100%
                </Typography>
                <Typography variant="caption" sx={{ color: '#5d4037' }}>
                    These fields are required to close the deal. Please complete Cash Investment, Equity Percentage, and Valuation before moving to 100% stage.
                </Typography>
            </Box>

            <Typography variant="h6" sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
                Team & Engagement
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <TextField
                    label="No. of Tech Team"
                    type="number"
                    value={formData.tech_team_count}
                    onChange={(e) => setFormData(prev => ({ ...prev, tech_team_count: e.target.value }))}
                    size="small"
                    sx={{ flex: 1 }}
                />
                <TextField
                    label="Engagement Period"
                    value={formData.engagement_period}
                    onChange={(e) => setFormData(prev => ({ ...prev, engagement_period: e.target.value }))}
                    placeholder="e.g., 12 months"
                    size="small"
                    sx={{ flex: 1 }}
                />
            </Box>

            <Typography variant="h6" sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
                Investment Details *
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                <TextField
                    label="Cash Investment *"
                    value={formData.total_cash_investment}
                    onChange={(e) => setFormData(prev => ({ ...prev, total_cash_investment: e.target.value }))}
                    size="small"
                    required
                    error={!!errors.total_cash_investment}
                    helperText={errors.total_cash_investment}
                    placeholder="e.g., $100k, 500K, 1M"
                    sx={{ flex: 1 }}
                />
                <TextField
                    label="Equity Conversion"
                    value={formData.equity_conversion_investment}
                    onChange={(e) => setFormData(prev => ({ ...prev, equity_conversion_investment: e.target.value }))}
                    size="small"
                    placeholder="e.g., $50k, 250K"
                    sx={{ flex: 1 }}
                />
            </Box>

            <TextField
                fullWidth
                multiline
                rows={2}
                label="Lead Gen Engineering"
                value={formData.lead_gen_eng}
                onChange={(e) => setFormData(prev => ({ ...prev, lead_gen_eng: e.target.value }))}
                placeholder="Size of engineering"
                size="small"
                sx={{ mb: 2 }}
            />

            <TextField
                fullWidth
                multiline
                rows={2}
                label="Marketing Engineering"
                value={formData.marketing_eng}
                onChange={(e) => setFormData(prev => ({ ...prev, marketing_eng: e.target.value }))}
                placeholder="Size of engineering"
                size="small"
                sx={{ mb: 2 }}
            />

            <TextField
                fullWidth
                multiline
                rows={2}
                label="Governance Engineering"
                value={formData.governanace_eng}
                onChange={(e) => setFormData(prev => ({ ...prev, governanace_eng: e.target.value }))}
                placeholder="Size of engineering"
                size="small"
                sx={{ mb: 3 }}
            />

            <Typography variant="h6" sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 600, color: 'text.secondary' }}>
                Equity & Valuation *
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <TextField
                    label="Valuation at Investing *"
                    value={formData.valuation_at_investing}
                    onChange={(e) => setFormData(prev => ({ ...prev, valuation_at_investing: e.target.value }))}
                    size="small"
                    required
                    error={!!errors.valuation_at_investing}
                    helperText={errors.valuation_at_investing}
                    placeholder="e.g., $5M, 10M"
                    sx={{ flex: 1 }}
                />
                <TextField
                    label="% Equity We Take *"
                    value={formData.equity_percentage}
                    onChange={(e) => setFormData(prev => ({ ...prev, equity_percentage: e.target.value }))}
                    size="small"
                    required
                    error={!!errors.equity_percentage}
                    helperText={errors.equity_percentage}
                    placeholder="e.g., 10%, 15"
                    sx={{ flex: 1 }}
                />
            </Box>

            <TextField
                fullWidth
                label="Deal Date"
                type="date"
                value={formData.deal_date}
                onChange={(e) => setFormData(prev => ({ ...prev, deal_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                sx={{ mb: 1.5 }}
            />

            {/* <TextField
                fullWidth
                label="Exit Strategy"
                value={formData.exit_strategy}
                onChange={(e) => setFormData(prev => ({ ...prev, exit_strategy: e.target.value }))}
                placeholder="e.g., IPO/Acquisition"
                size="small"
                sx={{ mb: 1.5 }}
            /> */}

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Risk Level</InputLabel>
                <Select
                    value={formData.risk_level}
                    onChange={(e) => setFormData(prev => ({ ...prev, risk_level: e.target.value }))}
                    label="Risk Level"
                >
                    <MenuItem value="A">Level A - Safe investment</MenuItem>
                    <MenuItem value="B">Level B - Moderate risk</MenuItem>
                    <MenuItem value="C">Level C - High risk</MenuItem>
                </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                * Required fields. A = Safe investment, B = Moderate risk, C = High risk
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, mt: 2 }}>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    fullWidth
                    disabled={isSaving}
                    sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' }, '&:disabled': { backgroundColor: '#cccccc' }, height: '40px' }}
                    onClick={handleSave}
                >
                    {isSaving ? 'Saving...' : 'Save to Closed'}
                </Button>
            </Box>
        </Box>
    );
}

// Task Assignment Drawer Content Component (Full Implementation from EditJourney.js)
function TaskAssignmentDrawerContent({ stage, companyName, companyId, companyData, teamMembers, onClose, onComplete }) {
    const [taskTitle, setTaskTitle] = useState('Assign BM and arrange introductory call with company');
    const [dueDate, setDueDate] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [existingMappings, setExistingMappings] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [bmUserId, setBmUserId] = useState('');
    const [bmPreexisting, setBmPreexisting] = useState(false);
    const [assigneeId, setAssigneeId] = useState('');

    // Fetch role types and existing company-user mappings; pre-fill BM if present
    useEffect(() => {
        const fetchMappingsAndRoles = async () => {
            if (!companyId) return;

            try {
                setLoading(true);

                const authConfig = {
                    headers: {
                        "Authorization": `Token ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                };

                // Fetch role types and company-user-mapping together
                const [rolesResponse, mappingsResponse] = await Promise.all([
                    apiInstance.get('user-role-type/', authConfig),
                    apiInstance.get(`company-user-mapping/?company=${companyId}`, authConfig)
                ]);

                const roles = rolesResponse.data?.data || [];
                setUserRoles(roles);

                const mappings = mappingsResponse.data?.results?.data || [];
                setExistingMappings(mappings);

                // Resolve SAM (BM) role id by name, never by label (Global Constraint 1)
                const samRoleId = roles.find(r => r.name === 'SAM')?.id;

                // Find an active BM (SAM) mapping and pre-fill it as the default assignee
                const bm = mappings.find(m =>
                    m.is_active && (m.role === samRoleId || m.role?.id === samRoleId)
                );

                if (bm) {
                    setBmUserId(String(bm.user));
                    setBmPreexisting(true);
                    setAssigneeId(String(bm.user));
                }
            } catch (error) {
                console.error('Error fetching mappings and roles:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMappingsAndRoles();
    }, [companyId]);

    // Resolve the display name for the (possibly newly-selected) BM
    const bmName = useMemo(() => {
        if (!bmUserId) return '';
        const mapping = existingMappings.find(m => String(m.user) === String(bmUserId));
        if (mapping?.user_name) return mapping.user_name;
        const member = teamMembers.find(m => String(m.pk) === String(bmUserId));
        return member ? `${member.first_name} ${member.last_name}` : String(bmUserId);
    }, [bmUserId, existingMappings, teamMembers]);

    // Build assignee options from the company's active assigned members (BA / BM / Team Lead / Intro Call Taken By)
    const assigneeOptions = useMemo(() => {
        const seen = new Set();
        const options = [];

        existingMappings.forEach(m => {
            if (!m.is_active) return;
            const id = String(m.user);
            if (seen.has(id)) return;
            seen.add(id);
            options.push({ id, name: m.user_name || id });
        });

        // Include a newly-selected BM that has not been persisted as a mapping yet
        if (bmUserId && !seen.has(String(bmUserId))) {
            options.push({ id: String(bmUserId), name: bmName });
        }

        return options;
    }, [existingMappings, bmUserId, bmName]);

    const handleAssignTask = async () => {
        // Validation
        if (!taskTitle.trim()) {
            showToast('Please enter a task title', 'error');
            return;
        }

        if (!dueDate) {
            showToast('Please select a due date', 'error');
            return;
        }

        if (!bmUserId) {
            showToast('Please select a BM before moving into Qualified Meetings', 'error');
            return;
        }

        try {
            setSubmitting(true);

            const samRoleId = userRoles.find(r => r.name === 'SAM')?.id;

            // Persist a newly-selected BM as an active SAM mapping
            if (!bmPreexisting) {
                await apiInstance.post('company-user-mapping/', {
                    company: companyId,
                    user: bmUserId,
                    role: samRoleId,
                    is_active: true,
                    is_multiple: false
                }, {
                    headers: {
                        "Authorization": `Token ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                });
            }

            // Create the intro-call task assigned to the chosen assignee (defaults to the BM)
            const taskData = {
                title: taskTitle,
                due_date: new Date(dueDate + 'T23:59:59').toISOString(),
                priority: 'high',
                status: 'To Do',
                company: companyId,
                stage: stage?.id,
                assigned_to: assigneeId
            };

            await tasksApi.createTask(taskData);

            showToast('Task assigned successfully!', 'success');

            // Trigger completion callback
            if (onComplete) {
                await onComplete();
            }
        } catch (error) {
            console.error('Error creating task:', error);
            showToast('Error creating task: ' + (error.response?.data?.message || error.message), 'error');
        } finally {
            setSubmitting(false);
        }
    };

    const createdByName = companyData?.created_by_name ||
        (companyData?.created_by
            ? `${companyData.created_by.first_name} ${companyData.created_by.last_name}`
            : 'Unknown');

    return (
        <Box sx={{ width: 500, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Assign Task for {stage?.name || stage?.label}
                </Typography>
                <IconButton onClick={onClose} size="small">
                    <Close />
                </IconButton>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* Information Section */}
                    <Box sx={{
                        bgcolor: '#f5f5f5',
                        p: 2,
                        borderRadius: 2,
                        mb: 3
                    }}>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Task Assignment Information
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                                    Company Created By:
                                </Typography>
                                <Chip
                                    label={createdByName}
                                    size="small"
                                    sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}
                                />
                            </Box>

                            {/* BM (Business Manager) — required before moving into Qualified Meetings */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                                    BM:
                                </Typography>
                                {bmPreexisting ? (
                                    <Chip
                                        label={`${bmName} (BM)`}
                                        size="small"
                                        sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                                    />
                                ) : (
                                    <Autocomplete
                                        size="small"
                                        sx={{ flex: 1 }}
                                        value={teamMembers.find(m => m && m.pk && String(m.pk) === String(bmUserId)) || null}
                                        onChange={(event, newValue) => {
                                            const newId = newValue?.pk ? String(newValue.pk) : '';
                                            setBmUserId(newId);
                                            // Default the assignee to the just-chosen BM when none picked yet (FR-007)
                                            setAssigneeId(prev => (prev === '' ? newId : prev));
                                        }}
                                        options={teamMembers.filter(m => m && m.pk)}
                                        getOptionLabel={(member) => `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || 'Unknown'}
                                        isOptionEqualToValue={(option, value) => option.pk === value.pk}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                required
                                                placeholder="Select BM"
                                            />
                                        )}
                                    />
                                )}
                            </Box>

                            {/* Assignee — who the intro-call task goes to (defaults to the BM, changeable) */}
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                                    Assigning Task To:
                                </Typography>
                                <Autocomplete
                                    size="small"
                                    sx={{ flex: 1 }}
                                    value={assigneeOptions.find(o => o.id === String(assigneeId)) || null}
                                    onChange={(event, newValue) => setAssigneeId(newValue?.id || '')}
                                    options={assigneeOptions}
                                    getOptionLabel={(option) => option.name || ''}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select assignee"
                                        />
                                    )}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                                    Stage:
                                </Typography>
                                <Chip
                                    label={stage?.name || stage?.label}
                                    size="small"
                                    sx={{ bgcolor: '#fff3e0', color: '#e65100' }}
                                />
                            </Box>
                        </Box>
                    </Box>

                    {/* Task Details Section */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                            Task Details
                        </Typography>

                        <TextField
                            fullWidth
                            label="Task Title"
                            value={taskTitle}
                            onChange={(e) => setTaskTitle(e.target.value)}
                            required
                            multiline
                            rows={2}
                            sx={{ mb: 2 }}
                            helperText="Describe what needs to be done"
                        />

                        <TextField
                            fullWidth
                            label="Due Date"
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            required
                            InputLabelProps={{
                                shrink: true,
                            }}
                            inputProps={{
                                min: new Date().toISOString().split('T')[0]
                            }}
                        />
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleAssignTask}
                            disabled={submitting || !bmUserId}
                            color="primary"
                            startIcon={submitting ? <CircularProgress size={20} /> : null}
                        >
                            {submitting ? 'Assigning...' : 'Assign Task'}
                        </Button>
                    </Box>

                    {!bmUserId && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                            Please select a BM before moving into Qualified Meetings.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
}

// Team Assignment Drawer Content Component (Exact copy from EditJourney.js)
function TeamAssignmentDrawerContent({ stage, companyName, companyId, onClose, onComplete, teamMembers, userRoles, companyData, getFilteredTeamMembersForRole }) {
    const [assignments, setAssignments] = useState({});
    const [initialAssignments, setInitialAssignments] = useState({});
    const [loading, setLoading] = useState(true);
    const [roleErrors, setRoleErrors] = useState({});
    const [existingMappings, setExistingMappings] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [filteredMembersByRole, setFilteredMembersByRole] = useState({});

    const stageNameLower = stage?.name?.toLowerCase() || '';

    // Clear errors when drawer opens
    useEffect(() => {
        setRoleErrors({});
    }, [stage?.id]);

    // Fetch filtered team members for each role when drawer opens or stage changes
    useEffect(() => {
        const fetchFilteredMembers = async () => {
            if (!stage || !getFilteredTeamMembersForRole || !userRoles || userRoles.length === 0) {
                return;
            }

            const filtered = {};
            for (const role of userRoles) {
                try {
                    const filteredMembers = await getFilteredTeamMembersForRole(stage, role.label, teamMembers);
                    filtered[role.id] = filteredMembers;
                } catch (error) {
                    console.warn(`Error fetching filtered members for role ${role.label}:`, error);
                    filtered[role.id] = teamMembers; // Fallback to all members
                }
            }
            setFilteredMembersByRole(filtered);
        };

        fetchFilteredMembers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stage?.id, userRoles]);

    // Fetch existing mappings only (userRoles now come from parent)
    useEffect(() => {
        const fetchData = async () => {
            if (!companyId) return;

            try {
                setLoading(true);

                const mappingsResponse = await apiInstance.get(`company-user-mapping/?company=${companyId}&page_size=1000`, {
                    headers: {
                        "Authorization": `Token ${localStorage.getItem('authToken')}`,
                        'Content-Type': 'application/json'
                    }
                });

                const mappings = mappingsResponse.data?.results?.data || [];
                setExistingMappings(mappings);

                // Initialize assignments
                const initialAssignments = {};
                userRoles.forEach(role => {
                    const isMultiSelect = role.label === 'Technical Leader' || role.label === 'Commercial Leader' || role.label === 'Engagement Lead';
                    initialAssignments[role.id] = isMultiSelect ? [] : '';
                });

                const sortedMappings = [...mappings].sort((a, b) =>
                    new Date(b.modified) - new Date(a.modified)
                );

                const processedRoles = new Set();

                sortedMappings.forEach(mapping => {
                    if (mapping.role && mapping.user && mapping.is_active) {
                        const roleId = mapping.role.toString();
                        const userId = mapping.user.toString();

                        const role = userRoles.find(r => r.id.toString() === roleId);
                        const isMultiSelect = role && (role.label === 'Technical Leader' || role.label === 'Commercial Leader' || role.label === 'Engagement Lead');

                        if (isMultiSelect) {
                            if (!initialAssignments[roleId]) {
                                initialAssignments[roleId] = [];
                            }
                            if (!initialAssignments[roleId].includes(userId)) {
                                initialAssignments[roleId].push(userId);
                            }
                        } else {
                            if (!processedRoles.has(roleId)) {
                                initialAssignments[roleId] = userId;
                                processedRoles.add(roleId);
                            }
                        }
                    }
                });

                setAssignments(initialAssignments);
                setInitialAssignments(initialAssignments);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [companyId, userRoles]);

    const handleAssignmentChange = (roleId, userId, isMultiSelect = false) => {
        setAssignments(prev => {
            if (isMultiSelect) {
                return {
                    ...prev,
                    [roleId]: userId
                };
            }
            return {
                ...prev,
                [roleId]: userId
            };
        });

        setRoleErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[roleId];
            return newErrors;
        });
    };

    const filteredRoles = getFilteredRolesByStage(stage?.name, userRoles).sort((a, b) => {
        if (a.label === 'Engagement Lead') return 1;
        if (b.label === 'Engagement Lead') return -1;
        return 0;
    });

    // Get required roles for this stage
    let requiredRoleNames = [];
    for (const [key, config] of Object.entries(STAGE_ROLES_CONFIG)) {
        if (stageNameLower.includes(key)) {
            requiredRoleNames = config.required;
            break;
        }
    }

    const handleComplete = async () => {
        // Check required roles
        const requiredRoles = userRoles.filter(role =>
            requiredRoleNames.some(reqName =>
                role.label.toLowerCase().includes(reqName.toLowerCase()) ||
                reqName.toLowerCase().includes(role.label.toLowerCase())
            )
        );

        const missingRequired = requiredRoles.filter(role => {
            const value = assignments[role.id];
            if (Array.isArray(value)) {
                return value.length === 0;
            }
            return !value || value === '';
        });

        if (missingRequired.length > 0) {
            const newErrors = {};
            missingRequired.forEach(role => {
                newErrors[role.id] = `This is required at stage ${stage?.name || ''}`;
            });
            setRoleErrors(newErrors);
            return;
        }

        try {
            setIsSaving(true);

            // Save changes
            for (const role of userRoles) {
                const roleId = role.id;
                const currentValue = assignments[roleId];
                const initialValue = initialAssignments[roleId];
                const isMultiSelect = role.label === 'Technical Leader' || role.label === 'Commercial Leader' || role.label === 'Engagement Lead';

                if (isMultiSelect) {
                    const currentArray = Array.isArray(currentValue) ? currentValue : [];
                    const initialArray = Array.isArray(initialValue) ? initialValue : [];

                    const usersToAdd = currentArray.filter(uid => !initialArray.includes(uid));
                    const usersToRemove = initialArray.filter(uid => !currentArray.includes(uid));

                    for (const userId of usersToRemove) {
                        const existingMapping = existingMappings.find(m =>
                            (m.role === parseInt(roleId) || m.role?.id === parseInt(roleId)) &&
                            m.user.toString() === userId &&
                            m.is_active
                        );

                        if (existingMapping) {
                            await apiInstance.put(
                                `company-user-mapping/${existingMapping.id}/`,
                                {
                                    company: companyId,
                                    user: userId,
                                    role: roleId,
                                    is_active: false,
                                    is_multiple: true
                                },
                                {
                                    headers: {
                                        "Authorization": `Token ${localStorage.getItem('authToken')}`,
                                        'Content-Type': 'application/json'
                                    }
                                }
                            );
                        }
                    }

                    for (const userId of usersToAdd) {
                        await apiInstance.post(
                            'company-user-mapping/',
                            {
                                company: companyId,
                                user: userId,
                                role: roleId,
                                is_active: true,
                                is_multiple: true
                            },
                            {
                                headers: {
                                    "Authorization": `Token ${localStorage.getItem('authToken')}`,
                                    'Content-Type': 'application/json'
                                }
                            }
                        );
                    }
                } else {
                    const currentUserId = currentValue && currentValue.toString().trim() !== '' ? currentValue : null;
                    const initialUserId = initialValue && initialValue.toString().trim() !== '' ? initialValue : null;

                    if (currentUserId !== initialUserId) {
                        const existingMapping = existingMappings.find(mapping =>
                            (mapping.role === parseInt(roleId) || mapping.role?.id === parseInt(roleId)) && mapping.is_active
                        );

                        if (currentUserId) {
                            const payload = {
                                company: companyId,
                                user: currentUserId,
                                role: roleId,
                                is_active: true,
                                is_multiple: false
                            };

                            if (existingMapping) {
                                await apiInstance.put(
                                    `company-user-mapping/${existingMapping.id}/`,
                                    payload,
                                    {
                                        headers: {
                                            "Authorization": `Token ${localStorage.getItem('authToken')}`,
                                            'Content-Type': 'application/json'
                                        }
                                    }
                                );
                            } else {
                                await apiInstance.post(
                                    'company-user-mapping/',
                                    payload,
                                    {
                                        headers: {
                                            "Authorization": `Token ${localStorage.getItem('authToken')}`,
                                            'Content-Type': 'application/json'
                                        }
                                    }
                                );
                            }
                        } else if (existingMapping) {
                            await apiInstance.put(
                                `company-user-mapping/${existingMapping.id}/`,
                                {
                                    company: companyId,
                                    user: existingMapping.user,
                                    role: roleId,
                                    is_active: false,
                                    is_multiple: false
                                },
                                {
                                    headers: {
                                        "Authorization": `Token ${localStorage.getItem('authToken')}`,
                                        'Content-Type': 'application/json'
                                    }
                                }
                            );
                        }
                    }
                }
            }

            showToast('Team assignments saved successfully', 'success');

            // Call onComplete which will handle closing the drawer and moving the ticket
            await onComplete();
        } catch (error) {
            console.error('Error saving team assignments:', error);
            showErrorToast('Failed to save team assignments');
        } finally {
            setIsSaving(false);
        }
    };

    if (loading || !userRoles || userRoles.length === 0) {
        return (
            <Box sx={{ width: 400, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Loading team roles...
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: 400, p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h6">Assign Team for {stage?.name}</Typography>
                </Box>
                <IconButton onClick={onClose} disabled={loading}>
                    <Close />
                </IconButton>
            </Box>

            {filteredRoles.length > 0 && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        📋 <strong>Roles for this stage:</strong> {filteredRoles.map(r => r.label).join(', ')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        👤 <strong>Company:</strong> {companyName}
                    </Typography>
                </Box>
            )}

            {filteredRoles.length > 0 ? (
                filteredRoles.map((role, index) => {
                    if (!role || !role.id) return null; // Skip invalid roles

                    const isRequired = requiredRoleNames.some(reqName =>
                        role.label.toLowerCase().includes(reqName.toLowerCase()) ||
                        reqName.toLowerCase().includes(role.label.toLowerCase())
                    );

                    const isMultiSelectRole = role.label === 'Technical Leader' || role.label === 'Commercial Leader' || role.label === 'Engagement Lead';

                    return (
                        <Box key={`${role.id}-${index}`} sx={{ mb: 2 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    mb: 1,
                                    fontWeight: isRequired ? 700 : 500,
                                    color: isRequired ? '#d32f2f' : 'inherit'
                                }}
                            >
                                {role.label} {isRequired && '(Required)'} {isMultiSelectRole && '(Multi-select)'}
                            </Typography>

                            {isMultiSelectRole ? (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    value={Array.isArray(assignments[role.id]) ?
                                        (filteredMembersByRole[role.id] || teamMembers).filter(m => m && m.pk && assignments[role.id].includes(m.pk?.toString())) :
                                        []
                                    }
                                    onChange={(event, newValue) => {
                                        setRoleErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors[role.id];
                                            return newErrors;
                                        });

                                        const selectedIds = newValue.map(member => member.pk?.toString()).filter(Boolean);
                                        handleAssignmentChange(role.id, selectedIds, true);
                                    }}
                                    options={(filteredMembersByRole[role.id] || teamMembers).filter(m => m && m.pk)}
                                    getOptionLabel={(member) => `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || 'Unknown'}
                                    isOptionEqualToValue={(option, value) => option.pk === value.pk}
                                    disabled={loading || isSaving}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={`Select ${role.label}`}
                                            error={!!roleErrors[role.id]}
                                        />
                                    )}
                                />
                            ) : (
                                <Autocomplete
                                    size="small"
                                    value={(() => {
                                        const selectedId = assignments[role.id]?.toString();
                                        if (!selectedId) return null;
                                        const membersForRole = filteredMembersByRole[role.id] || teamMembers;
                                        return membersForRole.find(m => m && m.pk && m.pk?.toString() === selectedId) || null;
                                    })()}
                                    onChange={(event, newValue) => {
                                        setRoleErrors(prev => {
                                            const newErrors = { ...prev };
                                            delete newErrors[role.id];
                                            return newErrors;
                                        });

                                        handleAssignmentChange(role.id, newValue?.pk?.toString() || '');
                                    }}
                                    options={(filteredMembersByRole[role.id] || teamMembers).filter(m => m && m.pk)}
                                    getOptionLabel={(member) => `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email || 'Unknown'}
                                    isOptionEqualToValue={(option, value) => option.pk === value.pk}
                                    disabled={loading || isSaving}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder={isRequired ? `Select ${role.label}` : `Select ${role.label} (or leave empty)`}
                                            error={!!roleErrors[role.id]}
                                        />
                                    )}
                                />
                            )}

                            {roleErrors[role.id] && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                    {roleErrors[role.id]}
                                </Typography>
                            )}
                        </Box>
                    );
                })
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="textSecondary">
                        ✅ No specific roles required for this stage
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        You can proceed with the stage change without mandatory team assignments.
                    </Typography>
                </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                    fullWidth
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleComplete}
                    disabled={loading || isSaving}
                    color="primary"
                    fullWidth
                >
                    {isSaving ? 'Saving...' : (requiredRoleNames.length > 0 ? 'Complete & Change Stage' : 'Done')}
                </Button>
            </Box>
        </Box>
    );
}

// Stage Rules Modal Component
function StageRulesModal({ open, onClose }) {
    const stageRules = [
        {
            stage: 'Data Points',
            percentage: '0%',
            timeline: '',
            teamTitles: 'Market Intelligence Analyst',
            updateLogic: 'Companies who never responded who we should reach out in future + Co being reached out',
            purposeClarity: 'Outreach, Book Meeting, Enter Company in CRM'
        },
        {
            stage: 'Future Outreach',
            percentage: '5%',
            timeline: '',
            teamTitles: '',
            updateLogic: 'Companies we have spoken to who should be reached out in future',
            purposeClarity: 'Keep Churning the database'
        },
        {
            stage: 'Qualified Meetings',
            percentage: '',
            timeline: '',
            teamTitles: '',
            updateLogic: 'Companies with a qualified meeting scheduled/held',
            purposeClarity: 'Track qualified meetings'
        },
        {
            stage: 'NDA/warm Leads',
            percentage: '10%',
            timeline: '',
            teamTitles: 'BM (must) + Intro Specialist + BA',
            updateLogic: 'Companies with whom first Intro Call is Booked',
            purposeClarity: 'First Interaction, Qualification, Collect Co Data'
        },
        {
            stage: 'Requirement Mapping, Demo given',
            percentage: '25%',
            timeline: '2 weeks',
            teamTitles: 'BA (must) + Deal Maker',
            updateLogic: 'Companies who move to workshop Level',
            purposeClarity: 'Confirm Financial Data, leads to Deal Making'
        },
        {
            stage: 'Proposal',
            percentage: '50%',
            timeline: '2 weeks',
            teamTitles: 'Legal Strategist (need not)',
            updateLogic: 'Move from Workshop to NB LOI Level',
            purposeClarity: 'Legal draft of the Non Binding LOI MSA as per deal'
        },
        {
            stage: 'Negotiation',
            percentage: '75%',
            timeline: '',
            teamTitles: 'Technical Leader + Commercial Leader',
            updateLogic: 'DD phase',
            purposeClarity: 'This phase team lead the DD process + Legal DD'
        },
        {
            stage: 'Closed',
            percentage: '100%',
            timeline: '',
            teamTitles: 'Engagement Lead (post-signing)',
            updateLogic: 'Final Deal Update',
            purposeClarity: 'Nurturing // Delivery shifts to GIS'
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    width: '95vw',
                    maxWidth: '900px',
                    height: 'auto',
                    maxHeight: '85vh'
                }
            }}
        >
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{
                    position: 'sticky',
                    top: 0,
                    bgcolor: 'white',
                    zIndex: 10,
                    borderBottom: '1px solid #e0e0e0',
                    px: 2,
                    py: 1.5
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                color: '#1976d2',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                fontSize: '1rem'
                            }}
                        >
                            📋 Stage Rules & Guidelines
                        </Typography>
                        <IconButton
                            onClick={onClose}
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                '&:hover': { bgcolor: '#e0e0e0' }
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Rules for all stages in the dashboard
                        </Typography>
                        <Chip
                            label="⏱️ Overall Timeline: 6 weeks"
                            size="small"
                            sx={{
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                height: '20px'
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Box sx={{
                        mb: 1.5,
                        p: 1.5,
                        bgcolor: '#e3f2fd',
                        borderRadius: 1,
                        border: '1px solid #90caf9'
                    }}>
                        <Typography variant="caption" sx={{ color: '#1976d2', fontWeight: 500, fontSize: '0.7rem' }}>
                            💡 <strong>Note:</strong> These stages represent the company pipeline progression with specific team members and criteria for each stage.
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            overflow: 'hidden',
                            display: 'flex'
                        }}
                    >
                        {/* 6 Weeks Overall Timeline Column - Spans All Rows */}
                        <Box sx={{
                            width: '50px',
                            bgcolor: '#c8e6c9',
                            borderRight: '2px solid #2e7d32',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}>
                            <Box sx={{
                                writingMode: 'vertical-rl',
                                transform: 'rotate(180deg)',
                                whiteSpace: 'nowrap',
                                color: '#1b5e20',
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                letterSpacing: '1px',
                                py: 2
                            }}>
                                6 WEEKS
                            </Box>
                        </Box>

                        {/* Main Table Content */}
                        <Box sx={{ flex: 1 }}>
                            {/* Compact Table Header */}
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '50px 110px 45px 1fr',
                                    bgcolor: '#1976d2',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem'
                                }}
                            >
                                <Box sx={{
                                    p: 1,
                                    borderRight: '1px solid rgba(255,255,255,0.2)',
                                    textAlign: 'center',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Box sx={{
                                        writingMode: 'vertical-rl',
                                        transform: 'rotate(180deg)',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        Timeline
                                    </Box>
                                </Box>
                                <Box sx={{ p: 1, borderRight: '1px solid rgba(255,255,255,0.2)' }}>Stage</Box>
                                <Box sx={{ p: 1, borderRight: '1px solid rgba(255,255,255,0.2)', textAlign: 'center' }}>%</Box>
                                <Box sx={{ p: 1 }}>Details</Box>
                            </Box>

                            {/* Compact Table Body */}
                            {stageRules.map((rule, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: '50px 110px 45px 1fr',
                                        bgcolor: index % 2 === 0 ? '#fafafa' : 'white',
                                        borderTop: '1px solid #e0e0e0',
                                        fontSize: '0.75rem'
                                    }}
                                >
                                    <Box sx={{
                                        p: 1,
                                        borderRight: '1px solid #e0e0e0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: rule.timeline ? '#e8f5e9' : 'transparent',
                                        position: 'relative'
                                    }}>
                                        {rule.timeline && (
                                            <Box sx={{
                                                writingMode: 'vertical-rl',
                                                transform: 'rotate(180deg)',
                                                whiteSpace: 'nowrap',
                                                color: '#2e7d32',
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.5px'
                                            }}>
                                                {rule.timeline}
                                            </Box>
                                        )}
                                    </Box>
                                    <Box sx={{
                                        p: 1,
                                        borderRight: '1px solid #e0e0e0',
                                        fontWeight: 600,
                                        color: '#1976d2',
                                        fontSize: '0.7rem'
                                    }}>
                                        {rule.stage}
                                    </Box>
                                    <Box sx={{
                                        p: 1,
                                        borderRight: '1px solid #e0e0e0',
                                        textAlign: 'center',
                                        fontWeight: 600,
                                        fontSize: '0.7rem'
                                    }}>
                                        {rule.percentage}
                                    </Box>
                                    <Box sx={{ p: 1, color: '#555' }}>
                                        {rule.teamTitles && (
                                            <Box sx={{ mb: 0.3 }}>
                                                <strong style={{ color: '#333', fontSize: '0.7rem' }}>Team:</strong> {rule.teamTitles}
                                            </Box>
                                        )}
                                        <Box sx={{ mb: 0.3 }}>
                                            <strong style={{ color: '#333', fontSize: '0.7rem' }}>Logic:</strong> {rule.updateLogic}
                                        </Box>
                                        <Box>
                                            <strong style={{ color: '#333', fontSize: '0.7rem' }}>Purpose:</strong> {rule.purposeClarity}
                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

// 5% Stage Reminder Dialog Component
function FivePercentReminderDialog({ open, onClose, onSave, onNewsletterSave, companyName, sourceStage }) {
    const [note, setNote] = useState('');
    const [reminderDate, setReminderDate] = useState('');
    const [reminderTime, setReminderTime] = useState('10:00');
    const [isSaving, setIsSaving] = useState(false);
    const [actionType, setActionType] = useState('contact_later'); // 'contact_later' or 'newsletter_only'

    // Get minimum date (tomorrow)
    const getMinDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
    };

    // Get default date (10 days from now)
    const getDefaultDate = () => {
        const defaultDate = new Date();
        defaultDate.setDate(defaultDate.getDate() + 10);
        return defaultDate.toISOString().split('T')[0];
    };

    // Time options
    const timeOptions = useMemo(() => {
        const options = [];
        for (let hour = 9; hour <= 18; hour++) {
            options.push({ value: `${hour.toString().padStart(2, '0')}:00`, label: `${hour > 12 ? hour - 12 : hour}:00 ${hour >= 12 ? 'PM' : 'AM'}` });
            options.push({ value: `${hour.toString().padStart(2, '0')}:30`, label: `${hour > 12 ? hour - 12 : hour}:30 ${hour >= 12 ? 'PM' : 'AM'}` });
        }
        return options;
    }, []);

    // Reset form when dialog opens
    useEffect(() => {
        if (open) {
            setNote('');
            setReminderDate(getDefaultDate());
            setReminderTime('10:00');
            setActionType('contact_later');
        }
    }, [open]);

    const handleSave = async () => {
        if (!note.trim()) {
            return;
        }

        // For contact later, require reminder date
        if (actionType === 'contact_later' && !reminderDate) {
            return;
        }

        setIsSaving(true);
        try {
            if (actionType === 'newsletter_only') {
                await onNewsletterSave({
                    note: note.trim()
                });
            } else {
                const reminderDateTime = `${reminderDate}T${reminderTime}:00`;
                await onSave({
                    note: note.trim(),
                    reminderDate: reminderDateTime
                });
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 450 } }
            }}
        >
            <Box sx={{
                bgcolor: '#ff9800',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontSize: '1.2rem' }}>⚠️</Box>
                    <Typography variant="h6">Moving to 5% Stage</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <Close />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        You are moving <strong>{companyName}</strong> from <strong>{sourceStage}</strong> to <strong>5% (On Hold)</strong>.
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        What would you like to do with this company?
                    </Typography>
                    <RadioGroup
                        value={actionType}
                        onChange={(e) => setActionType(e.target.value)}
                    >
                        <FormControlLabel
                            value="contact_later"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Contact Later</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Set a reminder to follow up with this company
                                    </Typography>
                                </Box>
                            }
                            sx={{ mb: 1, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                        />
                        <FormControlLabel
                            value="newsletter_only"
                            control={<Radio />}
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>Newsletter Only</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Park this company for newsletters only - no future direct contact
                                    </Typography>
                                </Box>
                            }
                            sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
                        />
                    </RadioGroup>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        {actionType === 'newsletter_only'
                            ? 'Why is this company being parked for newsletter only?'
                            : 'Why is this company being moved to 5%?'} <span style={{ color: '#f44336' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={actionType === 'newsletter_only'
                            ? "Enter the reason for parking this company for newsletter only..."
                            : "Enter the reason for moving this company to 5% stage..."}
                        value={note}
                        onChange={(e) => setNote(e.target.value.slice(0, 2000))}
                        helperText={`${note.length}/2000 characters`}
                        error={note.length === 0}
                    />
                </Box>

                {actionType === 'contact_later' && (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                When should we remind you? <span style={{ color: '#f44336' }}>*</span>
                            </Typography>
                            <TextField
                                type="date"
                                fullWidth
                                size="small"
                                value={reminderDate}
                                onChange={(e) => setReminderDate(e.target.value)}
                                inputProps={{ min: getMinDate() }}
                            />
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                At what time?
                            </Typography>
                            <FormControl fullWidth size="small">
                                <Select
                                    value={reminderTime}
                                    onChange={(e) => setReminderTime(e.target.value)}
                                >
                                    {timeOptions.map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </>
                )}

                {actionType === 'newsletter_only' && (
                    <Box sx={{
                        p: 2,
                        bgcolor: '#fff3e0',
                        borderRadius: 1,
                        border: '1px solid #ffcc80',
                        mb: 2
                    }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Note:</strong> This company will be moved to the 5% stage and marked for newsletter only.
                            They will receive newsletters but won't be contacted directly for business.
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={isSaving || !note.trim() || (actionType === 'contact_later' && !reminderDate)}
                    sx={{
                        bgcolor: actionType === 'newsletter_only' ? '#2196f3' : '#ff9800',
                        '&:hover': { bgcolor: actionType === 'newsletter_only' ? '#1976d2' : '#f57c00' }
                    }}
                >
                    {isSaving ? <CircularProgress size={20} /> : (
                        actionType === 'newsletter_only'
                            ? 'Move to 5% & Mark Newsletter Only'
                            : 'Move to 5% & Set Reminder'
                    )}
                </Button>
            </Box>
        </Drawer>
    );
}

function ReminderNoteDialog({ open, onClose, reminder, onEdit, onDeactivate }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editNote, setEditNote] = useState('');
    const [editDate, setEditDate] = useState('');
    const [editTime, setEditTime] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (reminder && open) {
            setEditNote(reminder.note || '');
            if (reminder.reminder_date) {
                const date = new Date(reminder.reminder_date);
                setEditDate(date.toISOString().split('T')[0]);
                setEditTime(date.toTimeString().slice(0, 5));
            }
            setIsEditing(false);
        }
    }, [reminder, open]);

    const handleSave = async () => {
        if (!reminder?.id) return;
        setIsSaving(true);
        try {
            const success = await onEdit(reminder.id, {
                note: editNote,
                reminder_date: `${editDate}T${editTime}:00`
            });
            if (success) {
                setIsEditing(false);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeactivate = async () => {
        if (!reminder?.id) return;
        await onDeactivate(reminder.id, reminder.company);
        onClose();
    };

    if (!reminder) return null;

    const reminderDate = reminder.reminder_date ? new Date(reminder.reminder_date) : null;
    const isDue = reminderDate && reminderDate <= new Date();

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: { xs: '100%', sm: 450 } }
            }}
        >
            <Box sx={{
                bgcolor: isDue ? '#f44336' : '#2196f3',
                color: 'white',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontSize: '1.2rem' }}>{isDue ? '🔔' : '📝'}</Box>
                    <Typography variant="h6">{isDue ? 'Reminder Due!' : 'Reminder Details'}</Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <Close />
                </IconButton>
            </Box>

            <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
                {!isEditing ? (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Reminder Date</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600, color: isDue ? '#f44336' : 'inherit' }}>
                                {reminderDate ? reminderDate.toLocaleString() : 'Not set'}
                                {isDue && ' (OVERDUE)'}
                            </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="text.secondary">Note</Typography>
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f5f5f5', p: 2, borderRadius: 1, mt: 0.5 }}>
                                {reminder.note || 'No note provided'}
                            </Typography>
                        </Box>
                        {reminder.marked_by_name && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">Set by</Typography>
                                <Typography variant="body2">{reminder.marked_by_name}</Typography>
                            </Box>
                        )}
                    </>
                ) : (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>New Reminder Date</Typography>
                            <TextField
                                type="date"
                                fullWidth
                                size="small"
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Time</Typography>
                            <TextField
                                type="time"
                                fullWidth
                                size="small"
                                value={editTime}
                                onChange={(e) => setEditTime(e.target.value)}
                            />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Note</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={editNote}
                                onChange={(e) => setEditNote(e.target.value.slice(0, 2000))}
                                helperText={`${editNote.length}/2000 characters`}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between' }}>
                <Button
                    color="error"
                    onClick={handleDeactivate}
                    disabled={isSaving}
                >
                    Deactivate Reminder
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isEditing ? (
                        <>
                            <Button onClick={() => setIsEditing(false)} disabled={isSaving}>Cancel</Button>
                            <Button variant="contained" onClick={handleSave} disabled={isSaving}>
                                {isSaving ? <CircularProgress size={20} /> : 'Save Changes'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={onClose}>Close</Button>
                            <Button variant="outlined" onClick={() => setIsEditing(true)}>
                                Edit Reminder
                            </Button>
                        </>
                    )}
                </Box>
            </Box>
        </Drawer>
    );
}

export default function Dashboard() {
    return (
        <AssistantProvider>
            <DashboardContent />
        </AssistantProvider>
    );
}